/**
 * Static translations for all fixed-content pages.
 * Google Translate handles dynamic content (Shopify products, etc.)
 * This file handles: Contact, Auth, Profile, About, Footer labels, etc.
 */
import { getTranslateLang } from "@/lib/translateUtils";
import { useState, useEffect, useCallback } from "react";

type LangCode = "en" | "ru" | "ar";

// ─── Contact Page ─────────────────────────────────────────
const contact = {
  en: {
    heroSubtitle: "We'd love to hear from you",
    heroTitle: "Contact Us",
    sendMessage: "Send us a message",
    fullName: "Full Name *",
    email: "Email *",
    phone: "Phone",
    messagePlaceholder: "Message *",
    sending: "Sending...",
    send: "Send Message",
    privacyNote: "*Don't worry, these details are saved only for contact purposes according to our",
    privacyPolicy: "Privacy Policy",
    and: "and",
    termsOfUse: "Terms of Use",
    address: "Address",
    addressValue: "58 Herzl St., Beer Sheva",
    phoneLabel: "Phone",
    emailLabel: "Email",
    businessHours: "Business Hours",
    sunday: "Sunday",
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    fridayHolidays: "Friday & Holiday Eves",
    saturdayHolidays: "Saturdays & Holidays",
    closed: "Closed",
    mapTitle: "Alice Jewelry Location",
    nameRequired: "Name is required",
    nameMax: "Name must be up to 100 characters",
    emailRequired: "Email is required",
    emailInvalid: "Invalid email address",
    emailMax: "Email must be up to 255 characters",
    phoneMax: "Phone must be up to 20 characters",
    phoneInvalid: "Invalid phone number",
    messageRequired: "Message is required",
    messageMax: "Message must be up to 2000 characters",
    fixFields: "Please fix the highlighted fields",
    securityFailed: "Security verification failed, please refresh and try again",
    messageSent: "Your message was sent successfully!",
    sendError: "An error occurred while sending. Please try again.",
  },
  ru: {
    heroSubtitle: "Мы будем рады услышать вас",
    heroTitle: "Свяжитесь с нами",
    sendMessage: "Отправьте нам сообщение",
    fullName: "Полное имя *",
    email: "Эл. почта *",
    phone: "Телефон",
    messagePlaceholder: "Сообщение *",
    sending: "Отправка...",
    send: "Отправить",
    privacyNote: "*Не волнуйтесь, данные сохраняются только для связи в соответствии с",
    privacyPolicy: "Политика конфиденциальности",
    and: "и",
    termsOfUse: "Условия использования",
    address: "Адрес",
    addressValue: "ул. Герцля 58, Беэр-Шева",
    phoneLabel: "Телефон",
    emailLabel: "Эл. почта",
    businessHours: "Часы работы",
    sunday: "Воскресенье",
    monday: "Понедельник",
    tuesday: "Вторник",
    wednesday: "Среда",
    thursday: "Четверг",
    fridayHolidays: "Пятница и кануны праздников",
    saturdayHolidays: "Суббота и праздники",
    closed: "Закрыто",
    mapTitle: "Расположение Alice Jewelry",
    nameRequired: "Имя обязательно",
    nameMax: "Имя должно быть до 100 символов",
    emailRequired: "Эл. почта обязательна",
    emailInvalid: "Неверный адрес эл. почты",
    emailMax: "Эл. почта до 255 символов",
    phoneMax: "Телефон до 20 символов",
    phoneInvalid: "Неверный номер телефона",
    messageRequired: "Сообщение обязательно",
    messageMax: "Сообщение до 2000 символов",
    fixFields: "Исправьте выделенные поля",
    securityFailed: "Проверка безопасности не удалась, обновите страницу",
    messageSent: "Ваше сообщение отправлено!",
    sendError: "Ошибка отправки. Попробуйте снова.",
  },
  ar: {
    heroSubtitle: "يسعدنا سماعك",
    heroTitle: "اتصل بنا",
    sendMessage: "أرسل لنا رسالة",
    fullName: "الاسم الكامل *",
    email: "البريد الإلكتروني *",
    phone: "الهاتف",
    messagePlaceholder: "الرسالة *",
    sending: "جاري الإرسال...",
    send: "إرسال الرسالة",
    privacyNote: "*لا تقلق، يتم حفظ هذه التفاصيل فقط لغرض الاتصال وفقًا لـ",
    privacyPolicy: "سياسة الخصوصية",
    and: "و",
    termsOfUse: "شروط الاستخدام",
    address: "العنوان",
    addressValue: "شارع هرتسل 58، بئر السبع",
    phoneLabel: "الهاتف",
    emailLabel: "البريد الإلكتروني",
    businessHours: "ساعات العمل",
    sunday: "الأحد",
    monday: "الإثنين",
    tuesday: "الثلاثاء",
    wednesday: "الأربعاء",
    thursday: "الخميس",
    fridayHolidays: "الجمعة وعشية الأعياد",
    saturdayHolidays: "السبت والأعياد",
    closed: "مغلق",
    mapTitle: "موقع أليس للمجوهرات",
    nameRequired: "الاسم مطلوب",
    nameMax: "الاسم يجب أن يكون حتى 100 حرف",
    emailRequired: "البريد الإلكتروني مطلوب",
    emailInvalid: "عنوان بريد إلكتروني غير صالح",
    emailMax: "البريد الإلكتروني حتى 255 حرف",
    phoneMax: "الهاتف حتى 20 حرف",
    phoneInvalid: "رقم هاتف غير صالح",
    messageRequired: "الرسالة مطلوبة",
    messageMax: "الرسالة حتى 2000 حرف",
    fixFields: "يرجى تصحيح الحقول المميزة",
    securityFailed: "فشل التحقق الأمني، يرجى تحديث الصفحة",
    messageSent: "تم إرسال رسالتك بنجاح!",
    sendError: "حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى.",
  },
};

// ─── Auth Page ─────────────────────────────────────────
const auth = {
  en: {
    login: "Login", signup: "Sign Up", emailLabel: "Email", passwordLabel: "Password",
    forgotPassword: "Forgot password", loginButton: "Login", loggingIn: "Logging in...",
    or: "or", loginWithGoogle: "Sign in with Google", firstName: "First Name",
    lastName: "Last Name", phoneLabel: "Phone", signupButton: "Sign Up",
    signingUp: "Signing up...", termsOfUse: "Terms of Use", privacyPolicy: "Privacy Policy",
    newCustomerGift: "Gift for new customers!", signupCoupon: "Sign up and get a 15% discount coupon for your first purchase",
    resetPassword: "Reset Password", resetDescription: "Enter your email and we'll send you a reset link",
    sendResetLink: "Send reset link", sendingReset: "Sending...", checkEmail: "Check your inbox",
    emailSentTo: "We sent a verification email to:", clickLink: "Click the link in the email to verify your account",
    checkSpam: "Didn't receive it? Check your spam folder", backToLogin: "Back to login",
    enterNewPassword: "Enter new password", chooseNewPassword: "Choose a new password for your account",
    newPassword: "New Password", confirmPassword: "Confirm Password", updatePassword: "Update Password",
    updating: "Updating...",
    alreadyRegistered: "User already registered", signupError: "An error occurred during signup",
    wrongCredentials: "Wrong email or password", loginError: "An error occurred during login",
    googleError: "Error signing in with Google", accountDisabled: "Your account has been disabled. Please contact support.",
    loggedIn: "Logged in successfully!", resetEmailSent: "Password reset email sent! Check your inbox.",
    resetEmailError: "Error sending email", passwordsNotMatch: "Passwords don't match",
    passwordTooShort: "Password must be at least 6 characters", passwordUpdated: "Password updated successfully!",
    passwordUpdateError: "Error updating password",
  },
  ru: {
    login: "Вход", signup: "Регистрация", emailLabel: "Эл. почта", passwordLabel: "Пароль",
    forgotPassword: "Забыли пароль", loginButton: "Войти", loggingIn: "Входим...",
    or: "или", loginWithGoogle: "Войти через Google", firstName: "Имя",
    lastName: "Фамилия", phoneLabel: "Телефон", signupButton: "Зарегистрироваться",
    signingUp: "Регистрация...", termsOfUse: "Условия использования", privacyPolicy: "Политика конфиденциальности",
    newCustomerGift: "Подарок новым клиентам!", signupCoupon: "Зарегистрируйтесь и получите купон на скидку 15% на первую покупку",
    resetPassword: "Сброс пароля", resetDescription: "Введите email и мы отправим ссылку для сброса",
    sendResetLink: "Отправить ссылку", sendingReset: "Отправка...", checkEmail: "Проверьте почту",
    emailSentTo: "Мы отправили письмо для подтверждения на:", clickLink: "Нажмите на ссылку в письме для подтверждения аккаунта",
    checkSpam: "Не получили? Проверьте папку спам", backToLogin: "Вернуться к входу",
    enterNewPassword: "Введите новый пароль", chooseNewPassword: "Выберите новый пароль для вашего аккаунта",
    newPassword: "Новый пароль", confirmPassword: "Подтверждение пароля", updatePassword: "Обновить пароль",
    updating: "Обновление...",
    alreadyRegistered: "Пользователь уже зарегистрирован", signupError: "Ошибка при регистрации",
    wrongCredentials: "Неверный email или пароль", loginError: "Ошибка при входе",
    googleError: "Ошибка входа через Google", accountDisabled: "Ваш аккаунт заблокирован. Обратитесь в поддержку.",
    loggedIn: "Вход выполнен успешно!", resetEmailSent: "Письмо для сброса пароля отправлено!",
    resetEmailError: "Ошибка отправки письма", passwordsNotMatch: "Пароли не совпадают",
    passwordTooShort: "Пароль должен быть не менее 6 символов", passwordUpdated: "Пароль успешно обновлён!",
    passwordUpdateError: "Ошибка обновления пароля",
  },
  ar: {
    login: "تسجيل الدخول", signup: "إنشاء حساب", emailLabel: "البريد الإلكتروني", passwordLabel: "كلمة المرور",
    forgotPassword: "نسيت كلمة المرور", loginButton: "تسجيل الدخول", loggingIn: "جاري الدخول...",
    or: "أو", loginWithGoogle: "تسجيل الدخول بحساب Google", firstName: "الاسم الأول",
    lastName: "اسم العائلة", phoneLabel: "الهاتف", signupButton: "إنشاء حساب",
    signingUp: "جاري التسجيل...", termsOfUse: "شروط الاستخدام", privacyPolicy: "سياسة الخصوصية",
    newCustomerGift: "هدية للعملاء الجدد!", signupCoupon: "سجّل واحصل على كوبون خصم 15% لأول عملية شراء",
    resetPassword: "إعادة تعيين كلمة المرور", resetDescription: "أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة التعيين",
    sendResetLink: "إرسال رابط إعادة التعيين", sendingReset: "جاري الإرسال...", checkEmail: "تحقق من بريدك",
    emailSentTo: "أرسلنا بريد تحقق إلى:", clickLink: "اضغط على الرابط في البريد لتأكيد حسابك",
    checkSpam: "لم تتلقه؟ تحقق من مجلد البريد العشوائي", backToLogin: "العودة لتسجيل الدخول",
    enterNewPassword: "أدخل كلمة مرور جديدة", chooseNewPassword: "اختر كلمة مرور جديدة لحسابك",
    newPassword: "كلمة مرور جديدة", confirmPassword: "تأكيد كلمة المرور", updatePassword: "تحديث كلمة المرور",
    updating: "جاري التحديث...",
    alreadyRegistered: "المستخدم مسجل بالفعل", signupError: "حدث خطأ أثناء التسجيل",
    wrongCredentials: "بريد إلكتروني أو كلمة مرور خاطئة", loginError: "حدث خطأ أثناء تسجيل الدخول",
    googleError: "خطأ في تسجيل الدخول بحساب Google", accountDisabled: "تم تعطيل حسابك. يرجى الاتصال بالدعم.",
    loggedIn: "تم تسجيل الدخول بنجاح!", resetEmailSent: "تم إرسال بريد إعادة تعيين كلمة المرور!",
    resetEmailError: "خطأ في إرسال البريد", passwordsNotMatch: "كلمتا المرور غير متطابقتين",
    passwordTooShort: "كلمة المرور يجب أن تكون 6 أحرف على الأقل", passwordUpdated: "تم تحديث كلمة المرور بنجاح!",
    passwordUpdateError: "خطأ في تحديث كلمة المرور",
  },
};

// ─── Profile Page ─────────────────────────────────────────
const profile = {
  en: {
    loading: "Loading...", myAccount: "My Account", tracking: "Tracking",
    personalDetails: "Personal Details", myOrders: "My Orders", favorites: "Favorites",
    repairTracking: "Repair & Delivery Tracking", myMessages: "My Messages",
    backToSite: "Back to Site", logout: "Log Out", loggedOut: "Logged out successfully",
    communication: "Communication", contentManagement: "Content Management",
    operations: "Operations", settingsAndTools: "Settings & Tools",
    contactMessages: "Contact Messages", sendToCustomers: "Send Messages to Customers",
    smsMessages: "SMS Messages", blogManagement: "Blog Management",
    sliderManagement: "Slider Management", promoBanner: "Promo Banner",
    faqManagement: "FAQ Management", repairManagement: "Repair & Order Management",
    userManagement: "User Management", tvDisplay: "Store Display (TV)",
    menuSettings: "Menu Settings", searchSettings: "Search Settings",
    botSettings: "Jewelry Advisor Settings", colorHelper: "Variant Helper",
    firstNameLabel: "First Name", lastNameLabel: "Last Name",
    emailLabel: "Email", phoneLabel: "Phone", birthDate: "Date of Birth",
    save: "Save Changes", saving: "Saving...", saved: "Changes saved successfully",
    changePassword: "Change Password", currentPassword: "Current Password",
    newPassword: "New Password", confirmPassword: "Confirm Password",
    updatePassword: "Update Password", changingPassword: "Changing password...",
    accountDetails: "Account Details", shippingAddress: "Shipping Address",
    streetAddress: "Street & House Number", city: "City", zipCode: "Zip Code",
    profileUpdated: "Profile updated successfully!", profileError: "Error updating profile",
    passwordsNotMatch: "Passwords don't match", passwordTooShort: "Password must be at least 6 characters",
    passwordChanged: "Password changed successfully!", passwordError: "Error changing password",
    moreOptions: "More Options", more: "More",
    // Mobile nav
    main: "Main", orders: "Orders", track: "Tracking",
    accessibilityReport: "Accessibility Report",
  },
  ru: {
    loading: "Загрузка...", myAccount: "Мой аккаунт", tracking: "Отслеживание",
    personalDetails: "Личные данные", myOrders: "Мои заказы", favorites: "Избранное",
    repairTracking: "Отслеживание ремонта и доставки", myMessages: "Мои сообщения",
    backToSite: "Вернуться на сайт", logout: "Выход", loggedOut: "Вы вышли из системы",
    communication: "Коммуникация", contentManagement: "Управление контентом",
    operations: "Операции", settingsAndTools: "Настройки и инструменты",
    contactMessages: "Сообщения контактов", sendToCustomers: "Отправить сообщения клиентам",
    smsMessages: "SMS сообщения", blogManagement: "Управление блогом",
    sliderManagement: "Управление слайдером", promoBanner: "Промо-баннер",
    faqManagement: "Управление FAQ", repairManagement: "Управление ремонтами и заказами",
    userManagement: "Управление пользователями", tvDisplay: "Дисплей магазина (TV)",
    menuSettings: "Настройки меню", searchSettings: "Настройки поиска",
    botSettings: "Настройки советника", colorHelper: "Помощник вариантов",
    firstNameLabel: "Имя", lastNameLabel: "Фамилия",
    emailLabel: "Эл. почта", phoneLabel: "Телефон", birthDate: "Дата рождения",
    save: "Сохранить", saving: "Сохранение...", saved: "Изменения сохранены",
    changePassword: "Изменить пароль", currentPassword: "Текущий пароль",
    newPassword: "Новый пароль", confirmPassword: "Подтверждение пароля",
    updatePassword: "Обновить пароль", changingPassword: "Изменяем пароль...",
    accountDetails: "Данные аккаунта", shippingAddress: "Адрес доставки",
    streetAddress: "Улица и номер дома", city: "Город", zipCode: "Индекс",
    profileUpdated: "Профиль обновлён!", profileError: "Ошибка обновления профиля",
    passwordsNotMatch: "Пароли не совпадают", passwordTooShort: "Пароль минимум 6 символов",
    passwordChanged: "Пароль изменён!", passwordError: "Ошибка изменения пароля",
    moreOptions: "Дополнительные опции", more: "Ещё",
    main: "Главная", orders: "Заказы", track: "Отслеживание",
    accessibilityReport: "Отчёт о доступности",
  },
  ar: {
    loading: "جاري التحميل...", myAccount: "حسابي", tracking: "التتبع",
    personalDetails: "البيانات الشخصية", myOrders: "طلباتي", favorites: "المفضلة",
    repairTracking: "تتبع الإصلاح والتوصيل", myMessages: "رسائلي",
    backToSite: "العودة للموقع", logout: "تسجيل الخروج", loggedOut: "تم تسجيل الخروج بنجاح",
    communication: "التواصل", contentManagement: "إدارة المحتوى",
    operations: "العمليات", settingsAndTools: "الإعدادات والأدوات",
    contactMessages: "رسائل الاتصال", sendToCustomers: "إرسال رسائل للعملاء",
    smsMessages: "رسائل SMS", blogManagement: "إدارة المدونة",
    sliderManagement: "إدارة الشريط", promoBanner: "شريط العروض",
    faqManagement: "إدارة الأسئلة الشائعة", repairManagement: "إدارة الإصلاحات والطلبات",
    userManagement: "إدارة المستخدمين", tvDisplay: "شاشة المتجر (TV)",
    menuSettings: "إعدادات القائمة", searchSettings: "إعدادات البحث",
    botSettings: "إعدادات مستشار المجوهرات", colorHelper: "مساعد المتغيرات",
    firstNameLabel: "الاسم الأول", lastNameLabel: "اسم العائلة",
    emailLabel: "البريد الإلكتروني", phoneLabel: "الهاتف", birthDate: "تاريخ الميلاد",
    save: "حفظ التغييرات", saving: "جاري الحفظ...", saved: "تم حفظ التغييرات بنجاح",
    changePassword: "تغيير كلمة المرور", currentPassword: "كلمة المرور الحالية",
    newPassword: "كلمة مرور جديدة", confirmPassword: "تأكيد كلمة المرور",
    updatePassword: "تحديث كلمة المرور", changingPassword: "جاري تغيير كلمة المرور...",
    accountDetails: "بيانات الحساب", shippingAddress: "عنوان الشحن",
    streetAddress: "الشارع ورقم المنزل", city: "المدينة", zipCode: "الرمز البريدي",
    profileUpdated: "تم تحديث الملف الشخصي!", profileError: "خطأ في تحديث الملف الشخصي",
    passwordsNotMatch: "كلمتا المرور غير متطابقتين", passwordTooShort: "كلمة المرور 6 أحرف على الأقل",
    passwordChanged: "تم تغيير كلمة المرور!", passwordError: "خطأ في تغيير كلمة المرور",
    moreOptions: "خيارات إضافية", more: "المزيد",
    main: "الرئيسية", orders: "الطلبات", track: "التتبع",
    accessibilityReport: "تقرير إمكانية الوصول",
  },
};

// ─── About Page ─────────────────────────────────────────
const about = {
  en: {
    since: "Since 1984", ourStory: "Our Story", tradition: "A Tradition of Excellence",
    storyP1: "In 1984, David and Albert Burmad opened the doors of Alice Jewelry in Beer Sheva, with a simple yet ambitious vision - to bring quality jewelry and excellent service to every customer.",
    storyP2: "Since then, we have become a household name in the world of jewelry in southern Israel. Every piece of jewelry we offer is carefully selected, every customer receives personal attention, and every moment with us is an experience.",
    storyP3: "We believe that a piece of jewelry is more than an accessory - it's a story, a memory, a moment of happiness preserved forever.",
    ourValues: "Our Values", whatGuidesUs: "What Guides Us",
    valuesSubtitle: "These values are the foundation of everything we do",
    inNumbers: "In Numbers", ourSuccess: "Our Success",
    yearFounded: "Year Founded", yearsExperience: "Years of Experience",
    satisfaction: "Satisfaction", happyCustomers: "Happy Customers",
    founding: "Store Founded", foundingDesc: "David and Albert Burmad found Alice Jewelry in Beer Sheva",
    expansion: "Expanding the Range", expansionDesc: "Expanding the luxury jewelry and watch collection",
    leadingSouth: "Leading in the South", leadingSouthDesc: "Becoming the leading jewelry store in the southern region",
    internationalPartners: "International Partnerships", internationalDesc: "Starting collaborations with leading European manufacturers",
    digitalRevolution: "Digital Revolution", digitalDesc: "Launching the new website and transitioning to online sales",
    fortyYears: "40 Years of Excellence", fortyDesc: "Celebrating four decades of service and uncompromising quality",
    uncompromising: "Uncompromising Quality", uncompDesc: "Every piece of jewelry undergoes thorough inspection before reaching you",
    personalService: "Warm Personal Service", personalDesc: "Personal and professional attention to every customer, like family",
    fullWarranty: "Full Warranty", warrantyDesc: "Comprehensive warranty on every product and professional repair service",
    professionalism: "Professionalism & Reliability", profDesc: "Expert team with decades of experience in the field",
    community: "Community & Family", communityDesc: "A family business that treats every customer as a friend",
    innovation: "Innovation & Modernity", innovationDesc: "Always updated with the latest trends from around the world",
    // About homepage section
    aboutAlice: "About Alice", showMore: "Show More", showLess: "Show Less", fullAboutPage: "Full About Page",
    ethicalTitle: "Ethical and Beautiful", ethicalSubtitle: "Quality without bias",
    aboutButton: "About Alice",
    // Contact section in about page
    visitUs: "Visit Us", addressLabel: "Address", phoneLabel: "Phone", emailLabel: "Email",
    hoursLabel: "Business Hours", viewCollections: "View Collections", contactUs: "Contact Us",
    yearsOfExperience: "years of experience",
  },
  ru: {
    since: "С 1984 года", ourStory: "Наша история", tradition: "Традиция мастерства",
    storyP1: "В 1984 году Давид и Альберт Бурмад открыли двери Alice Jewelry в Беэр-Шеве с простой, но амбициозной целью — приносить качественные украшения и отличный сервис каждому клиенту.",
    storyP2: "С тех пор мы стали известным именем в мире ювелирных изделий на юге Израиля. Каждое украшение тщательно отобрано, каждый клиент получает персональное внимание.",
    storyP3: "Мы верим, что украшение — это больше, чем аксессуар. Это история, воспоминание, момент счастья, сохранённый навсегда.",
    ourValues: "Наши ценности", whatGuidesUs: "Что нас направляет",
    valuesSubtitle: "Эти ценности — основа всего, что мы делаем",
    inNumbers: "В цифрах", ourSuccess: "Наш успех",
    yearFounded: "Год основания", yearsExperience: "Лет опыта",
    satisfaction: "Удовлетворённость", happyCustomers: "Довольных клиентов",
    founding: "Основание магазина", foundingDesc: "Давид и Альберт Бурмад основали Alice Jewelry в Беэр-Шеве",
    expansion: "Расширение ассортимента", expansionDesc: "Расширение коллекции роскошных украшений и часов",
    leadingSouth: "Лидеры на юге", leadingSouthDesc: "Становление ведущим ювелирным магазином в южном регионе",
    internationalPartners: "Международные партнёрства", internationalDesc: "Начало сотрудничества с ведущими европейскими производителями",
    digitalRevolution: "Цифровая революция", digitalDesc: "Запуск нового сайта и переход к онлайн-продажам",
    fortyYears: "40 лет мастерства", fortyDesc: "Празднуем четыре десятилетия сервиса и бескомпромиссного качества",
    uncompromising: "Бескомпромиссное качество", uncompDesc: "Каждое украшение проходит тщательную проверку",
    personalService: "Тёплый персональный сервис", personalDesc: "Личное и профессиональное внимание к каждому клиенту",
    fullWarranty: "Полная гарантия", warrantyDesc: "Комплексная гарантия на каждый продукт и профессиональный ремонт",
    professionalism: "Профессионализм и надёжность", profDesc: "Команда экспертов с десятилетиями опыта",
    community: "Сообщество и семейность", communityDesc: "Семейный бизнес, где к каждому клиенту относятся как к другу",
    innovation: "Инновации и современность", innovationDesc: "Всегда в курсе последних мировых трендов",
    aboutAlice: "Об Alice", showMore: "Показать больше", showLess: "Показать меньше", fullAboutPage: "Полная страница о нас",
    ethicalTitle: "Этично и красиво", ethicalSubtitle: "Качество без предубеждений",
    aboutButton: "Об Alice",
    visitUs: "Посетите нас", addressLabel: "Адрес", phoneLabel: "Телефон", emailLabel: "Эл. почта",
    hoursLabel: "Часы работы", viewCollections: "Посмотреть коллекции", contactUs: "Свяжитесь с нами",
    yearsOfExperience: "лет опыта",
  },
  ar: {
    since: "منذ 1984", ourStory: "قصتنا", tradition: "تقليد من التميز",
    storyP1: "في عام 1984، افتتح داود وألبرت بورماد أبواب أليس للمجوهرات في بئر السبع، برؤية بسيطة وطموحة - تقديم مجوهرات عالية الجودة وخدمة ممتازة لكل عميل.",
    storyP2: "منذ ذلك الحين، أصبحنا اسمًا معروفًا في عالم المجوهرات في جنوب إسرائيل. كل قطعة مجوهرات نقدمها يتم اختيارها بعناية، وكل عميل يحصل على اهتمام شخصي.",
    storyP3: "نحن نؤمن أن قطعة المجوهرات هي أكثر من مجرد إكسسوار - إنها قصة، ذكرى، لحظة سعادة محفوظة للأبد.",
    ourValues: "قيمنا", whatGuidesUs: "ما يوجهنا",
    valuesSubtitle: "هذه القيم هي أساس كل ما نقوم به",
    inNumbers: "بالأرقام", ourSuccess: "نجاحنا",
    yearFounded: "سنة التأسيس", yearsExperience: "سنوات خبرة",
    satisfaction: "رضا", happyCustomers: "عملاء سعداء",
    founding: "تأسيس المتجر", foundingDesc: "داود وألبرت بورماد يؤسسان أليس للمجوهرات في بئر السبع",
    expansion: "توسيع المجموعة", expansionDesc: "توسيع مجموعة المجوهرات والساعات الفاخرة",
    leadingSouth: "الرائدون في الجنوب", leadingSouthDesc: "أصبحنا متجر المجوهرات الرائد في المنطقة الجنوبية",
    internationalPartners: "شراكات دولية", internationalDesc: "بدء التعاون مع مصنعين أوروبيين رائدين",
    digitalRevolution: "الثورة الرقمية", digitalDesc: "إطلاق الموقع الجديد والانتقال للمبيعات عبر الإنترنت",
    fortyYears: "40 عامًا من التميز", fortyDesc: "نحتفل بأربعة عقود من الخدمة والجودة بلا تنازلات",
    uncompromising: "جودة بلا تنازلات", uncompDesc: "كل قطعة مجوهرات تخضع لفحص دقيق قبل وصولها إليك",
    personalService: "خدمة شخصية دافئة", personalDesc: "اهتمام شخصي ومهني لكل عميل، مثل العائلة",
    fullWarranty: "ضمان كامل", warrantyDesc: "ضمان شامل على كل منتج وخدمة إصلاح احترافية",
    professionalism: "الاحترافية والموثوقية", profDesc: "فريق خبراء بعقود من الخبرة في المجال",
    community: "المجتمع والعائلة", communityDesc: "عمل عائلي يعامل كل عميل كصديق",
    innovation: "الابتكار والحداثة", innovationDesc: "دائمًا على اطلاع بأحدث الاتجاهات العالمية",
    aboutAlice: "عن أليس", showMore: "عرض المزيد", showLess: "عرض أقل", fullAboutPage: "صفحة نبذة عنا الكاملة",
    ethicalTitle: "أخلاقي وجميل", ethicalSubtitle: "جودة بلا تحيز",
    aboutButton: "عن أليس",
    visitUs: "زورونا", addressLabel: "العنوان", phoneLabel: "الهاتف", emailLabel: "البريد الإلكتروني",
    hoursLabel: "ساعات العمل", viewCollections: "عرض المجموعات", contactUs: "اتصل بنا",
    yearsOfExperience: "سنوات خبرة",
  },
};

// ─── Shipping Page ─────────────────────────────────────────
const shipping = {
  en: {
    title: "Shipping Policy",
    homeDelivery: "Home Delivery",
    homeDeliveryP1: "Deliveries to remote areas may be delayed up to 12 business days. The list of remote areas can be found on the Israel Post website.",
    homeDeliveryP2: "After the shipment is received by the courier company, they will contact you at the number you provided to schedule the preferred delivery time.",
    homeDeliveryP3: "Deliveries are made Sunday-Thursday 08:00-19:00 and Friday 08:00-12:00. The courier company will contact you to arrange the delivery time.",
    homeDeliveryP4: "For non-remote areas, estimated delivery time is usually 2-5 business days from the order date.",
    selfPickup: "Self Pickup",
    selfPickupP1: "Orders can currently be picked up only at the main branch, 58 Herzl St., Beer Sheva, during business hours.",
    selfPickupP2: "Please come to pick up the order only after receiving an email confirming that the order is ready for pickup.",
    selfPickupP3: "You can also arrange pickup with customer service at 079-5575576 or email: info@alicejewelry.net",
    additionalNotes: "Additional Notes:",
    note1: "Orders received after 3:00 PM or on Friday/holiday eve will be processed the next business day.",
    note2: "Custom-size orders, such as rings (wedding/engagement), delivery time will vary according to the manufacturer's production time. Up to 21 business days.",
    note3: "If there is a delay for any reason, we will make sure to notify you.",
  },
  ru: {
    title: "Политика доставки",
    homeDelivery: "Доставка на дом",
    homeDeliveryP1: "Доставка в отдалённые районы может задерживаться до 12 рабочих дней. Список можно найти на сайте Почты Израиля.",
    homeDeliveryP2: "После получения посылки курьерской компанией с вами свяжутся для согласования времени доставки.",
    homeDeliveryP3: "Доставка: воскресенье-четверг 08:00-19:00, пятница 08:00-12:00.",
    homeDeliveryP4: "Для обычных районов срок доставки обычно 2-5 рабочих дней.",
    selfPickup: "Самовывоз",
    selfPickupP1: "Заказы можно забрать только в главном филиале: ул. Герцля 58, Беэр-Шева, в часы работы.",
    selfPickupP2: "Приходите за заказом только после получения подтверждения по email.",
    selfPickupP3: "Также можно договориться по телефону 079-5575576 или email: info@alicejewelry.net",
    additionalNotes: "Дополнительные примечания:",
    note1: "Заказы после 15:00 или в пятницу/канун праздника обрабатываются на следующий рабочий день.",
    note2: "Заказы по индивидуальным размерам (кольца) — срок до 21 рабочего дня.",
    note3: "В случае задержки мы обязательно вас уведомим.",
  },
  ar: {
    title: "سياسة الشحن",
    homeDelivery: "التوصيل للمنزل",
    homeDeliveryP1: "قد يتأخر التوصيل للمناطق النائية حتى 12 يوم عمل. يمكن العثور على القائمة في موقع بريد إسرائيل.",
    homeDeliveryP2: "بعد استلام شركة التوصيل للشحنة، سيتواصلون معك لتحديد وقت التسليم المفضل.",
    homeDeliveryP3: "التسليم: الأحد-الخميس 08:00-19:00 والجمعة 08:00-12:00.",
    homeDeliveryP4: "للمناطق العادية، وقت التسليم المقدر 2-5 أيام عمل.",
    selfPickup: "الاستلام الذاتي",
    selfPickupP1: "يمكن استلام الطلبات حاليًا فقط من الفرع الرئيسي، شارع هرتسل 58، بئر السبع.",
    selfPickupP2: "يرجى الحضور لاستلام الطلب فقط بعد تلقي تأكيد بالبريد الإلكتروني.",
    selfPickupP3: "يمكنك أيضًا الترتيب عبر الهاتف 079-5575576 أو البريد: info@alicejewelry.net",
    additionalNotes: "ملاحظات إضافية:",
    note1: "الطلبات بعد الساعة 15:00 أو في الجمعة/عشية العيد تُعالج في يوم العمل التالي.",
    note2: "الطلبات بمقاسات مخصصة (خواتم) — حتى 21 يوم عمل.",
    note3: "في حالة التأخير سنحرص على إبلاغكم.",
  },
};

// ─── Returns Page ─────────────────────────────────────────
const returns = {
  en: {
    title: "Returns & Cancellation Policy",
    subtitle: "Cancellation in accordance with Consumer Protection Law 1981 and product return policy",
    returnTitle: "Return & Cancellation",
    returnDesc: "Return the jewelry and cancel the transaction with the invoice and warranty within two days of purchase.",
    under3000Title: "Transactions up to ₪3,000",
    under3000Desc: "A jewelry purchase transaction up to ₪3,000 can be cancelled from the date of purchase until the end of two non-rest days after.",
    over3000Title: "Transactions over ₪3,000",
    over3000Desc: "Over ₪3,000, only store credit will be given for returning the jewelry within two days of purchase.",
    deadlineTitle: "Cancellation Deadline",
    deadlineDesc: "The transaction can be cancelled from the date of purchase until the end of two non-rest days after.",
    cancellationFee: "Cancellation Fee (Regret)",
    cancellationFeeDesc: "5% or ₪100, whichever is lower.",
    exchangeTitle: "Jewelry Exchange",
    exchangeDesc: "Exchange the jewelry with invoice and warranty within 14 days of purchase (not including rest days).",
    specialConditions: "Special Conditions",
    condition1: "Cancellation does not apply to custom/special order jewelry. No cash refund will be given.",
    condition2: "Jewelry made with custom design or modifications to existing stock — no refund/cash return.",
    condition3: "Return shipping costs are borne by the customer.",
    condition4: "Jewelry comes with a tag; removing the tag cancels cash refund — only exchange credit within 14 days.",
    condition5: "The company is not responsible for loss/damage to the central diamond (insurance recommended).",
    warrantyTitle: "Warranty",
    warrantyDesc: "One year warranty from purchase date. Warranty is void if improper use is made of the jewelry.",
  },
  ru: {
    title: "Политика возвратов и отмены",
    subtitle: "Отмена сделки в соответствии с Законом о защите прав потребителей 1981 года",
    returnTitle: "Возврат и отмена",
    returnDesc: "Возврат украшения и отмена сделки с чеком и гарантией в течение двух дней после покупки.",
    under3000Title: "Сделки до ₪3,000",
    under3000Desc: "Покупку украшения до ₪3,000 можно отменить в течение двух рабочих дней.",
    over3000Title: "Сделки свыше ₪3,000",
    over3000Desc: "Свыше ₪3,000 — только кредит магазина при возврате в течение двух дней.",
    deadlineTitle: "Крайний срок отмены",
    deadlineDesc: "Сделку можно отменить в течение двух рабочих дней после покупки.",
    cancellationFee: "Плата за отмену (по желанию)",
    cancellationFeeDesc: "5% или ₪100, что меньше.",
    exchangeTitle: "Обмен украшений",
    exchangeDesc: "Обмен с чеком и гарантией в течение 14 дней (без выходных).",
    specialConditions: "Особые условия",
    condition1: "Отмена не распространяется на индивидуальные заказы.",
    condition2: "Украшения с индивидуальным дизайном — без возврата.",
    condition3: "Стоимость обратной доставки оплачивается покупателем.",
    condition4: "Снятие бирки отменяет денежный возврат — только кредит на обмен.",
    condition5: "Компания не несёт ответственности за потерю/повреждение центрального бриллианта.",
    warrantyTitle: "Гарантия",
    warrantyDesc: "Гарантия один год с даты покупки. Не действует при ненадлежащем использовании.",
  },
  ar: {
    title: "سياسة الإرجاع والإلغاء",
    subtitle: "الإلغاء وفقًا لقانون حماية المستهلك 1981 وسياسة إرجاع المنتجات",
    returnTitle: "الإرجاع والإلغاء",
    returnDesc: "إرجاع المجوهرات وإلغاء المعاملة مع الفاتورة والضمان خلال يومين من الشراء.",
    under3000Title: "معاملات حتى ₪3,000",
    under3000Desc: "يمكن إلغاء شراء مجوهرات حتى ₪3,000 خلال يومي عمل.",
    over3000Title: "معاملات فوق ₪3,000",
    over3000Desc: "فوق ₪3,000 — رصيد متجر فقط عند الإرجاع خلال يومين.",
    deadlineTitle: "الموعد النهائي للإلغاء",
    deadlineDesc: "يمكن إلغاء المعاملة خلال يومي عمل بعد الشراء.",
    cancellationFee: "رسوم الإلغاء",
    cancellationFeeDesc: "5% أو ₪100، أيهما أقل.",
    exchangeTitle: "استبدال المجوهرات",
    exchangeDesc: "الاستبدال مع الفاتورة والضمان خلال 14 يومًا (بدون أيام الراحة).",
    specialConditions: "شروط خاصة",
    condition1: "الإلغاء لا ينطبق على الطلبات المخصصة.",
    condition2: "مجوهرات بتصميم مخصص — بدون استرداد.",
    condition3: "تكاليف شحن الإرجاع يتحملها العميل.",
    condition4: "إزالة العلامة تلغي الاسترداد النقدي — رصيد استبدال فقط.",
    condition5: "الشركة غير مسؤولة عن فقدان/تلف الماسة المركزية.",
    warrantyTitle: "الضمان",
    warrantyDesc: "ضمان سنة واحدة من تاريخ الشراء. لا يسري في حالة الاستخدام غير السليم.",
  },
};

// ─── Privacy, Terms, Accessibility, TradeIn, GiftCard, TrackOrder, FAQ, RingSizeGuide, CookiePolicy ───
const pages = {
  en: {
    // Page titles
    privacyTitle: "Privacy Policy", termsTitle: "Terms of Use",
    accessibilityTitle: "Accessibility Statement", tradeInTitle: "Alice Trade-In",
    giftCardTitle: "Gift Card", trackOrderTitle: "Repair Tracking",
    faqTitle: "Frequently Asked Questions", ringSizeGuideTitle: "Ring Size Guide",
    cookiePolicyTitle: "Cookie Policy",
    // FAQ
    faqSubtitle: "Find answers to the most common questions",
    noFaqQuestions: "No FAQ questions to display at this time",
    noQuestionsInCategory: "No questions in this category",
    faqNotFound: "Didn't find an answer to your question?",
    faqContactUs: "Contact us and we'll be happy to help",
    faqContactButton: "Contact Us",
    // Gift Card
    giftCardSubtitle: "Give someone you love the option to choose the perfect jewelry piece",
    chooseAmount: "Choose an amount",
    afterPurchase: "After purchase, the gift card code will be sent to your email",
    selectedAmount: "Selected amount",
    addToCart: "Add to Cart",
    giftCardInfo1: "The gift card will be sent to your email immediately after purchase",
    giftCardInfo2: "The gift card can only be redeemed for online purchases",
    giftCardInfo3: "The gift card has no expiration date",
    giftCardUnavailable: "The gift card is currently unavailable",
    giftCardAdded: "Gift card added to cart",
    // Track Order
    enterPhone: "Enter your phone number",
    wellSendCode: "We'll send you a verification code",
    phoneNumber: "Phone number",
    sendCode: "Send me a verification code",
    sendingCode: "Sending code...",
    haveAccount: "Have an account?",
    loginHere: "Login here",
    howItWorks: "How does it work?",
    step1Title: "Enter phone number",
    step1Desc: "The phone number appears on the receipt you received",
    step2Title: "Verify identity",
    step2Desc: "Enter the verification code sent to you",
    step3Title: "View orders",
    step3Desc: "Track the status in real time",
    enterOtp: "Enter verification code",
    codeSentTo: "Verification code sent to",
    verifying: "Verifying...",
    sendNewCode: "Send new code",
    changePhone: "Change phone number",
    createAccount: "Create an account and get benefits!",
    createAccountDesc: "Join us and get updates, exclusive discounts and easier tracking",
    toPersonalArea: "To personal area",
    noRepairs: "No repairs found",
    noRepairsDesc: "No repair or delivery tracking orders were found for this phone number",
    startOver: "Start over",
    repairNumber: "Repair #",
    lastUpdate: "Last update",
    created: "Created",
    // Trade-In
    tradeInSubtitle: "Your unused gold is worth money!",
    tradeInDesc: "At Alice, we allow you to exchange your broken or unused gold jewelry for a new, shiny and modern gold piece through our Trade-In program.",
    tradeInHowTo: "So what do you do?",
    tradeInHowToDesc: "Simply come to one of our stores with the old gold, we'll weigh it, perform several value assessments and give you the option to receive store credit or cash!",
    tradeInParams: "Parameters we check in store:",
    tradeInParam1: "Current gold rate on the exchange",
    tradeInParam2: "Gold weight",
    tradeInParam3: "Carat type",
    tradeInNote: "During these checks, the sales representative may request the customer's approval to remove stones from the returned jewelry for net gold weighing.",
    tradeInCTA: "Already weighed your gold and want a value estimate?",
    tradeInContact: "Contact us or call customer service:",
    // Ring Size Guide
    ringSizeNote: "Important: Ring size varies from right hand to left hand and depends on weather and finger effort before measurement. Measure after rest in reasonable weather.",
    ringSizeTools: "Required tools:",
    ringSizeThread: "Thread",
    ringSizeRuler: "Ruler",
    ringSizeFinger: "Finger",
    haveRing: "Already have a ring?",
    haveRingDesc: "If you already have a ring and want to find its size, you can use the size finder app:",
    downloadAndroid: "Download for Android, click here!",
    downloadIphone: "Download for iPhone, click here!",
    letsStart: "OK, let's start:",
    step1Ring: "With a sewing thread, wrap around the desired finger at its thickest part.",
    step2Ring: "Mark with a pen the spot on the thread that represents the finger circumference.",
    step3Ring: "Place the thread on a ruler, check the length to the marked area and compare with the table below:",
    sizeTable: "Size Table",
    circumferenceMM: "Size by circumference in mm",
    sizeByNumber: "Size by number",
    // Cookie Policy
    cookieWhat: "What are cookies?",
    cookieWhatDesc: "Cookies are small text files stored on your device when you visit a website. They allow the site to remember your actions and preferences over time.",
    cookieTypes: "Types of cookies we use",
    cookieNecessary: "Necessary Cookies",
    cookieNecessaryDesc: "Essential for the basic functioning of the website. They enable navigation, access to secure areas, shopping cart, and login.",
    cookieAlwaysActive: "Always active • Cannot be disabled",
    cookieAnalytics: "Statistics & Analytics Cookies",
    cookieAnalyticsDesc: "Help us understand how visitors use the site by collecting anonymous statistical information.",
    cookieMarketing: "Marketing & Advertising Cookies",
    cookieMarketingDesc: "Used to display relevant ads and track campaign effectiveness.",
    cookieFunctional: "Functional Cookies",
    cookieFunctionalDesc: "Enable advanced features like language preferences, video viewing, and social media integration.",
    cookieManage: "How to manage your cookies?",
    cookieManageDesc: "You have full control over cookies stored on your device:",
    cookieManageVia: "Via our site: Click the 'Manage Preferences' button at the top of this page.",
    cookieManageBrowser: "Via your browser: Most browsers allow you to view, delete and block cookies.",
    cookieThirdParty: "Third Party Providers",
    cookieThirdPartyDesc: "We work with trusted third-party providers for analytics and marketing:",
    cookieUpdates: "Policy Updates",
    cookieUpdatesDesc: "We may update this policy from time to time. Any material changes will be published on this page.",
    cookieLastUpdate: "Last update: December 2025",
    cookieContactTitle: "Contact",
    cookieContactDesc: "If you have questions about our use of cookies, please contact us:",
    managePreferences: "Manage my cookie preferences",
    // Providers
    providers: "Providers",
  },
  ru: {
    privacyTitle: "Политика конфиденциальности", termsTitle: "Условия использования",
    accessibilityTitle: "Заявление о доступности", tradeInTitle: "Alice Trade-In",
    giftCardTitle: "Подарочная карта", trackOrderTitle: "Отслеживание ремонта",
    faqTitle: "Часто задаваемые вопросы", ringSizeGuideTitle: "Гид по размерам колец",
    cookiePolicyTitle: "Политика cookies",
    faqSubtitle: "Найдите ответы на самые частые вопросы",
    noFaqQuestions: "Нет вопросов для отображения", noQuestionsInCategory: "Нет вопросов в этой категории",
    faqNotFound: "Не нашли ответ на свой вопрос?",
    faqContactUs: "Свяжитесь с нами, и мы будем рады помочь", faqContactButton: "Связаться",
    giftCardSubtitle: "Подарите любимым возможность выбрать идеальное украшение",
    chooseAmount: "Выберите сумму", afterPurchase: "После покупки код карты будет отправлен на вашу почту",
    selectedAmount: "Выбранная сумма", addToCart: "Добавить в корзину",
    giftCardInfo1: "Карта будет отправлена на email сразу после покупки",
    giftCardInfo2: "Карту можно использовать только для покупок на сайте",
    giftCardInfo3: "Подарочная карта бессрочная", giftCardUnavailable: "Подарочная карта недоступна",
    giftCardAdded: "Подарочная карта добавлена в корзину",
    enterPhone: "Введите номер телефона", wellSendCode: "Мы отправим вам код подтверждения",
    phoneNumber: "Номер телефона", sendCode: "Отправить код", sendingCode: "Отправка...",
    haveAccount: "Есть аккаунт?", loginHere: "Войдите здесь",
    howItWorks: "Как это работает?",
    step1Title: "Введите номер телефона", step1Desc: "Номер указан в квитанции",
    step2Title: "Подтвердите личность", step2Desc: "Введите код подтверждения",
    step3Title: "Просмотрите заказы", step3Desc: "Отслеживайте статус в реальном времени",
    enterOtp: "Введите код подтверждения", codeSentTo: "Код отправлен на",
    verifying: "Проверяем...", sendNewCode: "Отправить новый код", changePhone: "Изменить номер",
    createAccount: "Создайте аккаунт и получите бонусы!",
    createAccountDesc: "Присоединяйтесь и получайте обновления и скидки",
    toPersonalArea: "В личный кабинет",
    noRepairs: "Ремонты не найдены", noRepairsDesc: "Для этого номера не найдено заказов",
    startOver: "Начать заново", repairNumber: "Ремонт #", lastUpdate: "Последнее обновление", created: "Создано",
    tradeInSubtitle: "Ваше неиспользуемое золото стоит денег!",
    tradeInDesc: "В Alice мы позволяем обменять старые или сломанные золотые украшения на новые через программу Trade-In.",
    tradeInHowTo: "Что нужно сделать?",
    tradeInHowToDesc: "Приходите в магазин со старым золотом, мы взвесим его, оценим и предложим кредит или наличные!",
    tradeInParams: "Параметры, которые мы проверяем:", tradeInParam1: "Текущий курс золота",
    tradeInParam2: "Вес золота", tradeInParam3: "Тип карата",
    tradeInNote: "При проверке может потребоваться согласие на извлечение камней для взвешивания чистого золота.",
    tradeInCTA: "Уже взвесили золото? Хотите оценку?", tradeInContact: "Свяжитесь с нами:",
    ringSizeNote: "Важно: размер варьируется между руками и зависит от погоды. Измеряйте в покое.",
    ringSizeTools: "Необходимые инструменты:", ringSizeThread: "Нитка", ringSizeRuler: "Линейка", ringSizeFinger: "Палец",
    haveRing: "Уже есть кольцо?", haveRingDesc: "Используйте приложение для определения размера:",
    downloadAndroid: "Скачать для Android", downloadIphone: "Скачать для iPhone",
    letsStart: "Начинаем:", step1Ring: "Оберните нитку вокруг пальца в самом широком месте.",
    step2Ring: "Отметьте место на нитке.", step3Ring: "Измерьте длину и сравните с таблицей:",
    sizeTable: "Таблица размеров", circumferenceMM: "Размер по окружности (мм)", sizeByNumber: "Размер по номеру",
    cookieWhat: "Что такое cookies?",
    cookieWhatDesc: "Cookies — небольшие текстовые файлы, сохраняемые на вашем устройстве при посещении сайта.",
    cookieTypes: "Типы cookies", cookieNecessary: "Необходимые cookies",
    cookieNecessaryDesc: "Необходимы для базовой работы сайта.",
    cookieAlwaysActive: "Всегда активны • Нельзя отключить",
    cookieAnalytics: "Аналитические cookies", cookieAnalyticsDesc: "Помогают понять, как посетители используют сайт.",
    cookieMarketing: "Маркетинговые cookies", cookieMarketingDesc: "Для показа релевантной рекламы.",
    cookieFunctional: "Функциональные cookies", cookieFunctionalDesc: "Для расширенных функций и настроек.",
    cookieManage: "Как управлять cookies?", cookieManageDesc: "У вас полный контроль:",
    cookieManageVia: "Через наш сайт: кнопка 'Управление настройками'.",
    cookieManageBrowser: "Через браузер: большинство позволяют управлять cookies.",
    cookieThirdParty: "Сторонние провайдеры", cookieThirdPartyDesc: "Мы работаем с надёжными партнёрами:",
    cookieUpdates: "Обновления политики", cookieUpdatesDesc: "Мы можем обновлять эту политику.",
    cookieLastUpdate: "Последнее обновление: декабрь 2025",
    cookieContactTitle: "Контакт", cookieContactDesc: "Если у вас есть вопросы о cookies:",
    managePreferences: "Управление настройками cookies", providers: "Провайдеры",
  },
  ar: {
    privacyTitle: "سياسة الخصوصية", termsTitle: "شروط الاستخدام",
    accessibilityTitle: "بيان إمكانية الوصول", tradeInTitle: "Alice Trade-In",
    giftCardTitle: "بطاقة هدايا", trackOrderTitle: "تتبع الإصلاح",
    faqTitle: "الأسئلة الشائعة", ringSizeGuideTitle: "دليل مقاسات الخواتم",
    cookiePolicyTitle: "سياسة ملفات تعريف الارتباط",
    faqSubtitle: "ابحث عن إجابات للأسئلة الأكثر شيوعًا",
    noFaqQuestions: "لا توجد أسئلة للعرض حاليًا", noQuestionsInCategory: "لا توجد أسئلة في هذه الفئة",
    faqNotFound: "لم تجد إجابة لسؤالك؟",
    faqContactUs: "تواصل معنا وسنسعد بمساعدتك", faqContactButton: "اتصل بنا",
    giftCardSubtitle: "امنح من تحب فرصة اختيار قطعة المجوهرات المثالية",
    chooseAmount: "اختر المبلغ", afterPurchase: "بعد الشراء، سيتم إرسال رمز البطاقة إلى بريدك",
    selectedAmount: "المبلغ المختار", addToCart: "أضف إلى السلة",
    giftCardInfo1: "سيتم إرسال البطاقة فورًا بعد الشراء",
    giftCardInfo2: "يمكن استخدام البطاقة للشراء عبر الإنترنت فقط",
    giftCardInfo3: "بطاقة الهدايا غير محدودة الصلاحية", giftCardUnavailable: "بطاقة الهدايا غير متوفرة حاليًا",
    giftCardAdded: "تمت إضافة بطاقة الهدايا إلى السلة",
    enterPhone: "أدخل رقم هاتفك", wellSendCode: "سنرسل لك رمز التحقق",
    phoneNumber: "رقم الهاتف", sendCode: "أرسل لي رمز التحقق", sendingCode: "جاري الإرسال...",
    haveAccount: "لديك حساب؟", loginHere: "سجّل الدخول هنا",
    howItWorks: "كيف يعمل؟",
    step1Title: "أدخل رقم الهاتف", step1Desc: "الرقم موجود في الإيصال",
    step2Title: "أكّد هويتك", step2Desc: "أدخل رمز التحقق",
    step3Title: "اعرض الطلبات", step3Desc: "تتبع الحالة في الوقت الفعلي",
    enterOtp: "أدخل رمز التحقق", codeSentTo: "تم إرسال رمز التحقق إلى",
    verifying: "جاري التحقق...", sendNewCode: "إرسال رمز جديد", changePhone: "تغيير رقم الهاتف",
    createAccount: "أنشئ حسابًا واحصل على مزايا!",
    createAccountDesc: "انضم إلينا واحصل على تحديثات وخصومات حصرية",
    toPersonalArea: "إلى المنطقة الشخصية",
    noRepairs: "لم يتم العثور على إصلاحات", noRepairsDesc: "لم يتم العثور على طلبات لهذا الرقم",
    startOver: "ابدأ من جديد", repairNumber: "إصلاح #", lastUpdate: "آخر تحديث", created: "تم الإنشاء",
    tradeInSubtitle: "ذهبك غير المستخدم يستحق المال!",
    tradeInDesc: "في أليس، نتيح لك استبدال مجوهراتك الذهبية القديمة أو المكسورة بقطعة جديدة من خلال برنامج Trade-In.",
    tradeInHowTo: "ماذا تفعل؟",
    tradeInHowToDesc: "تعال إلى المتجر مع الذهب القديم، سنزنه ونقيّمه ونعطيك خيار الحصول على رصيد أو نقد!",
    tradeInParams: "المعايير التي نفحصها:", tradeInParam1: "سعر الذهب الحالي",
    tradeInParam2: "وزن الذهب", tradeInParam3: "نوع القيراط",
    tradeInNote: "قد يُطلب موافقة لإزالة الأحجار لوزن الذهب الصافي.",
    tradeInCTA: "وزنت ذهبك وتريد تقييمًا؟", tradeInContact: "تواصل معنا:",
    ringSizeNote: "مهم: المقاس يختلف بين اليدين ويعتمد على الطقس. قس بعد الراحة.",
    ringSizeTools: "الأدوات المطلوبة:", ringSizeThread: "خيط", ringSizeRuler: "مسطرة", ringSizeFinger: "إصبع",
    haveRing: "لديك خاتم بالفعل؟", haveRingDesc: "استخدم تطبيق تحديد المقاس:",
    downloadAndroid: "تحميل لأندرويد", downloadIphone: "تحميل لآيفون",
    letsStart: "لنبدأ:", step1Ring: "لف خيطًا حول الإصبع في أعرض نقطة.",
    step2Ring: "ضع علامة على الخيط.", step3Ring: "قس الطول وقارن بالجدول:",
    sizeTable: "جدول المقاسات", circumferenceMM: "المقاس حسب المحيط (مم)", sizeByNumber: "المقاس حسب الرقم",
    cookieWhat: "ما هي ملفات تعريف الارتباط؟",
    cookieWhatDesc: "ملفات تعريف الارتباط هي ملفات نصية صغيرة تُخزن على جهازك عند زيارة موقع.",
    cookieTypes: "أنواع ملفات تعريف الارتباط", cookieNecessary: "ملفات ضرورية",
    cookieNecessaryDesc: "ضرورية لعمل الموقع الأساسي.",
    cookieAlwaysActive: "نشطة دائمًا • لا يمكن تعطيلها",
    cookieAnalytics: "ملفات إحصائية", cookieAnalyticsDesc: "تساعدنا في فهم كيفية استخدام الزوار للموقع.",
    cookieMarketing: "ملفات تسويقية", cookieMarketingDesc: "لعرض إعلانات ذات صلة.",
    cookieFunctional: "ملفات وظيفية", cookieFunctionalDesc: "لميزات متقدمة وتفضيلات.",
    cookieManage: "كيف تدير ملفات تعريف الارتباط؟", cookieManageDesc: "لديك سيطرة كاملة:",
    cookieManageVia: "عبر موقعنا: زر 'إدارة التفضيلات'.",
    cookieManageBrowser: "عبر المتصفح: معظم المتصفحات تتيح إدارتها.",
    cookieThirdParty: "مقدمو خدمات طرف ثالث", cookieThirdPartyDesc: "نعمل مع شركاء موثوقين:",
    cookieUpdates: "تحديثات السياسة", cookieUpdatesDesc: "قد نحدث هذه السياسة من وقت لآخر.",
    cookieLastUpdate: "آخر تحديث: ديسمبر 2025",
    cookieContactTitle: "اتصل بنا", cookieContactDesc: "إذا كانت لديك أسئلة حول ملفات تعريف الارتباط:",
    managePreferences: "إدارة تفضيلات ملفات تعريف الارتباط", providers: "مقدمو الخدمات",
  },
};

// ─── Common / Shared ─────────────────────────────────────────
const common = {
  en: {
    loading: "Loading...", error: "Error", success: "Success",
    save: "Save", cancel: "Cancel", close: "Close", back: "Back",
    send: "Send", search: "Search", noResults: "No results found", required: "Required field",
  },
  ru: {
    loading: "Загрузка...", error: "Ошибка", success: "Успех",
    save: "Сохранить", cancel: "Отмена", close: "Закрыть", back: "Назад",
    send: "Отправить", search: "Поиск", noResults: "Ничего не найдено", required: "Обязательное поле",
  },
  ar: {
    loading: "جاري التحميل...", error: "خطأ", success: "نجاح",
    save: "حفظ", cancel: "إلغاء", close: "إغلاق", back: "رجوع",
    send: "إرسال", search: "بحث", noResults: "لم يتم العثور على نتائج", required: "حقل مطلوب",
  },
};

// ─── Newsletter ─────────────────────────────────────────
const newsletter = {
  en: {
    subtitle: "Sign up for hot deals",
    title: "Join our Newsletter",
    emailLabel: "Email",
    sending: "Sending...",
    subscribe: "Subscribe",
    consent: "By subscribing I agree to receive emails and updates and confirm I have read the site's",
    termsOfUse: "Terms of Use",
    privacyPolicy: "Privacy Policy",
    error: "Error",
    enterEmail: "Please enter an email address",
    invalidEmail: "Please enter a valid email address",
    consentRequired: "You must accept the newsletter terms",
    success: "Subscribed successfully!",
    thankYou: "Thank you for joining our newsletter",
    subscribeError: "An error occurred, please try again later",
  },
  ru: {
    subtitle: "Подпишитесь на горячие предложения",
    title: "Присоединяйтесь к рассылке",
    emailLabel: "Эл. почта",
    sending: "Отправка...",
    subscribe: "Подписаться",
    consent: "Подписываясь, я соглашаюсь получать письма и подтверждаю, что ознакомился с",
    termsOfUse: "Условия использования",
    privacyPolicy: "Политика конфиденциальности",
    error: "Ошибка",
    enterEmail: "Введите адрес эл. почты",
    invalidEmail: "Введите корректный адрес эл. почты",
    consentRequired: "Необходимо согласиться с условиями",
    success: "Подписка оформлена!",
    thankYou: "Спасибо за подписку на нашу рассылку",
    subscribeError: "Произошла ошибка, попробуйте позже",
  },
  ar: {
    subtitle: "اشترك في العروض الساخنة",
    title: "انضم إلى نشرتنا الإخبارية",
    emailLabel: "البريد الإلكتروني",
    sending: "جاري الإرسال...",
    subscribe: "اشتراك",
    consent: "بالاشتراك أوافق على تلقي رسائل وأؤكد أنني قرأت",
    termsOfUse: "شروط الاستخدام",
    privacyPolicy: "سياسة الخصوصية",
    error: "خطأ",
    enterEmail: "يرجى إدخال عنوان البريد الإلكتروني",
    invalidEmail: "يرجى إدخال عنوان بريد إلكتروني صالح",
    consentRequired: "يجب الموافقة على شروط النشرة",
    success: "تم الاشتراك بنجاح!",
    thankYou: "شكرًا لانضمامك إلى نشرتنا",
    subscribeError: "حدث خطأ، يرجى المحاولة لاحقًا",
  },
};

// ─── Footer ─────────────────────────────────────────
const footer = {
  en: {
    tagline: "Jewelry & Watches since 1984",
    importantLinks: "Important Links",
    customerService: "Customer Service",
    contactInfo: "Contact Info",
    socialMedia: "Social Media",
    privacyPolicy: "Privacy Policy",
    termsOfUse: "Terms of Use",
    shippingPolicy: "Shipping Policy",
    returnPolicy: "Returns Policy",
    accessibility: "Accessibility Statement",
    cookiePolicy: "Cookie Policy",
    manageCookies: "Manage Cookies",
    faq: "FAQ",
    sizeGuide: "Size Guide",
    tradeIn: "Alice Trade-In",
    contactUs: "Contact Us",
    trackRepairs: "Repair Tracking",
    phone: "Phone: 079-5575576",
    whatsapp: "Whatsapp: 054-5267564",
    address: "Address: 58 Herzl St., Beer Sheva",
    facebook: "Facebook",
    instagram: "Instagram",
    tiktok: "TikTok",
    email: "Email",
    copyright: "© Alice | Jewelry & Watches 2025",
    allProducts: "View All Products",
    viewAllCategories: "View All Categories",
  },
  ru: {
    tagline: "Ювелирные изделия и часы с 1984",
    importantLinks: "Важные ссылки",
    customerService: "Обслуживание клиентов",
    contactInfo: "Контакты",
    socialMedia: "Соцсети",
    privacyPolicy: "Политика конфиденциальности",
    termsOfUse: "Условия использования",
    shippingPolicy: "Политика доставки",
    returnPolicy: "Политика возвратов",
    accessibility: "Заявление о доступности",
    cookiePolicy: "Политика cookies",
    manageCookies: "Управление cookies",
    faq: "Часто задаваемые вопросы",
    sizeGuide: "Гид по размерам",
    tradeIn: "Alice Trade-In",
    contactUs: "Связаться с нами",
    trackRepairs: "Отслеживание ремонта",
    phone: "Телефон: 079-5575576",
    whatsapp: "Whatsapp: 054-5267564",
    address: "Адрес: ул. Герцля 58, Беэр-Шева",
    facebook: "Facebook",
    instagram: "Instagram",
    tiktok: "TikTok",
    email: "Эл. почта",
    copyright: "© Alice | Ювелирные изделия и часы 2025",
    allProducts: "Все товары",
    viewAllCategories: "Все категории",
  },
  ar: {
    tagline: "مجوهرات وساعات منذ 1984",
    importantLinks: "روابط مهمة",
    customerService: "خدمة العملاء",
    contactInfo: "معلومات الاتصال",
    socialMedia: "وسائل التواصل",
    privacyPolicy: "سياسة الخصوصية",
    termsOfUse: "شروط الاستخدام",
    shippingPolicy: "سياسة الشحن",
    returnPolicy: "سياسة الإرجاع",
    accessibility: "بيان إمكانية الوصول",
    cookiePolicy: "سياسة ملفات تعريف الارتباط",
    manageCookies: "إدارة ملفات تعريف الارتباط",
    faq: "الأسئلة الشائعة",
    sizeGuide: "دليل المقاسات",
    tradeIn: "Alice Trade-In",
    contactUs: "اتصل بنا",
    trackRepairs: "تتبع الإصلاحات",
    phone: "الهاتف: 079-5575576",
    whatsapp: "واتساب: 054-5267564",
    address: "العنوان: شارع هرتسل 58، بئر السبع",
    facebook: "فيسبوك",
    instagram: "إنستغرام",
    tiktok: "تيك توك",
    email: "البريد الإلكتروني",
    copyright: "© أليس | مجوهرات وساعات 2025",
    allProducts: "عرض جميع المنتجات",
    viewAllCategories: "عرض جميع الفئات",
  },
};

// ─── Homepage sections ─────────────────────────────────────────
const homepage = {
  en: {
    featuredCategories: "Featured Categories",
    viewAllProducts: "View all products in category",
    loadingProducts: "Loading products...",
    noProducts: "No products found in this category",
    loading: "Loading...",
  },
  ru: {
    featuredCategories: "Популярные категории",
    viewAllProducts: "Все товары категории",
    loadingProducts: "Загрузка товаров...",
    noProducts: "Товары в этой категории не найдены",
    loading: "Загрузка...",
  },
  ar: {
    featuredCategories: "الفئات المميزة",
    viewAllProducts: "عرض جميع منتجات الفئة",
    loadingProducts: "جاري تحميل المنتجات...",
    noProducts: "لم يتم العثور على منتجات في هذه الفئة",
    loading: "جاري التحميل...",
  },
};

// ─── Header ─────────────────────────────────────────
const header = {
  en: {
    home: "Home", blog: "Blog", aboutUs: "About", giftCard: "GiftCard",
    customerService: "Customer Service", contactUs: "Contact Us", faq: "FAQ",
    sizeGuide: "Size Guide", tradeIn: "Alice Trade In", trackRepairs: "Repair Tracking",
    back: "Back", viewCollection: "View Collection", personalArea: "Personal Area",
    login: "Login", accessibility: "Accessibility", wishlist: "Wishlist",
    socialFollow: "Follow us and stay updated on everything new! In our store and the jewelry world:",
    socialTitle: "Social Media", orderInfo: "Orders & Customer Service",
    terms: "Terms of Use", returnsPolicy: "Returns & Cancellation Policy",
    accessibilityStatement: "Accessibility Statement", ringSizeGuide: "Size Guide",
    giftCardFooter: "Gift Card", repairTracking: "Repair Tracking",
    ourApp: "Our App", downloadHere: "Download here",
    phone: "Phone", fax: "Fax", address: "Address",
    businessHours: "Business Hours",
    sunday: "Sunday", monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday",
    thursday: "Thursday", fridayHolidays: "Friday & Holiday Eves",
    saturdayHolidays: "Saturdays & Holidays", closed: "Closed",
    copyright: "© Alice | Jewelry & Watches 2025",
    chooseLanguage: "Choose language",
    ourCollection: "Our Collection", viewAll: "View",
  },
  ru: {
    home: "Главная", blog: "Блог", aboutUs: "О нас", giftCard: "GiftCard",
    customerService: "Обслуживание", contactUs: "Связаться", faq: "FAQ",
    sizeGuide: "Гид по размерам", tradeIn: "Alice Trade In", trackRepairs: "Отслеживание ремонта",
    back: "Назад", viewCollection: "Посмотреть коллекцию", personalArea: "Личный кабинет",
    login: "Вход", accessibility: "Доступность", wishlist: "Избранное",
    socialFollow: "Следите за нами и будьте в курсе всех новинок!",
    socialTitle: "Соцсети", orderInfo: "Заказы и обслуживание",
    terms: "Условия использования", returnsPolicy: "Политика возвратов и отмены",
    accessibilityStatement: "Заявление о доступности", ringSizeGuide: "Гид по размерам",
    giftCardFooter: "Подарочная карта", repairTracking: "Отслеживание ремонта",
    ourApp: "Наше приложение", downloadHere: "Скачать здесь",
    phone: "Телефон", fax: "Факс", address: "Адрес",
    businessHours: "Часы работы",
    sunday: "Воскресенье", monday: "Понедельник", tuesday: "Вторник", wednesday: "Среда",
    thursday: "Четверг", fridayHolidays: "Пятница и кануны праздников",
    saturdayHolidays: "Суббота и праздники", closed: "Закрыто",
    copyright: "© Alice | Ювелирные изделия и часы 2025",
    chooseLanguage: "Выберите язык",
    ourCollection: "Наша коллекция", viewAll: "Посмотреть",
  },
  ar: {
    home: "الرئيسية", blog: "المدونة", aboutUs: "نبذة عنا", giftCard: "GiftCard",
    customerService: "خدمة العملاء", contactUs: "اتصل بنا", faq: "الأسئلة الشائعة",
    sizeGuide: "دليل المقاسات", tradeIn: "Alice Trade In", trackRepairs: "تتبع الإصلاحات",
    back: "رجوع", viewCollection: "عرض المجموعة", personalArea: "المنطقة الشخصية",
    login: "تسجيل الدخول", accessibility: "إمكانية الوصول", wishlist: "المفضلة",
    socialFollow: "تابعونا وابقوا على اطلاع بكل جديد!",
    socialTitle: "وسائل التواصل", orderInfo: "الطلبات وخدمة العملاء",
    terms: "شروط الاستخدام", returnsPolicy: "سياسة الإرجاع والإلغاء",
    accessibilityStatement: "بيان إمكانية الوصول", ringSizeGuide: "دليل المقاسات",
    giftCardFooter: "بطاقة هدايا", repairTracking: "تتبع الإصلاحات",
    ourApp: "تطبيقنا", downloadHere: "اضغط هنا للتحميل",
    phone: "الهاتف", fax: "الفاكس", address: "العنوان",
    businessHours: "ساعات العمل",
    sunday: "الأحد", monday: "الإثنين", tuesday: "الثلاثاء", wednesday: "الأربعاء",
    thursday: "الخميس", fridayHolidays: "الجمعة وعشية الأعياد",
    saturdayHolidays: "السبت والأعياد", closed: "مغلق",
    copyright: "© أليس | مجوهرات وساعات 2025",
    chooseLanguage: "اختر اللغة",
    ourCollection: "مجموعتنا", viewAll: "عرض",
  },
};

// ─── WhyUs Section ─────────────────────────────────────────
const whyUs = {
  en: {
    sectionLabel: "Why Us?",
    title: "What Makes Us",
    titleHighlight: "Special",
    description: "We don't just sell jewelry - we create a perfect experience from start to long after purchase. Our customers enjoy exceptional service, uncompromising quality and lasting support.",
    viewAllProducts: "View All Products",
    reason1Title: "Full Warranty",
    reason1Desc: "We provide full warranty on all our products. Every piece undergoes rigorous quality testing and comes with comprehensive manufacturer warranty.",
    reason2Title: "Personal Support",
    reason2Desc: "Our team is always available for you. We provide personal consultation, help choose the perfect jewelry and respond quickly to any question.",
    reason3Title: "Uncompromising Quality",
    reason3Desc: "We work only with top-tier raw materials and highest quality diamonds. Every piece is carefully crafted by skilled artisans meeting the strictest international standards.",
    reason4Title: "Fair Prices",
    reason4Desc: "We believe quality service and luxury products don't have to come with excessive prices. We offer fair and competitive prices without compromising quality.",
    reason5Title: "Unique Design",
    reason5Desc: "Our collections combine classic design with a modern, unique touch. We also offer custom design services so you can create jewelry that reflects your personality.",
  },
  ru: {
    sectionLabel: "Почему мы?",
    title: "Что делает нас",
    titleHighlight: "особенными",
    description: "Мы не просто продаём украшения — мы создаём идеальный опыт от начала и до долгого времени после покупки.",
    viewAllProducts: "Все товары",
    reason1Title: "Полная гарантия",
    reason1Desc: "Мы предоставляем полную гарантию на все наши товары. Каждое украшение проходит тщательную проверку качества.",
    reason2Title: "Личная поддержка",
    reason2Desc: "Наша команда всегда доступна для вас. Мы предоставляем персональные консультации и помогаем выбрать идеальное украшение.",
    reason3Title: "Бескомпромиссное качество",
    reason3Desc: "Мы работаем только с лучшими материалами и бриллиантами высочайшего качества.",
    reason4Title: "Справедливые цены",
    reason4Desc: "Мы предлагаем справедливые и конкурентные цены без ущерба качеству.",
    reason5Title: "Уникальный дизайн",
    reason5Desc: "Наши коллекции сочетают классический дизайн с современным уникальным стилем.",
  },
  ar: {
    sectionLabel: "لماذا نحن؟",
    title: "ما يجعلنا",
    titleHighlight: "مميزين",
    description: "نحن لا نبيع المجوهرات فقط - نحن نخلق تجربة مثالية من البداية وحتى ما بعد الشراء بوقت طويل.",
    viewAllProducts: "عرض جميع المنتجات",
    reason1Title: "ضمان كامل",
    reason1Desc: "نقدم ضمانًا كاملاً على جميع منتجاتنا. كل قطعة تخضع لفحص جودة دقيق.",
    reason2Title: "دعم شخصي",
    reason2Desc: "فريقنا متاح لكم دائمًا. نقدم استشارات شخصية ونساعدكم في اختيار المجوهرات المثالية.",
    reason3Title: "جودة بلا تنازلات",
    reason3Desc: "نعمل فقط مع أفضل المواد الخام والماس عالي الجودة.",
    reason4Title: "أسعار عادلة",
    reason4Desc: "نقدم أسعارًا عادلة وتنافسية دون المساس بالجودة.",
    reason5Title: "تصميم فريد",
    reason5Desc: "مجموعاتنا تجمع بين التصميم الكلاسيكي واللمسة العصرية الفريدة.",
  },
};

// ─── WhyChoose Accordion Section ─────────────────────────────
const whyChoose = {
  en: {
    sectionLabel: "Why Choose Us",
    title: "Our Advantages",
    feature1: "Highest Quality Diamonds",
    feature1Desc: "We work exclusively with the highest quality diamonds, certified by leading international laboratories. Every diamond undergoes rigorous testing and comes with a detailed quality certificate.",
    feature2: "Design & Uniqueness",
    feature2Desc: "Our collections combine classic design with a modern, personal touch. We also offer full custom design services to create unique jewelry that reflects your personality.",
    feature3: "Fair & Transparent Prices",
    feature3Desc: "We believe quality jewelry should be accessible. By working directly with leading manufacturers, we offer fair and competitive prices with complete transparency.",
    feature4: "Professional & Personal Service",
    feature4Desc: "Our experienced team provides professional and personal guidance throughout the entire process - from initial selection to after purchase. Our maintenance and adjustment services are always available.",
    feature5: "Decades of Experience",
    feature5Desc: "Since 1984, Alice Jewelry has been providing excellent service throughout the south. Our extensive experience allows us to understand your needs and offer personalized solutions.",
    feature6: "Warranty & After-Sales Service",
    feature6Desc: "We offer comprehensive warranty on all our jewelry and professional repair and maintenance service. Our team is available for any question even after purchase.",
  },
  ru: {
    sectionLabel: "Почему выбирают нас",
    title: "Наши преимущества",
    feature1: "Бриллианты высочайшего качества",
    feature1Desc: "Мы работаем исключительно с бриллиантами высочайшего качества, сертифицированными ведущими международными лабораториями.",
    feature2: "Дизайн и уникальность",
    feature2Desc: "Наши коллекции сочетают классический дизайн с современным и персональным стилем. Мы также предлагаем услуги индивидуального дизайна.",
    feature3: "Справедливые и прозрачные цены",
    feature3Desc: "Работая напрямую с производителями, мы предлагаем справедливые цены с полной прозрачностью.",
    feature4: "Профессиональный и личный сервис",
    feature4Desc: "Наша опытная команда сопровождает вас на каждом этапе — от выбора до послепродажного обслуживания.",
    feature5: "Десятилетия опыта",
    feature5Desc: "С 1984 года Alice Jewelry предоставляет отличный сервис на юге Израиля.",
    feature6: "Гарантия и послепродажное обслуживание",
    feature6Desc: "Мы предоставляем комплексную гарантию на все украшения и профессиональный сервис ремонта.",
  },
  ar: {
    sectionLabel: "لماذا تختارنا",
    title: "مزايانا",
    feature1: "ألماس بأعلى جودة",
    feature1Desc: "نعمل حصريًا مع ألماس بأعلى جودة، معتمد من مختبرات دولية رائدة.",
    feature2: "تصميم وتميز",
    feature2Desc: "مجموعاتنا تجمع بين التصميم الكلاسيكي واللمسة العصرية الشخصية. نقدم أيضًا خدمات تصميم مخصصة.",
    feature3: "أسعار عادلة وشفافة",
    feature3Desc: "بالعمل مباشرة مع المصنعين، نقدم أسعارًا عادلة مع شفافية كاملة.",
    feature4: "خدمة مهنية وشخصية",
    feature4Desc: "فريقنا المتمرس يرافقكم في كل مرحلة — من الاختيار الأولي وحتى ما بعد الشراء.",
    feature5: "عقود من الخبرة",
    feature5Desc: "منذ 1984، تقدم أليس للمجوهرات خدمة ممتازة في جنوب إسرائيل.",
    feature6: "ضمان وخدمة ما بعد البيع",
    feature6Desc: "نقدم ضمانًا شاملاً على جميع المجوهرات وخدمة إصلاح وصيانة احترافية.",
  },
};

// ─── Privacy Page ─────────────────────────────────────────
const privacy = {
  en: {
    title: "Privacy Policy", companyName: "Alice Jewelry Ltd.",
    intro: "This privacy policy (\"Privacy Policy\") is an integral part of the terms of use and should be read together with the terms of use, using the definitions appearing in the terms of use.",
    siteDescription: "The website \"www.alisjewelry.co.il\" (\"the Site\") operated and managed by Alice Jewelry Ltd., Company No. 514849876 (\"the Operator\") serves, among other things, for online commerce for the purchase of products by visitors and users of the site (\"the Service\") and for customers who register and use the Operator's services.",
    generalTitle: "General",
    generalP1: "As part of using the site's services, the user is required to provide personal details to the Operator including name, address, phone and email, ID number. These details are stored by the Operator in its customer database and are used for internal purposes and future communication with customers.",
    generalP2: "Additional details, such as credit card details, are required for billing purposes only and are not stored by the Operator.",
    databaseTitle: "Database and Its Use",
    databaseDesc: "The data collected about the user is stored in the Operator's database. The Operator undertakes to use said information in accordance with this privacy policy and/or in accordance with any applicable law.",
    cookiesTitle: "Use of Cookies",
    cookiesDesc: "The Operator uses various technologies to collect information from the user's mobile device and/or computer and about the user's activity on the site. Most browsers accept cookies automatically, but the user can usually change their browser settings.",
    securityTitle: "Information Security",
    securityDesc: "The Operator implements up-to-date information security systems and procedures on the site. These systems and procedures reduce the risks of unauthorized access, but do not provide absolute security.",
    changesTitle: "Changes to Privacy Policy",
    changesDesc: "The Operator may change the provisions of this privacy policy from time to time. If material changes are made, a notice will be published on the Operator's website.",
  },
  ru: {
    title: "Политика конфиденциальности", companyName: "Алис Ювелирные изделия ООО",
    intro: "Данная политика конфиденциальности является неотъемлемой частью условий использования и должна читаться совместно с условиями использования.",
    siteDescription: "Веб-сайт «www.alisjewelry.co.il» управляется компанией Алис Ювелирные изделия ООО и служит для онлайн-торговли и обслуживания клиентов.",
    generalTitle: "Общие положения",
    generalP1: "При использовании услуг сайта пользователь должен предоставить личные данные: имя, адрес, телефон, email и номер удостоверения личности. Эти данные хранятся в базе данных клиентов.",
    generalP2: "Дополнительные данные, такие как данные кредитной карты, требуются только для оплаты и не хранятся оператором.",
    databaseTitle: "База данных и её использование",
    databaseDesc: "Собранные данные хранятся в базе данных оператора. Оператор обязуется использовать информацию в соответствии с данной политикой и действующим законодательством.",
    cookiesTitle: "Использование файлов Cookie",
    cookiesDesc: "Оператор использует различные технологии для сбора информации с устройства пользователя. Большинство браузеров принимают cookies автоматически, но пользователь может изменить настройки.",
    securityTitle: "Безопасность информации",
    securityDesc: "Оператор применяет современные системы безопасности. Эти системы снижают риски несанкционированного доступа, но не обеспечивают абсолютную безопасность.",
    changesTitle: "Изменения в политике конфиденциальности",
    changesDesc: "Оператор может время от времени изменять положения данной политики. О существенных изменениях будет опубликовано уведомление на сайте.",
  },
  ar: {
    title: "سياسة الخصوصية", companyName: "أليس للمجوهرات م.ض",
    intro: "سياسة الخصوصية هذه جزء لا يتجزأ من شروط الاستخدام ويجب قراءتها مع شروط الاستخدام.",
    siteDescription: "موقع الإنترنت يُدار من قبل شركة أليس للمجوهرات ويُستخدم للتجارة الإلكترونية وخدمة العملاء.",
    generalTitle: "عام",
    generalP1: "يُطلب من المستخدم تقديم بيانات شخصية تشمل الاسم والعنوان والهاتف والبريد الإلكتروني ورقم الهوية. يتم حفظ هذه البيانات في قاعدة بيانات العملاء.",
    generalP2: "تفاصيل إضافية مثل بيانات بطاقة الائتمان مطلوبة فقط لأغراض الفوترة ولا يتم تخزينها.",
    databaseTitle: "قاعدة البيانات واستخدامها",
    databaseDesc: "يتم تخزين البيانات المجمعة في قاعدة بيانات المشغل. يلتزم المشغل باستخدام المعلومات وفقًا لسياسة الخصوصية والقانون المعمول به.",
    cookiesTitle: "استخدام ملفات تعريف الارتباط (Cookies)",
    cookiesDesc: "يستخدم المشغل تقنيات مختلفة لجمع المعلومات من جهاز المستخدم. معظم المتصفحات تقبل ملفات تعريف الارتباط تلقائيًا، لكن يمكن للمستخدم تغيير الإعدادات.",
    securityTitle: "أمن المعلومات",
    securityDesc: "يطبق المشغل أنظمة وإجراءات أمن معلومات حديثة. هذه الأنظمة تقلل المخاطر لكنها لا توفر أمانًا مطلقًا.",
    changesTitle: "تغييرات في سياسة الخصوصية",
    changesDesc: "يحق للمشغل تغيير أحكام سياسة الخصوصية من وقت لآخر. سيتم نشر إشعار بالتغييرات الجوهرية على الموقع.",
  },
};

// ─── Terms Page ─────────────────────────────────────────
const terms = {
  en: {
    title: "Terms of Use",
    generalTitle: "General", generalDesc: "Welcome to Alice Jewelry and Watches website. Use of this site is subject to the terms of use detailed below. By browsing the site and/or making a purchase, you confirm that you have read and agree to these terms.",
    aboutTitle: "About Alice Jewelry", aboutDesc: "Alice Jewelry and Watches was founded in 1984 by owners David Burmad and his brother Albert Burmad. The store is named after David's wife, Alice Burmad. Alice started with selling watches and gold jewelry, and over the years grew to become the leading jewelry chain in the south.",
    purchaseTitle: "Purchase and Payment",
    purchaseItem1: "All prices on the site are in New Israeli Shekel and include VAT",
    purchaseItem2: "Prices are valid at the time of publication and may change without prior notice",
    purchaseItem3: "Payment will be made using recognized credit cards or other payment methods offered on the site",
    purchaseItem4: "We reserve the right to cancel an order in case of a price or product description error",
    shippingTitle: "Shipping", shippingDesc: "Deliveries are made through recognized courier companies throughout the country. Estimated delivery time is 3-7 business days from the order date. Shipping cost will appear on the payment page before completing the order.",
    copyrightTitle: "Copyright and Intellectual Property", copyrightDesc: "All content, images, logos and designs on the site are the property of Alice Jewelry or its business partners and are protected by copyright. Copying, reproducing, distributing or commercial use of this content without prior written permission is prohibited.",
    privacyTitle: "Privacy and Information Security", privacyDesc: "We are committed to protecting your personal information. Information collected on the site will be used solely for the purposes detailed in our privacy policy. We employ advanced security measures to protect your personal information.",
    liabilityTitle: "Limitation of Liability", liabilityP1: "Alice Jewelry will not be liable for any direct or indirect damage arising from use of the site or reliance on information displayed on it.", liabilityP2: "We make every effort to display products as accurately as possible, but there may be slight differences between displayed images and the actual product.",
    changesTitle: "Changes to Terms of Use", changesDesc: "Alice Jewelry reserves the right to update and change the terms of use from time to time without prior notice. Continued use of the site after changes constitutes agreement to the updated terms.",
    contactTitle: "Contact Us", contactDesc: "For any question or inquiry regarding the terms of use, you may contact us through the contact page on the site or through the contact details in the footer.",
    jurisdictionTitle: "Jurisdiction", jurisdictionDesc: "These terms of use shall be governed exclusively by the laws of the State of Israel. Exclusive jurisdiction shall be vested in the competent courts in the Southern District.",
  },
  ru: {
    title: "Условия использования",
    generalTitle: "Общие положения", generalDesc: "Добро пожаловать на сайт Алис Ювелирные изделия и Часы. Используя сайт, вы подтверждаете, что прочитали и согласны с условиями использования.",
    aboutTitle: "Об Алис Ювелирные изделия", aboutDesc: "Алис Ювелирные изделия и Часы была основана в 1984 году. За годы компания стала ведущей ювелирной сетью на юге.",
    purchaseTitle: "Покупка и оплата",
    purchaseItem1: "Все цены указаны в шекелях и включают НДС",
    purchaseItem2: "Цены актуальны на момент публикации и могут изменяться",
    purchaseItem3: "Оплата производится кредитными картами или другими способами",
    purchaseItem4: "Мы оставляем за собой право отменить заказ при ошибке в цене",
    shippingTitle: "Доставка", shippingDesc: "Доставка осуществляется курьерскими компаниями по всей стране. Ориентировочный срок — 3-7 рабочих дней.",
    copyrightTitle: "Авторские права", copyrightDesc: "Весь контент, изображения и дизайн защищены авторским правом. Копирование без разрешения запрещено.",
    privacyTitle: "Конфиденциальность и безопасность", privacyDesc: "Мы обязуемся защищать ваши персональные данные. Мы используем передовые средства защиты.",
    liabilityTitle: "Ограничение ответственности", liabilityP1: "Алис Ювелирные изделия не несёт ответственности за прямой или косвенный ущерб.", liabilityP2: "Мы стараемся точно отображать товары, но возможны незначительные отличия.",
    changesTitle: "Изменения условий", changesDesc: "Алис оставляет за собой право изменять условия без предварительного уведомления.",
    contactTitle: "Связаться с нами", contactDesc: "По вопросам об условиях использования свяжитесь через страницу контактов.",
    jurisdictionTitle: "Юрисдикция", jurisdictionDesc: "Настоящие условия регулируются законодательством Израиля. Юрисдикция принадлежит судам Южного округа.",
  },
  ar: {
    title: "شروط الاستخدام",
    generalTitle: "عام", generalDesc: "مرحبًا بكم في موقع أليس للمجوهرات والساعات. باستخدام الموقع فإنك توافق على شروط الاستخدام.",
    aboutTitle: "عن أليس للمجوهرات", aboutDesc: "تأسست أليس للمجوهرات والساعات عام 1984 ونمت لتصبح سلسلة المجوهرات الرائدة في الجنوب.",
    purchaseTitle: "الشراء والدفع",
    purchaseItem1: "جميع الأسعار بالشيكل وتشمل ضريبة القيمة المضافة",
    purchaseItem2: "الأسعار صالحة وقت النشر وقابلة للتغيير",
    purchaseItem3: "يتم الدفع ببطاقات الائتمان أو وسائل دفع أخرى",
    purchaseItem4: "نحتفظ بالحق في إلغاء الطلب في حالة خطأ في السعر",
    shippingTitle: "الشحن", shippingDesc: "يتم التوصيل عبر شركات شحن معتمدة. مدة التسليم المقدرة 3-7 أيام عمل.",
    copyrightTitle: "حقوق النشر والملكية الفكرية", copyrightDesc: "جميع المحتويات والتصاميم محمية بحقوق النشر. يُمنع النسخ دون إذن مكتوب.",
    privacyTitle: "الخصوصية وأمن المعلومات", privacyDesc: "نلتزم بحماية معلوماتك الشخصية ونستخدم وسائل أمان متقدمة.",
    liabilityTitle: "تحديد المسؤولية", liabilityP1: "لا تتحمل أليس المسؤولية عن أي ضرر مباشر أو غير مباشر.", liabilityP2: "نبذل كل جهد لعرض المنتجات بدقة، لكن قد تكون هناك فروقات طفيفة.",
    changesTitle: "تغييرات في الشروط", changesDesc: "تحتفظ أليس بالحق في تحديث الشروط دون إشعار مسبق.",
    contactTitle: "اتصل بنا", contactDesc: "لأي استفسار حول شروط الاستخدام، يمكنكم التواصل عبر صفحة الاتصال.",
    jurisdictionTitle: "الاختصاص القضائي", jurisdictionDesc: "تخضع هذه الشروط حصريًا لقوانين دولة إسرائيل.",
  },
};

// ─── GiftCard Page ─────────────────────────────────────────
const giftCard = {
  en: {
    title: "Gift Card", heroDesc: "Alice's gift card gives your loved ones the option to choose the perfect jewelry for them",
    chooseAmount: "Choose an amount", afterPurchase: "After purchase, the gift card code will be sent to your email",
    selectedAmount: "Selected amount", addToCart: "Add to Cart",
    info1: "The gift card will be sent to your email immediately after purchase",
    info2: "The gift card can be redeemed for payment on the website only",
    info3: "The gift card has no expiration date",
    notAvailable: "The gift card is currently unavailable",
    addedToCart: "Gift card added to cart",
  },
  ru: {
    title: "Подарочная карта", heroDesc: "Подарочная карта Алис даст вашим близким возможность выбрать идеальное украшение",
    chooseAmount: "Выберите сумму", afterPurchase: "После покупки код подарочной карты будет отправлен на ваш email",
    selectedAmount: "Выбранная сумма", addToCart: "Добавить в корзину",
    info1: "Подарочная карта будет отправлена на ваш email сразу после покупки",
    info2: "Подарочную карту можно использовать для оплаты только на сайте",
    info3: "Подарочная карта не имеет срока действия",
    notAvailable: "Подарочная карта сейчас недоступна",
    addedToCart: "Подарочная карта добавлена в корзину",
  },
  ar: {
    title: "بطاقة هدايا", heroDesc: "بطاقة هدايا أليس تمنح أحبائكم خيار اختيار المجوهرات المثالية",
    chooseAmount: "اختر المبلغ", afterPurchase: "بعد الشراء، سيتم إرسال رمز بطاقة الهدايا إلى بريدكم الإلكتروني",
    selectedAmount: "المبلغ المختار", addToCart: "أضف إلى السلة",
    info1: "سيتم إرسال بطاقة الهدايا إلى بريدكم فور الشراء",
    info2: "يمكن استخدام بطاقة الهدايا للدفع على الموقع فقط",
    info3: "بطاقة الهدايا ليس لها تاريخ انتهاء",
    notAvailable: "بطاقة الهدايا غير متوفرة حاليًا",
    addedToCart: "تمت إضافة بطاقة الهدايا إلى السلة",
  },
};

// ─── TrackOrder Page ─────────────────────────────────────────
const trackOrder = {
  en: {
    title: "Repair Tracking", subtitle: "Track your order status in real time",
    enterPhone: "Enter your phone number", sendCode: "We'll send you a verification code",
    phoneLabel: "Phone number", sendingCode: "Sending code...", sendOtp: "Send me a verification code",
    haveAccount: "Have an account?", loginHere: "Login here",
    howItWorks: "How does it work?",
    step1Title: "Enter phone number", step1Desc: "The phone number appears on the receipt you received when submitting the repair",
    step2Title: "Verify identity", step2Desc: "Enter the verification code sent to you",
    step3Title: "View orders", step3Desc: "Track the status in real time",
    enterOtp: "Enter verification code", otpSentTo: "Verification code sent to number",
    verifying: "Verifying...", resendCode: "Resend code", resendIn: "Resend code",
    changePhone: "Change phone number",
    openAccount: "Open an account and get benefits!", joinUs: "Join us and get updates, exclusive discounts and easier tracking",
    toPersonalArea: "To personal area",
    yourOrders: "Your orders", ordersFound: "orders found", checkAnother: "Check another order",
    noOrdersFound: "No orders found", noOrdersDesc: "No orders found for this number. If you recently placed an order, it may not have been entered into the system yet.",
    pending: "Pending", inRepair: "In repair", waitingInStore: "Waiting in store", delivered: "Delivered",
    requestNumber: "Request number", created: "Created", updated: "Updated",
    storePickup: "Store pickup", pickupDesc: "You can pick up your order from our store during business hours",
    addressLabel: "Address", phoneContact: "Phone", businessHours: "Business Hours",
    emailLabel: "Email", navigateWaze: "Navigate with Waze",
  },
  ru: {
    title: "Отслеживание ремонта", subtitle: "Отслеживайте статус заказа в реальном времени",
    enterPhone: "Введите номер телефона", sendCode: "Мы отправим вам код подтверждения",
    phoneLabel: "Номер телефона", sendingCode: "Отправка кода...", sendOtp: "Отправить код подтверждения",
    haveAccount: "Есть аккаунт?", loginHere: "Войти здесь",
    howItWorks: "Как это работает?",
    step1Title: "Введите номер телефона", step1Desc: "Номер телефона указан на квитанции",
    step2Title: "Подтвердите личность", step2Desc: "Введите отправленный код",
    step3Title: "Просмотрите заказы", step3Desc: "Отслеживайте статус в реальном времени",
    enterOtp: "Введите код подтверждения", otpSentTo: "Код отправлен на номер",
    verifying: "Проверяем...", resendCode: "Отправить новый код", resendIn: "Отправить код",
    changePhone: "Изменить номер",
    openAccount: "Откройте аккаунт и получите преимущества!", joinUs: "Присоединяйтесь и получайте обновления и скидки",
    toPersonalArea: "В личный кабинет",
    yourOrders: "Ваши заказы", ordersFound: "заказов найдено", checkAnother: "Проверить другой заказ",
    noOrdersFound: "Заказы не найдены", noOrdersDesc: "Заказов по этому номеру не найдено.",
    pending: "Ожидание", inRepair: "В ремонте", waitingInStore: "Ожидает в магазине", delivered: "Доставлен",
    requestNumber: "Номер заявки", created: "Создан", updated: "Обновлён",
    storePickup: "Самовывоз из магазина", pickupDesc: "Вы можете забрать заказ в часы работы",
    addressLabel: "Адрес", phoneContact: "Телефон", businessHours: "Часы работы",
    emailLabel: "Эл. почта", navigateWaze: "Навигация через Waze",
  },
  ar: {
    title: "تتبع الإصلاحات", subtitle: "تتبع حالة طلبك في الوقت الفعلي",
    enterPhone: "أدخل رقم هاتفك", sendCode: "سنرسل لك رمز تحقق",
    phoneLabel: "رقم الهاتف", sendingCode: "جاري إرسال الرمز...", sendOtp: "أرسل لي رمز تحقق",
    haveAccount: "لديك حساب؟", loginHere: "سجل دخولك هنا",
    howItWorks: "كيف يعمل؟",
    step1Title: "أدخل رقم الهاتف", step1Desc: "رقم الهاتف موجود على الإيصال",
    step2Title: "تحقق من الهوية", step2Desc: "أدخل رمز التحقق المرسل إليك",
    step3Title: "اعرض الطلبات", step3Desc: "تتبع الحالة في الوقت الفعلي",
    enterOtp: "أدخل رمز التحقق", otpSentTo: "تم إرسال رمز التحقق إلى الرقم",
    verifying: "جاري التحقق...", resendCode: "إعادة إرسال الرمز", resendIn: "إعادة إرسال",
    changePhone: "تغيير رقم الهاتف",
    openAccount: "افتح حسابًا واحصل على مزايا!", joinUs: "انضم إلينا واحصل على تحديثات وخصومات حصرية",
    toPersonalArea: "إلى المنطقة الشخصية",
    yourOrders: "طلباتك", ordersFound: "طلبات وُجدت", checkAnother: "تحقق من طلب آخر",
    noOrdersFound: "لم يتم العثور على طلبات", noOrdersDesc: "لم يتم العثور على طلبات لهذا الرقم.",
    pending: "قيد الانتظار", inRepair: "قيد الإصلاح", waitingInStore: "ينتظر في المتجر", delivered: "تم التسليم",
    requestNumber: "رقم الطلب", created: "تم الإنشاء", updated: "تم التحديث",
    storePickup: "الاستلام من المتجر", pickupDesc: "يمكنك استلام طلبك خلال ساعات العمل",
    addressLabel: "العنوان", phoneContact: "الهاتف", businessHours: "ساعات العمل",
    emailLabel: "البريد الإلكتروني", navigateWaze: "انتقل عبر Waze",
  },
};

// ─── RingSizeGuide Page ─────────────────────────────────────────
const ringSizeGuide = {
  en: {
    title: "Ring Size Guide", subtitle: "We've prepared a size guide to help you order the ring in the right size",
    importantNote: "Important note:", importantNoteDesc: "Ring sizes vary from right hand to left hand and depend on weather conditions and finger effort before measurement. It is recommended to measure after rest and in reasonable weather conditions.",
    toolsTitle: "Required tools:", tool1: "Thread", tool2: "Ruler", tool3: "Finger",
    haveRing: "Already have a ring?", haveRingDesc: "If you already have a ring and want to find out its size, you can use the size finder app:",
    downloadAndroid: "Download the app for Android, click here!", downloadIphone: "Download the app for iPhone, click here!",
    letsStart: "OK, let's start:",
    step1: "Using a sewing thread, or any other thread, wrap it around the desired finger at its thickest part.",
    step2: "Mark the spot on the thread representing the finger circumference with a pen.",
    step3: "Place the thread on a ruler, check the thread length to the marked area and compare to the following table:",
    sizeTable: "Size Table", circumference: "Size by finger circumference (mm)", sizeNumber: "Size by number",
  },
  ru: {
    title: "Гид по размерам колец", subtitle: "Мы подготовили гид по размерам, который поможет заказать кольцо подходящего размера",
    importantNote: "Важно:", importantNoteDesc: "Размер колец варьируется от правой руки к левой и зависит от погодных условий. Рекомендуется измерять после отдыха.",
    toolsTitle: "Необходимые инструменты:", tool1: "Нитка", tool2: "Линейка", tool3: "Палец",
    haveRing: "Уже есть кольцо?", haveRingDesc: "Если у вас уже есть кольцо, используйте приложение для определения размера:",
    downloadAndroid: "Скачать для Android, нажмите здесь!", downloadIphone: "Скачать для iPhone, нажмите здесь!",
    letsStart: "Итак, начнём:",
    step1: "С помощью нитки оберните палец в самом широком месте.",
    step2: "Отметьте ручкой место на нитке, обозначающее окружность пальца.",
    step3: "Положите нитку на линейку, проверьте длину до отметки и сравните с таблицей:",
    sizeTable: "Таблица размеров", circumference: "Размер по окружности (мм)", sizeNumber: "Размер по номеру",
  },
  ar: {
    title: "دليل مقاسات الخواتم", subtitle: "أعددنا لكم دليل مقاسات لمساعدتكم في طلب الخاتم بالمقاس المناسب",
    importantNote: "ملاحظة مهمة:", importantNoteDesc: "تختلف مقاسات الخواتم من اليد اليمنى إلى اليسرى وتعتمد على الطقس. يُنصح بالقياس بعد الراحة.",
    toolsTitle: "الأدوات المطلوبة:", tool1: "خيط", tool2: "مسطرة", tool3: "إصبع",
    haveRing: "لديك خاتم بالفعل؟", haveRingDesc: "إذا كان لديك خاتم وتريد معرفة مقاسه، يمكنك استخدام تطبيق تحديد المقاس:",
    downloadAndroid: "لتحميل التطبيق للأندرويد، اضغط هنا!", downloadIphone: "لتحميل التطبيق للآيفون، اضغط هنا!",
    letsStart: "حسنًا، لنبدأ:",
    step1: "باستخدام خيط، لف الإصبع المطلوب في أعرض جزء منه.",
    step2: "ضع علامة بالقلم على الخيط عند نقطة محيط الإصبع.",
    step3: "ضع الخيط على المسطرة، تحقق من الطول حتى العلامة وقارن بالجدول التالي:",
    sizeTable: "جدول المقاسات", circumference: "المقاس حسب محيط الإصبع (مم)", sizeNumber: "المقاس حسب الرقم",
  },
};

// ─── Accessibility Page ─────────────────────────────────────────
const accessibility = {
  en: {
    title: "Accessibility Statement", companyName: "Alice Jewelry Ltd.",
    updatedDate: "Accessibility statement updated on 10.10.2023",
    intro: "We at Alice Jewelry strive to ensure that our digital services are accessible to people with disabilities, and therefore have invested resources to facilitate the use of the site for people with disabilities, as much as possible, out of the belief that every person deserves the right to live in equality, dignity, comfort and independence.",
    importance: "We attach great importance to providing equal opportunity for people with disabilities to use the information displayed on the site:",
    accessibleSiteTitle: "What is an Accessible Website",
    accessibleSiteDesc: "An accessible website is a site that allows a person with a disability to browse with the same level of efficiency and enjoyment as other users, using the capabilities of the system on which it operates and through assistive technologies for accessibility.",
    adaptationsTitle: "Accessibility Adaptations on the Website",
    adaptationsDesc: "The accessibility adaptations on the site were made in accordance with Section C: Internet Services in the Equal Rights for Persons with Disabilities Regulations (Service Accessibility Adjustments) 2013, based on the Israeli Standard SI 5568 which is based on WCAG 2.0 guidelines, and the site has been made accessible to level AA.",
    supportTitle: "The site supports the use of assistive technologies such as:",
    support1: "Screen reader software", support2: "Keyboard navigation", support3: "Font enlargement to 4 sizes",
    support4: "Flash blocking", support5: "Contrast adjustment", support6: "Color blindness adaptation",
    support7: "Readable font change", support8: "Cursor enlargement", support9: "Display enlargement up to ~200%",
    support10: "Link and heading highlighting",
    orderAccessibility: "Accessibility for Website Orders",
    orderAccessibilityDesc: "If you need accessibility adjustments for making a purchase and/or for coordinating and receiving delivery, please note this in the order comments.",
    contactTitle: "Contact Us",
    contactIntro: "If you find an accessibility issue on the website or need help, please contact us:",
    contactPhone: "Phone: 079-5575576", contactEmail: "Email: store@alice.jewelry",
    closingMessage: "We wish you a pleasant browsing experience on our website!",
  },
  ru: {
    title: "Заявление о доступности", companyName: "Алис Ювелирные изделия ООО",
    updatedDate: "Заявление обновлено 10.10.2023",
    intro: "Мы в Алис Ювелирные изделия стремимся обеспечить доступность наших цифровых услуг для людей с ограниченными возможностями.",
    importance: "Мы придаём большое значение предоставлению равных возможностей для использования информации на сайте:",
    accessibleSiteTitle: "Что такое доступный сайт",
    accessibleSiteDesc: "Доступный сайт позволяет человеку с ограниченными возможностями пользоваться им с тем же уровнем эффективности, что и другие пользователи.",
    adaptationsTitle: "Адаптации доступности на сайте",
    adaptationsDesc: "Адаптации доступности были выполнены в соответствии с израильским стандартом SI 5568 на основе WCAG 2.0, до уровня AA.",
    supportTitle: "Сайт поддерживает вспомогательные технологии:",
    support1: "Программы чтения с экрана", support2: "Навигация с клавиатуры", support3: "Увеличение шрифта",
    support4: "Блокировка мерцания", support5: "Настройка контраста", support6: "Адаптация для дальтоников",
    support7: "Изменение на читаемый шрифт", support8: "Увеличение курсора", support9: "Увеличение отображения до ~200%",
    support10: "Выделение ссылок и заголовков",
    orderAccessibility: "Доступность для заказов",
    orderAccessibilityDesc: "Если вам нужны адаптации для совершения покупки или доставки, укажите это в комментариях к заказу.",
    contactTitle: "Свяжитесь с нами",
    contactIntro: "Если вы нашли проблему с доступностью или нуждаетесь в помощи:",
    contactPhone: "Телефон: 079-5575576", contactEmail: "Email: store@alice.jewelry",
    closingMessage: "Желаем вам приятного пользования нашим сайтом!",
  },
  ar: {
    title: "بيان إمكانية الوصول", companyName: "أليس للمجوهرات م.ض",
    updatedDate: "تم تحديث بيان إمكانية الوصول بتاريخ 10.10.2023",
    intro: "نحن في أليس للمجوهرات نسعى لضمان إمكانية الوصول إلى خدماتنا الرقمية للأشخاص ذوي الإعاقة.",
    importance: "نولي أهمية كبيرة لتوفير فرص متساوية لاستخدام المعلومات المعروضة على الموقع:",
    accessibleSiteTitle: "ما هو موقع إنترنت يسهل الوصول إليه",
    accessibleSiteDesc: "موقع يسهل الوصول إليه يتيح للأشخاص ذوي الإعاقة التصفح بنفس مستوى الكفاءة.",
    adaptationsTitle: "تعديلات إمكانية الوصول على الموقع",
    adaptationsDesc: "تم إجراء تعديلات إمكانية الوصول وفقًا للمعيار الإسرائيلي المبني على إرشادات WCAG 2.0 حتى المستوى AA.",
    supportTitle: "يدعم الموقع التقنيات المساعدة مثل:",
    support1: "برامج قراءة الشاشة", support2: "التصفح بلوحة المفاتيح", support3: "تكبير الخط",
    support4: "حظر الوميض", support5: "ضبط التباين", support6: "تكييف لعمى الألوان",
    support7: "تغيير الخط لأكثر قابلية للقراءة", support8: "تكبير المؤشر", support9: "تكبير العرض حتى ~200%",
    support10: "تمييز الروابط والعناوين",
    orderAccessibility: "إمكانية الوصول للطلبات",
    orderAccessibilityDesc: "إذا كنت بحاجة إلى ترتيبات وصول عند الشراء أو التسليم، يرجى ذكر ذلك في ملاحظات الطلب.",
    contactTitle: "اتصل بنا",
    contactIntro: "إذا وجدت مشكلة في إمكانية الوصول أو تحتاج إلى مساعدة:",
    contactPhone: "هاتف: 079-5575576", contactEmail: "بريد إلكتروني: store@alice.jewelry",
    closingMessage: "نتمنى لكم تجربة تصفح ممتعة على موقعنا!",
  },
};

// ─── CookiePolicy Page ─────────────────────────────────────────
const cookiePolicy = {
  en: {
    title: "Cookie Policy", subtitle: "This page explains how we use cookies and similar technologies on our site, in accordance with GDPR regulations and the European ePrivacy Directive.",
    managePreferences: "Manage my cookie preferences",
    whatAreCookies: "What are Cookies?", whatAreCookiesDesc: "Cookies are small text files stored on your device (computer, phone or tablet) when you visit a website. They allow the site to \"remember\" your actions and preferences over time.",
    cookieTypes: "Types of Cookies We Use",
    necessaryTitle: "Necessary Cookies", necessaryDesc: "These cookies are essential for the basic functioning of the site. They enable actions like site navigation, access to secure areas, saving items in the shopping cart and logging into your account.",
    necessaryBadge: "Always active • Cannot be disabled",
    analyticsTitle: "Statistics and Analytics Cookies", analyticsDesc: "These cookies help us understand how visitors use the site by collecting anonymous statistical information.",
    analyticsProviders: "Providers:", 
    marketingTitle: "Marketing and Advertising Cookies", marketingDesc: "These cookies are used to display relevant advertisements and track the effectiveness of marketing campaigns.",
    functionalTitle: "Functional Cookies", functionalDesc: "These cookies enable advanced and personalized functions such as saving language preferences, viewing videos, social network integration and additional interactive features.",
    manageTitle: "How to Manage Your Cookies?", manageIntro: "You have full control over the cookies stored on your device:",
    manageViaWebsite: "Through our website:", manageViaWebsiteDesc: "Click the \"Manage Preferences\" button at the top of this page or the \"Cookie Management\" link at the bottom of any page.",
    manageViaBrowser: "Through your browser:", manageViaBrowserDesc: "Most browsers allow you to view, delete and block cookies from specific sites. Refer to your browser settings for more information.",
    thirdPartyTitle: "Third Party Providers", thirdPartyIntro: "We work with trusted third-party providers for analytics and marketing:",
    updatesTitle: "Policy Updates", updatesDesc: "We may update this policy from time to time. Any material changes will be published on this page with the last update date.",
    lastUpdate: "Last update: December 2025",
    contactTitle: "Contact Us", contactDesc: "If you have questions about our use of cookies, please contact us:",
  },
  ru: {
    title: "Политика использования файлов Cookie", subtitle: "Эта страница объясняет, как мы используем файлы cookie в соответствии с GDPR.",
    managePreferences: "Управление настройками cookie",
    whatAreCookies: "Что такое Cookie?", whatAreCookiesDesc: "Cookie — это небольшие текстовые файлы, сохраняемые на вашем устройстве при посещении сайта.",
    cookieTypes: "Типы cookie, которые мы используем",
    necessaryTitle: "Необходимые cookie", necessaryDesc: "Эти cookie необходимы для базовой работы сайта: навигация, безопасные зоны, корзина и авторизация.",
    necessaryBadge: "Всегда активны • Нельзя отключить",
    analyticsTitle: "Аналитические cookie", analyticsDesc: "Помогают понять, как посетители используют сайт, собирая анонимную статистику.",
    analyticsProviders: "Поставщики:",
    marketingTitle: "Маркетинговые cookie", marketingDesc: "Используются для отображения релевантной рекламы и отслеживания эффективности кампаний.",
    functionalTitle: "Функциональные cookie", functionalDesc: "Обеспечивают расширенные функции: сохранение языка, видео, интеграцию с соцсетями.",
    manageTitle: "Как управлять cookie?", manageIntro: "У вас полный контроль над cookie:",
    manageViaWebsite: "Через наш сайт:", manageViaWebsiteDesc: "Нажмите кнопку «Управление настройками» вверху этой страницы.",
    manageViaBrowser: "Через браузер:", manageViaBrowserDesc: "Большинство браузеров позволяют просматривать и блокировать cookie.",
    thirdPartyTitle: "Сторонние поставщики", thirdPartyIntro: "Мы работаем с надёжными сторонними поставщиками:",
    updatesTitle: "Обновления политики", updatesDesc: "Мы можем обновлять эту политику. Существенные изменения будут опубликованы на этой странице.",
    lastUpdate: "Последнее обновление: декабрь 2025",
    contactTitle: "Свяжитесь с нами", contactDesc: "Если у вас есть вопросы об использовании cookie:",
  },
  ar: {
    title: "سياسة ملفات تعريف الارتباط (Cookies)", subtitle: "توضح هذه الصفحة كيفية استخدامنا لملفات تعريف الارتباط وفقًا لـ GDPR.",
    managePreferences: "إدارة تفضيلات ملفات تعريف الارتباط",
    whatAreCookies: "ما هي ملفات تعريف الارتباط؟", whatAreCookiesDesc: "ملفات تعريف الارتباط هي ملفات نصية صغيرة تُخزن على جهازك عند زيارة موقع إنترنت.",
    cookieTypes: "أنواع ملفات تعريف الارتباط التي نستخدمها",
    necessaryTitle: "ملفات تعريف الارتباط الضرورية", necessaryDesc: "ضرورية لعمل الموقع الأساسي: التنقل والمناطق الآمنة والسلة وتسجيل الدخول.",
    necessaryBadge: "نشطة دائمًا • لا يمكن تعطيلها",
    analyticsTitle: "ملفات تعريف الارتباط التحليلية", analyticsDesc: "تساعدنا في فهم كيفية استخدام الزوار للموقع من خلال جمع إحصاءات مجهولة.",
    analyticsProviders: "المزودون:",
    marketingTitle: "ملفات تعريف الارتباط التسويقية", marketingDesc: "تُستخدم لعرض إعلانات ذات صلة وتتبع فعالية الحملات.",
    functionalTitle: "ملفات تعريف الارتباط الوظيفية", functionalDesc: "تمكّن وظائف متقدمة مثل حفظ اللغة والفيديو وتكامل وسائل التواصل.",
    manageTitle: "كيف تدير ملفات تعريف الارتباط؟", manageIntro: "لديك تحكم كامل:",
    manageViaWebsite: "عبر موقعنا:", manageViaWebsiteDesc: "اضغط على زر «إدارة التفضيلات» أعلى هذه الصفحة.",
    manageViaBrowser: "عبر المتصفح:", manageViaBrowserDesc: "معظم المتصفحات تتيح عرض وحذف وحظر ملفات تعريف الارتباط.",
    thirdPartyTitle: "مزودو الطرف الثالث", thirdPartyIntro: "نعمل مع مزودين موثوقين:",
    updatesTitle: "تحديثات السياسة", updatesDesc: "قد نحدث هذه السياسة. ستُنشر التغييرات الجوهرية على هذه الصفحة.",
    lastUpdate: "آخر تحديث: ديسمبر 2025",
    contactTitle: "اتصل بنا", contactDesc: "إذا كانت لديك أسئلة حول استخدامنا لملفات تعريف الارتباط:",
  },
};

// ─── Wishlist Login Prompt ─────────────────────────────────────────
const wishlistPrompt = {
  en: {
    title: "Save to Favorites",
    subtitle: "How would you like to save this product?",
    loginButton: "Log in to account",
    recommended: "(recommended)",
    saveLocally: "Save without logging in",
    savedSuccess: "Product saved to favorites!",
    footerNote: "Logging in allows access to favorites from any device",
  },
  ru: {
    title: "Сохранить в избранное",
    subtitle: "Как вы хотите сохранить этот товар?",
    loginButton: "Войти в аккаунт",
    recommended: "(рекомендуется)",
    saveLocally: "Сохранить без входа",
    savedSuccess: "Товар сохранён в избранное!",
    footerNote: "Вход позволяет получить доступ к избранному с любого устройства",
  },
  ar: {
    title: "حفظ في المفضلة",
    subtitle: "كيف تريد حفظ هذا المنتج؟",
    loginButton: "تسجيل الدخول",
    recommended: "(موصى به)",
    saveLocally: "حفظ بدون تسجيل دخول",
    savedSuccess: "تم حفظ المنتج في المفضلة!",
    footerNote: "تسجيل الدخول يتيح الوصول للمفضلة من أي جهاز",
  },
};

// ─── Ring Size Guide Dialog ─────────────────────────────────────────
const ringSizeDialog = {
  en: {
    triggerLabel: "Ring Size Guide",
    title: "Ring Size Guide",
    circumference: "Finger Circumference (mm)",
    size: "Size",
  },
  ru: {
    triggerLabel: "Таблица размеров колец",
    title: "Таблица размеров колец",
    circumference: "Окружность пальца (мм)",
    size: "Размер",
  },
  ar: {
    triggerLabel: "دليل مقاسات الخواتم",
    title: "دليل مقاسات الخواتم",
    circumference: "محيط الإصبع (مم)",
    size: "المقاس",
  },
};

// ─── Cookie Banner ─────────────────────────────────────────
const cookieBanner = {
  en: {
    title: "We use cookies",
    description: "We use cookies and similar technologies to improve your browsing experience, personalize content and ads, provide social media features and analyze site traffic. Some cookies are essential for proper site operation, while others help us improve our service.",
    learnMore: "Learn more",
    acceptAll: "Accept All Cookies",
    rejectAll: "Reject Non-Essential Cookies",
    managePreferences: "Manage Preferences",
  },
  ru: {
    title: "Мы используем файлы cookie",
    description: "Мы используем файлы cookie и аналогичные технологии для улучшения вашего опыта просмотра, персонализации контента и рекламы, предоставления функций социальных сетей и анализа трафика. Некоторые cookie необходимы для работы сайта, другие помогают нам улучшить сервис.",
    learnMore: "Подробнее",
    acceptAll: "Принять все cookie",
    rejectAll: "Отклонить необязательные cookie",
    managePreferences: "Управление настройками",
  },
  ar: {
    title: "نحن نستخدم ملفات تعريف الارتباط",
    description: "نستخدم ملفات تعريف الارتباط وتقنيات مشابهة لتحسين تجربة التصفح وتخصيص المحتوى والإعلانات وتوفير ميزات وسائل التواصل الاجتماعي وتحليل حركة المرور. بعض ملفات تعريف الارتباط ضرورية لعمل الموقع، بينما يساعدنا البعض الآخر في تحسين خدمتنا.",
    learnMore: "اقرأ المزيد",
    acceptAll: "قبول جميع ملفات تعريف الارتباط",
    rejectAll: "رفض ملفات تعريف الارتباط غير الضرورية",
    managePreferences: "إدارة التفضيلات",
  },
};

// ─── Cookie Preferences Modal ─────────────────────────────────────────
const cookiePreferences = {
  en: {
    title: "Cookie Preferences",
    description: "Manage your cookie preferences. You can enable or disable different types of cookies below.",
    acceptAll: "Accept All",
    rejectAll: "Reject All",
    savePreferences: "Save Preferences",
    alwaysActive: "Always Active",
    necessaryTitle: "Necessary Cookies",
    necessaryDesc: "These cookies are essential for the website to function properly. They cannot be disabled.",
    analyticsTitle: "Analytics Cookies",
    analyticsDesc: "These cookies help us understand how visitors interact with the website by collecting and reporting information anonymously.",
    marketingTitle: "Marketing Cookies",
    marketingDesc: "These cookies are used to track visitors across websites to display relevant advertisements.",
    functionalTitle: "Functional Cookies",
    functionalDesc: "These cookies enable the website to provide enhanced functionality and personalization.",
  },
  ru: {
    title: "Настройки cookie",
    description: "Управляйте настройками cookie. Вы можете включить или отключить различные типы cookie.",
    acceptAll: "Принять все",
    rejectAll: "Отклонить все",
    savePreferences: "Сохранить настройки",
    alwaysActive: "Всегда активны",
    necessaryTitle: "Необходимые cookie",
    necessaryDesc: "Эти cookie необходимы для правильной работы сайта. Их нельзя отключить.",
    analyticsTitle: "Аналитические cookie",
    analyticsDesc: "Помогают нам понять, как посетители взаимодействуют с сайтом.",
    marketingTitle: "Маркетинговые cookie",
    marketingDesc: "Используются для отслеживания посетителей и показа релевантной рекламы.",
    functionalTitle: "Функциональные cookie",
    functionalDesc: "Обеспечивают расширенные функции и персонализацию.",
  },
  ar: {
    title: "تفضيلات ملفات تعريف الارتباط",
    description: "أدر تفضيلات ملفات تعريف الارتباط. يمكنك تمكين أو تعطيل أنواع مختلفة أدناه.",
    acceptAll: "قبول الكل",
    rejectAll: "رفض الكل",
    savePreferences: "حفظ التفضيلات",
    alwaysActive: "نشط دائمًا",
    necessaryTitle: "ملفات تعريف الارتباط الضرورية",
    necessaryDesc: "ضرورية لعمل الموقع بشكل صحيح. لا يمكن تعطيلها.",
    analyticsTitle: "ملفات تعريف الارتباط التحليلية",
    analyticsDesc: "تساعدنا على فهم كيفية تفاعل الزوار مع الموقع.",
    marketingTitle: "ملفات تعريف الارتباط التسويقية",
    marketingDesc: "تُستخدم لتتبع الزوار وعرض إعلانات ذات صلة.",
    functionalTitle: "ملفات تعريف الارتباط الوظيفية",
    functionalDesc: "تتيح وظائف محسّنة وتخصيصًا.",
  },
};

// ─── Product Card ─────────────────────────────────────────
const productCard = {
  en: {
    addToCart: "Add to Cart",
  },
  ru: {
    addToCart: "В корзину",
  },
  ar: {
    addToCart: "أضف إلى السلة",
  },
};

// ─── Search Dialog ─────────────────────────────────────────
const search = {
  en: {
    triggerLabel: "Search products",
    title: "Product Search",
    placeholder: "Search jewelry by name...",
    voiceStop: "Stop recording",
    voiceStart: "Voice search",
    imageSearch: "Search by image",
    smartFilter: "Smart filter",
    visualSearchAdvanced: "Advanced visual search:",
    imageSearchLabel: "Image search:",
    results: "results",
    priceRange: "Price Range",
    category: "Category",
    all: "All",
    clearFilter: "Clear filter",
    searching: "Searching...",
    sitePages: "Site Pages",
    categories: "Categories",
    foundProducts: (count: number) => count === 1 ? "Found 1 product" : `Found ${count} products`,
    pages: "pages",
    categoriesCount: "categories",
    filtered: "(filtered)",
    foundFor: "for",
    relatedProducts: "Related products you might like",
    noExactResults: "No exact results found for",
    butWeHaveRelated: "but we have related products",
    forTerm: "for",
    noResults: "No results found for",
    tryDifferent: "Try a different search term",
    tryClearFilter: "Try clearing the filter",
    couldNotLoad: "Couldn't load products right now",
    startTyping: "Start typing to search products",
    similarPercent: "similar",
  },
  ru: {
    triggerLabel: "Поиск товаров",
    title: "Поиск товаров",
    placeholder: "Искать украшения по названию...",
    voiceStop: "Остановить запись",
    voiceStart: "Голосовой поиск",
    imageSearch: "Поиск по изображению",
    smartFilter: "Умный фильтр",
    visualSearchAdvanced: "Расширенный визуальный поиск:",
    imageSearchLabel: "Поиск по изображению:",
    results: "результатов",
    priceRange: "Диапазон цен",
    category: "Категория",
    all: "Все",
    clearFilter: "Очистить фильтр",
    searching: "Поиск...",
    sitePages: "Страницы сайта",
    categories: "Категории",
    foundProducts: (count: number) => count === 1 ? "Найден 1 товар" : `Найдено ${count} товаров`,
    pages: "страниц",
    categoriesCount: "категорий",
    filtered: "(отфильтровано)",
    foundFor: "по запросу",
    relatedProducts: "Похожие товары, которые могут вас заинтересовать",
    noExactResults: "Точных результатов не найдено для",
    butWeHaveRelated: "но у нас есть похожие товары",
    forTerm: "для",
    noResults: "Ничего не найдено для",
    tryDifferent: "Попробуйте другой запрос",
    tryClearFilter: "Попробуйте очистить фильтр",
    couldNotLoad: "Не удалось загрузить товары",
    startTyping: "Начните вводить для поиска товаров",
    similarPercent: "сходство",
  },
  ar: {
    triggerLabel: "البحث عن منتجات",
    title: "البحث عن منتجات",
    placeholder: "ابحث عن مجوهرات بالاسم...",
    voiceStop: "إيقاف التسجيل",
    voiceStart: "بحث صوتي",
    imageSearch: "بحث بالصورة",
    smartFilter: "فلتر ذكي",
    visualSearchAdvanced: "بحث مرئي متقدم:",
    imageSearchLabel: "بحث بالصورة:",
    results: "نتائج",
    priceRange: "نطاق السعر",
    category: "الفئة",
    all: "الكل",
    clearFilter: "مسح الفلتر",
    searching: "جاري البحث...",
    sitePages: "صفحات الموقع",
    categories: "الفئات",
    foundProducts: (count: number) => count === 1 ? "تم العثور على منتج واحد" : `تم العثور على ${count} منتجات`,
    pages: "صفحات",
    categoriesCount: "فئات",
    filtered: "(مُفلتر)",
    foundFor: "لـ",
    relatedProducts: "منتجات ذات صلة قد تعجبك",
    noExactResults: "لم يتم العثور على نتائج دقيقة لـ",
    butWeHaveRelated: "لكن لدينا منتجات ذات صلة",
    forTerm: "لـ",
    noResults: "لم يتم العثور على نتائج لـ",
    tryDifferent: "جرب كلمة بحث مختلفة",
    tryClearFilter: "جرب مسح الفلتر",
    couldNotLoad: "لم نتمكن من تحميل المنتجات حالياً",
    startTyping: "ابدأ بالكتابة للبحث عن منتجات",
    similarPercent: "تشابه",
  },
};

// ─── Cart Drawer ─────────────────────────────────────────
const cartDrawer = {
  en: {
    triggerLabel: "Shopping Cart",
  },
  ru: {
    triggerLabel: "Корзина",
  },
  ar: {
    triggerLabel: "سلة التسوق",
  },
};

// ─── SEO - Titles, Descriptions & Breadcrumbs ─────────────
const seo = {
  en: {
    siteName: "Alice | Jewellery & Watches",
    homeTitle: "Alice Jewellery | Jewellery & Watches in Beer Sheva Since 1984",
    homeDescription: "Alice Jewellery – the leading jewellery store in Beer Sheva since 1984. Huge selection of engagement rings, gold necklaces, bracelets, earrings and luxury watches. Online shop with shipping across Israel.",
    shopTitle: "Online Jewellery Store",
    shopDescription: "Alice online jewellery store – huge selection of 14K gold jewellery, engagement rings, necklaces, earrings and bracelets. Free shipping and full warranty.",
    contactTitle: "Contact Us",
    contactDescription: "Contact Alice Jewellery & Watches – visit us at 58 Herzl St., Beer Sheva, call 079-5575576 or send us a message.",
    aboutTitle: "About Us – Alice Jewellery",
    aboutDescription: "Alice Jewellery – the leading jewellery company in the south since 1984. Specialising in engagement rings, wedding rings, gold jewellery and luxury watches.",
    faqTitle: "Frequently Asked Questions",
    faqDescription: "Answers to frequently asked questions about jewellery, orders, shipping, returns and warranty at Alice Jewellery.",
    blogTitle: "Jewellery Blog – Alice",
    blogDescription: "Alice Jewellery blog – articles, tips and guides on jewellery, engagement rings, gold and latest trends.",
    privacyTitle: "Privacy Policy",
    privacyDescription: "Alice Jewellery privacy policy – how we collect, use and protect your personal information.",
    termsTitle: "Terms of Use",
    termsDescription: "Terms and conditions of Alice Jewellery online store – purchase, shipping, returns and legal terms.",
    shippingTitle: "Shipping Policy",
    shippingDescription: "Alice Jewellery shipping policy – home delivery, self-pickup, delivery times and costs. Shipping throughout Israel.",
    returnsTitle: "Returns & Exchanges Policy",
    returnsDescription: "Alice Jewellery returns and exchanges policy – how to return or exchange products, deadlines and conditions.",
    accessibilityTitle: "Accessibility Statement",
    accessibilityDescription: "Alice Jewellery accessibility statement – our commitment to making the website accessible to all users.",
    giftCardTitle: "Gift Card – Alice Jewellery",
    giftCardDescription: "Alice Jewellery gift card – the perfect gift. Choose an amount and send to a loved one.",
    trackOrderTitle: "Track Order",
    trackOrderDescription: "Track your order or repair status at Alice Jewellery.",
    ringSizeTitle: "Ring Size Guide",
    ringSizeDescription: "Complete guide to measuring your ring size at home – ring size chart and simple measuring methods.",
    cookieTitle: "Cookie Policy",
    cookieDescription: "Alice Jewellery cookie policy – how we use cookies and how you can manage your preferences.",
    cartTitle: "Shopping Cart",
    cartDescription: "Your shopping cart at Alice Jewellery.",
    authTitle: "Login / Sign Up",
    authDescription: "Login or create an account at Alice Jewellery – manage your orders and get personal discounts.",
    wishlistTitle: "Wishlist",
    wishlistDescription: "Your wishlist at Alice Jewellery – save your favourite jewellery and share with friends and family.",
    // Breadcrumbs
    home: "Home",
    breadcrumbShipping: "Shipping Policy",
    breadcrumbReturns: "Returns & Exchanges",
    breadcrumbPrivacy: "Privacy Policy",
    breadcrumbTerms: "Terms of Use",
    breadcrumbFaq: "FAQ",
    breadcrumbBlog: "Blog",
    breadcrumbAccessibility: "Accessibility",
    breadcrumbGiftCard: "Gift Card",
    breadcrumbRingSize: "Ring Size Guide",
    breadcrumbCookie: "Cookie Policy",
    breadcrumbWishlist: "Wishlist",
    // Organization schema
    orgDescription: "Leading jewellery company in Beer Sheva since 1984. Online jewellery store – engagement rings, wedding rings, bracelets, necklaces, pendants & earrings.",
    // Navigation
    navEarrings: "Gold Earrings",
    navNecklaces: "Necklaces with Pendant",
    navRings: "Alice Rings",
    navBracelets: "Bracelets",
    navBlog: "Jewellery Blog",
    navStore: "Jewellery Store in Beer Sheva, Herzl 58",
    navFaq: "FAQ",
    navAbout: "About",
  },
  ru: {
    siteName: "Алиса | Ювелирные изделия и часы",
    homeTitle: "Ювелирные изделия Алиса | Украшения и часы в Беэр-Шеве с 1984 года",
    homeDescription: "Ювелирные изделия Алиса – ведущий ювелирный магазин в Беэр-Шеве с 1984 года. Огромный выбор помолвочных колец, золотых цепочек, браслетов, серёг и часов класса люкс. Интернет-магазин с доставкой по Израилю.",
    shopTitle: "Интернет-магазин ювелирных изделий",
    shopDescription: "Интернет-магазин Алиса – золотые украшения 14К, помолвочные кольца, цепочки, серьги и браслеты. Бесплатная доставка и полная гарантия.",
    contactTitle: "Свяжитесь с нами",
    contactDescription: "Свяжитесь с Алиса Ювелирные изделия – посетите нас: ул. Герцля 58, Беэр-Шева, тел. 079-5575576.",
    aboutTitle: "О нас – Алиса Ювелирные изделия",
    aboutDescription: "Алиса – ведущая ювелирная компания юга с 1984 года. Помолвочные кольца, обручальные кольца, золотые украшения и люксовые часы.",
    faqTitle: "Часто задаваемые вопросы",
    faqDescription: "Ответы на частые вопросы о ювелирных изделиях, заказах, доставке, возвратах и гарантии в Алиса.",
    blogTitle: "Блог о ювелирных изделиях – Алиса",
    blogDescription: "Блог Алиса – статьи, советы и руководства по ювелирным изделиям, помолвочным кольцам и трендам.",
    privacyTitle: "Политика конфиденциальности",
    privacyDescription: "Политика конфиденциальности Алиса – как мы собираем, используем и защищаем вашу личную информацию.",
    termsTitle: "Условия использования",
    termsDescription: "Условия использования интернет-магазина Алиса – покупка, доставка, возвраты и юридические условия.",
    shippingTitle: "Политика доставки",
    shippingDescription: "Политика доставки Алиса – доставка на дом, самовывоз, сроки и стоимость. Доставка по всему Израилю.",
    returnsTitle: "Политика возвратов и обменов",
    returnsDescription: "Политика возвратов и обменов Алиса – как вернуть или обменять товар, сроки и условия.",
    accessibilityTitle: "Заявление о доступности",
    accessibilityDescription: "Заявление о доступности Алиса – наше обязательство сделать сайт доступным для всех пользователей.",
    giftCardTitle: "Подарочная карта – Алиса",
    giftCardDescription: "Подарочная карта Алиса – идеальный подарок. Выберите сумму и отправьте близкому человеку.",
    trackOrderTitle: "Отслеживание заказа",
    trackOrderDescription: "Отслеживание статуса заказа или ремонта в Алиса.",
    ringSizeTitle: "Гид по размерам колец",
    ringSizeDescription: "Полное руководство по измерению размера кольца дома – таблица размеров и простые методы измерения.",
    cookieTitle: "Политика cookies",
    cookieDescription: "Политика использования cookie-файлов Алиса – как мы используем cookies и как вы можете управлять настройками.",
    cartTitle: "Корзина",
    cartDescription: "Ваша корзина в Алиса.",
    authTitle: "Вход / Регистрация",
    authDescription: "Войдите или создайте аккаунт в Алиса – управляйте заказами и получайте персональные скидки.",
    wishlistTitle: "Избранное",
    wishlistDescription: "Ваш список избранного в Алиса – сохраняйте любимые украшения и делитесь с друзьями.",
    home: "Главная",
    breadcrumbShipping: "Политика доставки",
    breadcrumbReturns: "Возвраты и обмены",
    breadcrumbPrivacy: "Конфиденциальность",
    breadcrumbTerms: "Условия использования",
    breadcrumbFaq: "Вопросы и ответы",
    breadcrumbBlog: "Блог",
    breadcrumbAccessibility: "Доступность",
    breadcrumbGiftCard: "Подарочная карта",
    breadcrumbRingSize: "Гид по размерам колец",
    breadcrumbCookie: "Политика cookies",
    breadcrumbWishlist: "Избранное",
    orgDescription: "Ведущая ювелирная компания в Беэр-Шеве с 1984 года. Интернет-магазин – помолвочные и обручальные кольца, браслеты, цепочки, подвески и серьги.",
    navEarrings: "Золотые серьги",
    navNecklaces: "Цепочки с подвеской",
    navRings: "Кольца Алиса",
    navBracelets: "Браслеты",
    navBlog: "Блог о ювелирных изделиях",
    navStore: "Ювелирный магазин в Беэр-Шеве, Герцля 58",
    navFaq: "Вопросы и ответы",
    navAbout: "О нас",
  },
  ar: {
    siteName: "أليس | مجوهرات وساعات",
    homeTitle: "مجوهرات أليس | مجوهرات وساعات في بئر السبع منذ 1984",
    homeDescription: "مجوهرات أليس – متجر المجوهرات الرائد في بئر السبع منذ 1984. تشكيلة ضخمة من خواتم الخطبة، قلائد الذهب، الأساور، الأقراط وساعات فاخرة. متجر أونلاين مع شحن لجميع أنحاء إسرائيل.",
    shopTitle: "متجر مجوهرات أونلاين",
    shopDescription: "متجر أليس للمجوهرات أونلاين – تشكيلة ضخمة من مجوهرات ذهب 14 قيراط، خواتم خطبة، قلائد، أقراط وأساور. شحن مجاني وضمان كامل.",
    contactTitle: "اتصل بنا",
    contactDescription: "تواصل مع أليس للمجوهرات والساعات – زورونا في شارع هرتسل 58، بئر السبع، هاتف 079-5575576.",
    aboutTitle: "عن أليس – مجوهرات",
    aboutDescription: "أليس للمجوهرات – الشركة الرائدة في الجنوب منذ 1984. خواتم خطبة وزواج ومجوهرات ذهبية وساعات فاخرة.",
    faqTitle: "أسئلة شائعة",
    faqDescription: "إجابات على الأسئلة الشائعة حول المجوهرات والطلبات والشحن والإرجاع والضمان في أليس.",
    blogTitle: "مدوّنة المجوهرات – أليس",
    blogDescription: "مدوّنة أليس للمجوهرات – مقالات ونصائح وأدلة حول المجوهرات وخواتم الخطبة والذهب وأحدث الصيحات.",
    privacyTitle: "سياسة الخصوصية",
    privacyDescription: "سياسة خصوصية أليس – كيف نجمع ونستخدم ونحمي معلوماتك الشخصية.",
    termsTitle: "شروط الاستخدام",
    termsDescription: "شروط وأحكام متجر أليس – الشراء والشحن والإرجاع والشروط القانونية.",
    shippingTitle: "سياسة الشحن",
    shippingDescription: "سياسة شحن أليس – توصيل للمنزل، استلام ذاتي، مواعيد التسليم والتكاليف. شحن لجميع أنحاء إسرائيل.",
    returnsTitle: "سياسة الإرجاع والاستبدال",
    returnsDescription: "سياسة إرجاع واستبدال أليس – كيفية إرجاع أو استبدال المنتجات والمواعيد والشروط.",
    accessibilityTitle: "بيان إمكانية الوصول",
    accessibilityDescription: "بيان إمكانية الوصول لأليس – التزامنا بجعل الموقع متاحاً لجميع المستخدمين.",
    giftCardTitle: "بطاقة هدية – أليس",
    giftCardDescription: "بطاقة هدية أليس – الهدية المثالية. اختر مبلغاً وأرسل لشخص عزيز.",
    trackOrderTitle: "تتبع الطلب",
    trackOrderDescription: "تتبع حالة طلبك أو إصلاحك في أليس للمجوهرات.",
    ringSizeTitle: "دليل مقاسات الخواتم",
    ringSizeDescription: "دليل شامل لقياس مقاس الخاتم في المنزل – جدول المقاسات وطرق قياس بسيطة.",
    cookieTitle: "سياسة ملفات تعريف الارتباط",
    cookieDescription: "سياسة ملفات تعريف الارتباط لأليس – كيف نستخدم ملفات تعريف الارتباط وكيف يمكنك إدارة تفضيلاتك.",
    cartTitle: "سلة التسوق",
    cartDescription: "سلة التسوق الخاصة بك في أليس.",
    authTitle: "تسجيل الدخول / إنشاء حساب",
    authDescription: "سجّل الدخول أو أنشئ حساباً في أليس – أدر طلباتك واحصل على خصومات شخصية.",
    wishlistTitle: "المفضلة",
    wishlistDescription: "قائمة المفضلة في أليس – احفظ مجوهراتك المفضلة وشاركها مع الأصدقاء والعائلة.",
    home: "الرئيسية",
    breadcrumbShipping: "سياسة الشحن",
    breadcrumbReturns: "الإرجاع والاستبدال",
    breadcrumbPrivacy: "سياسة الخصوصية",
    breadcrumbTerms: "شروط الاستخدام",
    breadcrumbFaq: "أسئلة شائعة",
    breadcrumbBlog: "المدوّنة",
    breadcrumbAccessibility: "إمكانية الوصول",
    breadcrumbGiftCard: "بطاقة هدية",
    breadcrumbRingSize: "دليل مقاسات الخواتم",
    breadcrumbCookie: "سياسة ملفات تعريف الارتباط",
    breadcrumbWishlist: "المفضلة",
    orgDescription: "شركة المجوهرات الرائدة في بئر السبع منذ 1984. متجر أونلاين – خواتم خطبة وزواج وأساور وقلائد وأقراط.",
    navEarrings: "أقراط ذهبية",
    navNecklaces: "قلائد مع تعليقة",
    navRings: "خواتم أليس",
    navBracelets: "أساور",
    navBlog: "مدوّنة المجوهرات",
    navStore: "متجر مجوهرات في بئر السبع، هرتسل 58",
    navFaq: "أسئلة شائعة",
    navAbout: "عن أليس",
  },
};

// ─── Try On Dialog ─────────────────────────────────────────
const tryOn = {
  en: {
    title: "Try On —",
    before: "Before",
    after: "After",
    tryAgain: "Try Again",
    download: "Download",
    addToCart: "Add to Cart",
    close: "Close",
    processing: "Processing...",
    starting: "Starting...",
    step1: "Analyzing the image...",
    step2: "Detecting body area...",
    step3: "Fitting the jewelry...",
    step4: "Processing details...",
    step5: "Almost ready...",
    ready: "Ready!",
    processTime: "The process may take up to a minute",
    uploadPrompt: "Upload a clear photo of yourself — frontal, good lighting, and the relevant area (neck/ears/hands) visible.",
    yourImage: "Your photo",
    tryOnMe: "Try on me",
    upload: "Upload",
    capture: "Capture",
    imagesNotSaved: "Images are not saved or collected — single use only.",
    imageError: "Error processing the image",
    cameraError: "Cannot access the camera. Please enable access in your browser settings.",
    flipCamera: "Flip camera",
    cancel: "Cancel",
    errorCreating: "Error creating the image",
    switchCameraError: "Cannot switch camera.",
  },
  ru: {
    title: "Примерить —",
    before: "До",
    after: "После",
    tryAgain: "Попробовать снова",
    download: "Скачать",
    addToCart: "В корзину",
    close: "Закрыть",
    processing: "Обработка...",
    starting: "Начинаем...",
    step1: "Анализируем изображение...",
    step2: "Определяем область тела...",
    step3: "Примеряем украшение...",
    step4: "Обрабатываем детали...",
    step5: "Почти готово...",
    ready: "Готово!",
    processTime: "Процесс может занять до минуты",
    uploadPrompt: "Загрузите чёткое фото — фронтальное, с хорошим освещением, область (шея/уши/руки) открыта.",
    yourImage: "Ваше фото",
    tryOnMe: "Примерить",
    upload: "Загрузить",
    capture: "Сфотографировать",
    imagesNotSaved: "Изображения не сохраняются — одноразовое использование.",
    imageError: "Ошибка обработки изображения",
    cameraError: "Не удаётся получить доступ к камере. Разрешите доступ в настройках браузера.",
    flipCamera: "Переключить камеру",
    cancel: "Отмена",
    errorCreating: "Ошибка при создании изображения",
    switchCameraError: "Не удаётся переключить камеру.",
  },
  ar: {
    title: "جرّب —",
    before: "قبل",
    after: "بعد",
    tryAgain: "حاول مرة أخرى",
    download: "تحميل",
    addToCart: "أضف إلى السلة",
    close: "إغلاق",
    processing: "جاري المعالجة...",
    starting: "جاري البدء...",
    step1: "تحليل الصورة...",
    step2: "تحديد منطقة الجسم...",
    step3: "تجربة المجوهرات...",
    step4: "معالجة التفاصيل...",
    step5: "تقريباً جاهز...",
    ready: "جاهز!",
    processTime: "قد تستغرق العملية حتى دقيقة",
    uploadPrompt: "ارفع صورة واضحة لك — أمامية، إضاءة جيدة، والمنطقة المعنية (رقبة/أذنين/يدين) ظاهرة.",
    yourImage: "صورتك",
    tryOnMe: "جرّب عليّ",
    upload: "رفع",
    capture: "تصوير",
    imagesNotSaved: "الصور لا تُحفظ ولا تُجمع — استخدام لمرة واحدة فقط.",
    imageError: "خطأ في معالجة الصورة",
    cameraError: "لا يمكن الوصول إلى الكاميرا. يرجى السماح بالوصول في إعدادات المتصفح.",
    flipCamera: "قلب الكاميرا",
    cancel: "إلغاء",
    errorCreating: "خطأ في إنشاء الصورة",
    switchCameraError: "لا يمكن تبديل الكاميرا.",
  },
};

// ─── Image Search Uploader ─────────────────────────────────
const imageSearch = {
  en: {
    cameraError: "Cannot access the camera. Please enable access in your browser settings.",
    switchCameraError: "Cannot switch camera.",
    flipCamera: "Flip camera",
    cancel: "Cancel",
    capture: "Capture",
    preparing: "Preparing image...",
    identifyingType: "Identifying jewellery type...",
    searchingProducts: "Searching matching products...",
    loadingImages: "Loading product images...",
    comparingImages: "Comparing images...",
    rankingSimilarity: "Ranking by similarity...",
    finishing: "Finishing...",
    done: "Done...",
    systemBusy: "The system is busy. Try again in a few minutes.",
    serviceError: "There is a problem with the service. Try again later.",
    analyzeError: "Error analyzing the image",
    foundProducts: "similar products found!",
    identified: "Identified:",
    error: "Error",
    processingImage: "Processing image...",
    imageProcessError: "Error processing the image, try a different image",
    unsupportedFile: "Unsupported file",
    uploadImageFile: "Please upload an image file",
    searchImage: "Image for search",
    visualSearchTime: "Visual search may take up to a minute",
    dragImageHere: "Drag an image here",
    orClickToSelect: "or click to select a file from your device",
    advancedVisualSearch: "Advanced visual search — finds similar products by image",
    takePhoto: "Take a photo",
  },
  ru: {
    cameraError: "Не удаётся получить доступ к камере. Разрешите доступ в настройках браузера.",
    switchCameraError: "Не удаётся переключить камеру.",
    flipCamera: "Переключить камеру",
    cancel: "Отмена",
    capture: "Сфотографировать",
    preparing: "Подготовка изображения...",
    identifyingType: "Определение типа украшения...",
    searchingProducts: "Поиск подходящих товаров...",
    loadingImages: "Загрузка изображений товаров...",
    comparingImages: "Сравнение изображений...",
    rankingSimilarity: "Ранжирование по сходству...",
    finishing: "Завершаем...",
    done: "Готово...",
    systemBusy: "Система загружена. Попробуйте через несколько минут.",
    serviceError: "Проблема с сервисом. Попробуйте позже.",
    analyzeError: "Ошибка анализа изображения",
    foundProducts: "похожих товаров найдено!",
    identified: "Определено:",
    error: "Ошибка",
    processingImage: "Обработка изображения...",
    imageProcessError: "Ошибка обработки изображения, попробуйте другое",
    unsupportedFile: "Неподдерживаемый файл",
    uploadImageFile: "Загрузите файл изображения",
    searchImage: "Изображение для поиска",
    visualSearchTime: "Визуальный поиск может занять до минуты",
    dragImageHere: "Перетащите изображение сюда",
    orClickToSelect: "или нажмите для выбора файла",
    advancedVisualSearch: "Продвинутый визуальный поиск — находит похожие товары по фото",
    takePhoto: "Сделать фото",
  },
  ar: {
    cameraError: "لا يمكن الوصول إلى الكاميرا. يرجى السماح بالوصول في إعدادات المتصفح.",
    switchCameraError: "لا يمكن تبديل الكاميرا.",
    flipCamera: "قلب الكاميرا",
    cancel: "إلغاء",
    capture: "تصوير",
    preparing: "تحضير الصورة...",
    identifyingType: "تحديد نوع المجوهرات...",
    searchingProducts: "البحث عن منتجات مطابقة...",
    loadingImages: "تحميل صور المنتجات...",
    comparingImages: "مقارنة الصور...",
    rankingSimilarity: "ترتيب حسب التشابه...",
    finishing: "جاري الانتهاء...",
    done: "تم...",
    systemBusy: "النظام مشغول. حاول مرة أخرى بعد بضع دقائق.",
    serviceError: "هناك مشكلة في الخدمة. حاول لاحقاً.",
    analyzeError: "خطأ في تحليل الصورة",
    foundProducts: "منتجات مشابهة!",
    identified: "تم التعرف:",
    error: "خطأ",
    processingImage: "معالجة الصورة...",
    imageProcessError: "خطأ في معالجة الصورة، جرب صورة أخرى",
    unsupportedFile: "ملف غير مدعوم",
    uploadImageFile: "يرجى رفع ملف صورة",
    searchImage: "صورة للبحث",
    visualSearchTime: "البحث البصري قد يستغرق حتى دقيقة",
    dragImageHere: "اسحب صورة إلى هنا",
    orClickToSelect: "أو انقر لاختيار ملف من جهازك",
    advancedVisualSearch: "بحث بصري متقدم — يجد منتجات مشابهة حسب الصورة",
    takePhoto: "التقط صورة",
  },
};

// ─── Engraving Options ─────────────────────────────────────
const engraving = {
  en: {
    title: "Personal Engraving",
    subtitle: "Add a personal touch to your jewellery",
    multipleLocations: "engraving locations available",
    selectFont: "Select font style",
    required: "*",
    recommendation: "Recommendation:",
    rec2: "You can engrave up to 3 words per field. Example: Field 1 - \"I love you\", Field 2 - \"Forever yours\"",
    rec3: "You can engrave up to 3 words per field. Example: one word per field, or a short sentence in one field",
    rec4: "You can engrave up to 3 words per field. Example: name in field 1, date in field 2, short message in other fields",
    rec5: "You can engrave up to 3 words in each of the fields. Recommended to split the message between fields",
    engravingText: "Engraving text",
    engravingTextNum: "Engraving text",
    words: "words",
    example1: "e.g.: I love you",
    example2a: "e.g.: I love you",
    example2b: "e.g.: Forever yours",
    example3a: "e.g.: Love",
    example3b: "e.g.: Forever",
    example3c: "e.g.: Ours",
    example4a: "e.g.: Name",
    example4b: "e.g.: Date",
    example4c: "e.g.: Love",
    example4d: "e.g.: Forever",
    exampleDefault: "e.g.: Text",
    upTo3Words: "(up to 3 words)",
    optional: "- optional",
    infoMultiple: "The texts will be engraved on the jewellery in different locations in the style you chose",
    infoSingle: "The text will be engraved on the jewellery in the style you chose",
    selectFontError: "Please select a font",
    enterTextError: "Please enter engraving text",
  },
  ru: {
    title: "Личная гравировка",
    subtitle: "Добавьте личный штрих к вашему украшению",
    multipleLocations: "мест для гравировки доступно",
    selectFont: "Выберите стиль шрифта",
    required: "*",
    recommendation: "Рекомендация:",
    rec2: "Можно выгравировать до 3 слов в каждом поле. Например: поле 1 — «Я люблю тебя», поле 2 — «Навсегда твоя»",
    rec3: "Можно выгравировать до 3 слов в каждом поле. Например: одно слово в каждом поле или короткую фразу",
    rec4: "Можно выгравировать до 3 слов в каждом поле. Например: имя в поле 1, дату в поле 2, сообщение в остальных",
    rec5: "Можно выгравировать до 3 слов в каждом из полей. Рекомендуется разделить послание между полями",
    engravingText: "Текст гравировки",
    engravingTextNum: "Текст гравировки",
    words: "слов",
    example1: "напр.: Я люблю тебя",
    example2a: "напр.: Я люблю тебя",
    example2b: "напр.: Навсегда твоя",
    example3a: "напр.: Любовь",
    example3b: "напр.: Навсегда",
    example3c: "напр.: Наша",
    example4a: "напр.: Имя",
    example4b: "напр.: Дата",
    example4c: "напр.: Любовь",
    example4d: "напр.: Навсегда",
    exampleDefault: "напр.: Текст",
    upTo3Words: "(до 3 слов)",
    optional: "— необязательно",
    infoMultiple: "Тексты будут выгравированы на украшении в разных местах в выбранном стиле",
    infoSingle: "Текст будет выгравирован на украшении в выбранном стиле",
    selectFontError: "Выберите шрифт",
    enterTextError: "Введите текст для гравировки",
  },
  ar: {
    title: "نقش شخصي",
    subtitle: "أضف لمسة شخصية إلى مجوهراتك",
    multipleLocations: "مواقع نقش متاحة",
    selectFont: "اختر نمط الخط",
    required: "*",
    recommendation: "توصية:",
    rec2: "يمكن نقش حتى 3 كلمات في كل حقل. مثال: حقل 1 — «أحبك»، حقل 2 — «إلى الأبد»",
    rec3: "يمكن نقش حتى 3 كلمات في كل حقل. مثال: كلمة واحدة في كل حقل أو جملة قصيرة",
    rec4: "يمكن نقش حتى 3 كلمات في كل حقل. مثال: الاسم في حقل 1، التاريخ في حقل 2",
    rec5: "يمكن نقش حتى 3 كلمات في كل حقل. يُنصح بتقسيم الرسالة بين الحقول",
    engravingText: "نص النقش",
    engravingTextNum: "نص النقش",
    words: "كلمات",
    example1: "مثال: أحبك",
    example2a: "مثال: أحبك",
    example2b: "مثال: إلى الأبد",
    example3a: "مثال: حب",
    example3b: "مثال: أبداً",
    example3c: "مثال: لنا",
    example4a: "مثال: الاسم",
    example4b: "مثال: التاريخ",
    example4c: "مثال: حب",
    example4d: "مثال: أبداً",
    exampleDefault: "مثال: نص",
    upTo3Words: "(حتى 3 كلمات)",
    optional: "— اختياري",
    infoMultiple: "ستُنقش النصوص على المجوهرات في مواقع مختلفة بالنمط الذي اخترته",
    infoSingle: "سيُنقش النص على المجوهرات بالنمط الذي اخترته",
    selectFontError: "يرجى اختيار خط",
    enterTextError: "يرجى إدخال نص للنقش",
  },
};

// ─── Order History ─────────────────────────────────────────
const orderHistory = {
  en: {
    title: "Order History", ordersCount: "orders", noEmail: "Please log in to see your orders",
    noOrders: "No orders to display", ordersAppearHere: "Orders will appear here after making a purchase",
    retry: "Try Again", reconnectShopify: "Reconnect Shopify", loadError: "Error loading order history",
    reconnectError: "Error reconnecting", quantity: "Qty:", sku: "SKU:",
    shippingAddress: "Shipping address:", shippingMethod: "Shipping method:",
    trackShipment: "Track Shipment", track: "Track", subtotal: "Subtotal",
    shipping: "Shipping", vat: "VAT", discount: "Discount", total: "Total",
    cancelled: "Cancelled", cancelledOn: "Cancelled on", note: "Note:",
    // Status translations
    PENDING: "Pending Payment", AUTHORIZED: "Authorized", PARTIALLY_PAID: "Partially Paid",
    PAID: "Paid", PARTIALLY_REFUNDED: "Partially Refunded", REFUNDED: "Refunded",
    VOIDED: "Voided", EXPIRED: "Expired", UNFULFILLED: "Awaiting Shipment",
    PARTIALLY_FULFILLED: "Partially Shipped", FULFILLED: "Shipped", RESTOCKED: "Restocked",
    ON_HOLD: "On Hold", SCHEDULED: "Scheduled", IN_PROGRESS: "In Progress",
    CUSTOMER: "Customer Request", FRAUD: "Fraud Suspicion", INVENTORY: "Out of Stock",
    DECLINED: "Payment Declined", OTHER: "Other Reason",
    SUCCESS: "Completed", OPEN: "Open", CANCELLED: "Cancelled", ERROR: "Error", FAILURE: "Failed",
  },
  ru: {
    title: "История заказов", ordersCount: "заказов", noEmail: "Войдите, чтобы увидеть заказы",
    noOrders: "Нет заказов для отображения", ordersAppearHere: "Заказы появятся здесь после покупки",
    retry: "Попробовать снова", reconnectShopify: "Переподключить Shopify", loadError: "Ошибка загрузки истории заказов",
    reconnectError: "Ошибка переподключения", quantity: "Кол-во:", sku: "Артикул:",
    shippingAddress: "Адрес доставки:", shippingMethod: "Способ доставки:",
    trackShipment: "Отслеживание доставки", track: "Отследить", subtotal: "Подытог",
    shipping: "Доставка", vat: "НДС", discount: "Скидка", total: "Итого",
    cancelled: "Отменён", cancelledOn: "Отменён", note: "Примечание:",
    PENDING: "Ожидание оплаты", AUTHORIZED: "Авторизован", PARTIALLY_PAID: "Частично оплачен",
    PAID: "Оплачен", PARTIALLY_REFUNDED: "Частично возвращён", REFUNDED: "Возвращён",
    VOIDED: "Аннулирован", EXPIRED: "Истёк", UNFULFILLED: "Ожидает отправки",
    PARTIALLY_FULFILLED: "Частично отправлен", FULFILLED: "Отправлен", RESTOCKED: "Возвращён на склад",
    ON_HOLD: "На удержании", SCHEDULED: "Запланирован", IN_PROGRESS: "В процессе",
    CUSTOMER: "Запрос клиента", FRAUD: "Подозрение в мошенничестве", INVENTORY: "Нет в наличии",
    DECLINED: "Платёж отклонён", OTHER: "Другая причина",
    SUCCESS: "Завершён", OPEN: "Открыт", CANCELLED: "Отменён", ERROR: "Ошибка", FAILURE: "Не удалось",
  },
  ar: {
    title: "سجل الطلبات", ordersCount: "طلبات", noEmail: "سجّل الدخول لعرض طلباتك",
    noOrders: "لا توجد طلبات للعرض", ordersAppearHere: "ستظهر الطلبات هنا بعد إجراء عملية شراء",
    retry: "حاول مرة أخرى", reconnectShopify: "إعادة الاتصال بـ Shopify", loadError: "خطأ في تحميل سجل الطلبات",
    reconnectError: "خطأ في إعادة الاتصال", quantity: "الكمية:", sku: "رمز المنتج:",
    shippingAddress: "عنوان الشحن:", shippingMethod: "طريقة الشحن:",
    trackShipment: "تتبع الشحنة", track: "تتبع", subtotal: "المجموع الفرعي",
    shipping: "الشحن", vat: "ضريبة", discount: "خصم", total: "المجموع",
    cancelled: "ملغى", cancelledOn: "ألغي في", note: "ملاحظة:",
    PENDING: "في انتظار الدفع", AUTHORIZED: "مصرح", PARTIALLY_PAID: "مدفوع جزئياً",
    PAID: "مدفوع", PARTIALLY_REFUNDED: "مسترد جزئياً", REFUNDED: "مسترد",
    VOIDED: "ملغى", EXPIRED: "منتهي الصلاحية", UNFULFILLED: "في انتظار الشحن",
    PARTIALLY_FULFILLED: "شُحن جزئياً", FULFILLED: "تم الشحن", RESTOCKED: "أُعيد للمخزون",
    ON_HOLD: "معلق", SCHEDULED: "مجدول", IN_PROGRESS: "قيد التنفيذ",
    CUSTOMER: "طلب العميل", FRAUD: "اشتباه احتيال", INVENTORY: "نفاد المخزون",
    DECLINED: "دفع مرفوض", OTHER: "سبب آخر",
    SUCCESS: "مكتمل", OPEN: "مفتوح", CANCELLED: "ملغى", ERROR: "خطأ", FAILURE: "فشل",
  },
};

// ─── Favorites Section ─────────────────────────────────────────
const favorites = {
  en: {
    title: "My Favorites", noFavorites: "You have no favorite products",
    startFavoriting: "Start marking products you like to easily find them here",
    moveAllToCart: "Move All to Cart", clearAll: "Clear All",
    favoriteProduct: "favorite product", favoriteProducts: "favorite products",
    movedToCart: "products moved to cart!", moveError: "Error moving products to cart",
    loadingFavorites: "Loading favorites...",
  },
  ru: {
    title: "Мои избранные", noFavorites: "У вас нет избранных товаров",
    startFavoriting: "Начните отмечать понравившиеся товары, чтобы легко находить их здесь",
    moveAllToCart: "Переместить всё в корзину", clearAll: "Очистить всё",
    favoriteProduct: "избранный товар", favoriteProducts: "избранных товаров",
    movedToCart: "товаров перемещено в корзину!", moveError: "Ошибка перемещения товаров",
    loadingFavorites: "Загрузка избранного...",
  },
  ar: {
    title: "المفضلة", noFavorites: "ليس لديك منتجات مفضلة",
    startFavoriting: "ابدأ بتحديد المنتجات التي تعجبك للعثور عليها بسهولة هنا",
    moveAllToCart: "نقل الكل إلى السلة", clearAll: "مسح الكل",
    favoriteProduct: "منتج مفضل", favoriteProducts: "منتجات مفضلة",
    movedToCart: "منتجات نُقلت إلى السلة!", moveError: "خطأ في نقل المنتجات",
    loadingFavorites: "جاري تحميل المفضلة...",
  },
};

// ─── User Repair Orders ─────────────────────────────────────────
const repairOrders = {
  en: {
    noOrders: "No orders found", noOrdersDesc: "When you have an order or active repair, you can track it here",
    yourOrders: "Your Orders", ordersFound: "orders found",
    requestNumber: "Request No.", created: "Created:", updated: "Updated:",
    // Status labels
    pending: "Pending", inRepair: "In Repair", waitingInStore: "Waiting in Store",
    completed: "Completed", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled",
    // Status descriptions
    pendingDesc: "Your order has been received and is awaiting processing",
    inRepairDesc: "Your jewelry is currently being repaired",
    waitingInStoreDesc: "Repair completed! Your jewelry is waiting for you in the store",
    completedDesc: "Repair completed!", shippedDesc: "Your order has been shipped",
    deliveredDesc: "Your order was delivered successfully", cancelledDesc: "The order was cancelled",
    // Store card
    storePickup: "Store Pickup", storePickupDesc: "You can pick up your order from our store during business hours",
    address: "Address", addressValue: "58 Herzl St., Beer Sheva",
    phone: "Phone", businessHours: "Business Hours",
    sunday: "Sunday: 10:00-19:00", monday: "Monday: 10:00-19:00", tuesday: "Tuesday: 09:45-14:00",
    wednesday: "Wednesday: 10:00-19:00", thursday: "Thursday: 10:00-19:00",
    email: "Email", navigateWaze: "Navigate with Waze", storeLocation: "Store Location",
  },
  ru: {
    noOrders: "Заказы не найдены", noOrdersDesc: "Когда у вас будет заказ или ремонт, вы сможете отслеживать его здесь",
    yourOrders: "Ваши заказы", ordersFound: "заказов найдено",
    requestNumber: "Запрос №", created: "Создан:", updated: "Обновлён:",
    pending: "Ожидание", inRepair: "В ремонте", waitingInStore: "Ожидает в магазине",
    completed: "Завершён", shipped: "Отправлен", delivered: "Доставлен", cancelled: "Отменён",
    pendingDesc: "Ваш заказ получен и ожидает обработки",
    inRepairDesc: "Ваше украшение сейчас в ремонте",
    waitingInStoreDesc: "Ремонт завершён! Ваше украшение ждёт вас в магазине",
    completedDesc: "Ремонт завершён!", shippedDesc: "Ваш заказ отправлен",
    deliveredDesc: "Ваш заказ успешно доставлен", cancelledDesc: "Заказ отменён",
    storePickup: "Самовывоз", storePickupDesc: "Заказ можно забрать из нашего магазина в часы работы",
    address: "Адрес", addressValue: "ул. Герцля 58, Беэр-Шева",
    phone: "Телефон", businessHours: "Часы работы",
    sunday: "Воскресенье: 10:00-19:00", monday: "Понедельник: 10:00-19:00", tuesday: "Вторник: 09:45-14:00",
    wednesday: "Среда: 10:00-19:00", thursday: "Четверг: 10:00-19:00",
    email: "Эл. почта", navigateWaze: "Навигация через Waze", storeLocation: "Расположение магазина",
  },
  ar: {
    noOrders: "لم يتم العثور على طلبات", noOrdersDesc: "عندما يكون لديك طلب أو إصلاح نشط، يمكنك تتبعه هنا",
    yourOrders: "طلباتك", ordersFound: "طلبات وُجدت",
    requestNumber: "طلب رقم", created: "أُنشئ:", updated: "تم التحديث:",
    pending: "قيد الانتظار", inRepair: "قيد الإصلاح", waitingInStore: "في انتظار بالمتجر",
    completed: "مكتمل", shipped: "تم الشحن", delivered: "تم التسليم", cancelled: "ملغى",
    pendingDesc: "تم استلام طلبك وهو في انتظار المعالجة",
    inRepairDesc: "مجوهراتك قيد الإصلاح حالياً",
    waitingInStoreDesc: "اكتمل الإصلاح! مجوهراتك في انتظارك بالمتجر",
    completedDesc: "اكتمل الإصلاح!", shippedDesc: "تم شحن طلبك",
    deliveredDesc: "تم تسليم طلبك بنجاح", cancelledDesc: "تم إلغاء الطلب",
    storePickup: "الاستلام من المتجر", storePickupDesc: "يمكنك استلام طلبك من متجرنا خلال ساعات العمل",
    address: "العنوان", addressValue: "شارع هرتسل 58، بئر السبع",
    phone: "الهاتف", businessHours: "ساعات العمل",
    sunday: "الأحد: 10:00-19:00", monday: "الإثنين: 10:00-19:00", tuesday: "الثلاثاء: 09:45-14:00",
    wednesday: "الأربعاء: 10:00-19:00", thursday: "الخميس: 10:00-19:00",
    email: "البريد الإلكتروني", navigateWaze: "التنقل عبر Waze", storeLocation: "موقع المتجر",
  },
};

// ─── User Messages ─────────────────────────────────────────
const userMessages = {
  en: {
    title: "My Messages", noMessages: "No messages yet", newBadge: "New",
    attachedFile: "Attached file", newMessage: "You have a new message!",
    loadError: "Error loading messages",
  },
  ru: {
    title: "Мои сообщения", noMessages: "Сообщений пока нет", newBadge: "Новое",
    attachedFile: "Прикреплённый файл", newMessage: "У вас новое сообщение!",
    loadError: "Ошибка загрузки сообщений",
  },
  ar: {
    title: "رسائلي", noMessages: "لا توجد رسائل بعد", newBadge: "جديد",
    attachedFile: "ملف مرفق", newMessage: "لديك رسالة جديدة!",
    loadError: "خطأ في تحميل الرسائل",
  },
};

// ─── Profile Tour ─────────────────────────────────────────
const profileTour = {
  en: {
    personalDetails: "Personal Details", personalDetailsDesc: "Here you can edit your personal information, including name, phone, and shipping address.",
    myOrders: "My Orders", myOrdersDesc: "View your order history and easily track your shipments.",
    myFavorites: "My Favorites", myFavoritesDesc: "All the products you marked as favorites are here. You can easily add them to your cart.",
    adminMessages: "Message Management", adminMessagesDesc: "As an admin, you can view and manage all messages sent through the contact form.",
    done: "Done", next: "Next",
  },
  ru: {
    personalDetails: "Личные данные", personalDetailsDesc: "Здесь вы можете редактировать свои данные: имя, телефон и адрес доставки.",
    myOrders: "Мои заказы", myOrdersDesc: "Просматривайте историю заказов и легко отслеживайте доставки.",
    myFavorites: "Мои избранные", myFavoritesDesc: "Все товары, которые вы отметили как избранные, находятся здесь. Легко добавляйте их в корзину.",
    adminMessages: "Управление сообщениями", adminMessagesDesc: "Как администратор, вы можете просматривать и управлять всеми сообщениями контактной формы.",
    done: "Готово", next: "Далее",
  },
  ar: {
    personalDetails: "البيانات الشخصية", personalDetailsDesc: "هنا يمكنك تعديل بياناتك الشخصية، بما في ذلك الاسم والهاتف وعنوان الشحن.",
    myOrders: "طلباتي", myOrdersDesc: "اعرض سجل طلباتك وتتبع شحناتك بسهولة.",
    myFavorites: "المفضلة", myFavoritesDesc: "جميع المنتجات التي حددتها كمفضلة موجودة هنا. يمكنك إضافتها بسهولة إلى السلة.",
    adminMessages: "إدارة الرسائل", adminMessagesDesc: "كمسؤول، يمكنك عرض وإدارة جميع الرسائل المرسلة عبر نموذج الاتصال.",
    done: "تم", next: "التالي",
  },
};

// ─── Profile Greeting ─────────────────────────────────────────
const profileGreeting = {
  en: { morning: "Good morning", afternoon: "Good afternoon", evening: "Good evening", night: "Good night" },
  ru: { morning: "Доброе утро", afternoon: "Добрый день", evening: "Добрый вечер", night: "Доброй ночи" },
  ar: { morning: "صباح الخير", afternoon: "مساء الخير", evening: "مساء الخير", night: "تصبح على خير" },
};

// ─── All sections combined ─────────────────────────────────
// ─── Admin UI ─────────────────────────────────────────
const admin = {
  en: {
    // Blog Management
    manageTags: "Manage Tags", newPost: "New Post", backToList: "Back to list",
    newTagPlaceholder: "New tag name...", addTag: "Add", noPostsYet: "No posts yet. Create your first post!",
    published: "Published", draft: "Draft", deletePost: "Delete post?", hide: "Hide", publish: "Publish",
    tagCreated: "Tag created", tagDeleted: "Tag deleted", postDeleted: "Post deleted", statusUpdated: "Status updated",
    deleteTag: "Delete tag", confirmDeleteTag: "Delete tag",
    // Slider
    sliderManagement: "Slider Management", addSlide: "Add slide", saveChanges: "Save changes",
    slideNumber: "Slide", active: "Active", image: "Image", uploadImage: "Upload image",
    title: "Title", titlePlaceholder: "Slide title", subtitle: "Subtitle", subtitlePlaceholder: "Subtitle",
    buttonText: "Button text", buttonTextPlaceholder: "e.g.: View collection",
    linkType: "Link type", category: "Category", externalLink: "External link", internalPage: "Internal page",
    selectCategory: "Select category", enterUrl: "URL address", selectPage: "Select page",
    noSlides: "No slides. Add a first slide to get started.",
    slidesSaved: "Slides saved successfully", slideSaveError: "Error saving slides",
    newSlideAdded: "New slide added", slideDeleted: "Slide deleted",
    imageUploaded: "Image uploaded successfully", imageUploadError: "Error uploading image",
    sliderLoadError: "Error loading slider data",
    // Repair Management
    smsBalance: "SMS balance", messages: "messages",
    searchPlaceholder: "Search by order number, name, phone...",
    addOrder: "Add order/repair", newOrder: "New order/repair", editOrder: "Edit order/repair",
    orderNumber: "Order number", rescan: "Rescan", orderExists: "This order number already exists.",
    guestCustomer: "Guest customer", registeredCustomer: "Registered customer",
    guestPhone: "Guest phone number *", guestPhoneNote: "Customer can track order via phone number with OTP code",
    searchCustomer: "Search customer", searchCustomerPlaceholder: "Search by name, phone or email...",
    noCustomersFound: "No customers found", titleField: "Title *", description: "Description",
    status: "Status", cancel: "Cancel", save: "Save",
    noOrdersFound: "No orders matching search", noOrders: "No orders/repairs at the moment",
    guest: "Guest", phone: "Phone", email: "Email", customer: "Customer",
    created: "Created", updated: "Updated", markDelivered: "Mark as delivered",
    alreadyDelivered: "Order already marked as delivered", statusDelivered: "Status changed to delivered",
    fillRequired: "Please fill in all required fields", orderAdded: "Order added successfully",
    orderUpdated: "Order updated successfully", orderDeleted: "Order deleted successfully",
    deleteOrder: "Delete this order?", orderAddError: "Error adding order", orderUpdateError: "Error updating order",
    orderDeleteError: "Error deleting order", dataLoadError: "Error loading data",
    smsError: "Order updated but SMS failed", smsSent: "SMS sent to customer", smsErrorMsg: "SMS error",
    orderExistsError: "This order number already exists",
    // FAQ
    faqPage: "FAQ - FAQ Page", faqProduct: "FAQ - Product Page",
    // Profile nav
    main: "Main", orders: "Orders", favorites: "Favorites", tracking: "Tracking",
    more: "More", myMessages: "My Messages", moreOptions: "More options",
    contactMessages: "Contact messages", sendMessages: "Send messages", smsMessages: "SMS messages",
    blogManagement: "Blog management", sliderManagementNav: "Slider management", promoBanner: "Promo banner",
    faq: "FAQ", repairManagement: "Repair management", userManagement: "User management",
    menuSettings: "Menu settings", searchSettings: "Search settings", botSettings: "Bot settings",
    colorHelper: "Help - Colors", accessibilityReport: "Accessibility report",
    communication: "Communication", contentManagement: "Content management", operations: "Operations",
    settingsAndTools: "Settings & tools", backToSite: "Back to site", disconnect: "Disconnect",
    hello: "Hello",
    // Repair statuses
    statusPending: "Pending", statusInRepair: "In repair", statusCompleted: "Completed",
    statusWaitingInStore: "Waiting in store", statusShipped: "Shipped",
    statusDeliveredLabel: "Delivered", statusCancelled: "Cancelled",
    addRepair: "Add order",
  },
  ru: {
    manageTags: "Управление тегами", newPost: "Новый пост", backToList: "Назад к списку",
    newTagPlaceholder: "Название нового тега...", addTag: "Добавить", noPostsYet: "Пока нет постов. Создайте первый пост!",
    published: "Опубликовано", draft: "Черновик", deletePost: "Удалить пост?", hide: "Скрыть", publish: "Опубликовать",
    tagCreated: "Тег создан", tagDeleted: "Тег удалён", postDeleted: "Пост удалён", statusUpdated: "Статус обновлён",
    deleteTag: "Удалить тег", confirmDeleteTag: "Удалить тег",
    sliderManagement: "Управление слайдером", addSlide: "Добавить слайд", saveChanges: "Сохранить",
    slideNumber: "Слайд", active: "Активен", image: "Изображение", uploadImage: "Загрузить изображение",
    title: "Заголовок", titlePlaceholder: "Заголовок слайда", subtitle: "Подзаголовок", subtitlePlaceholder: "Подзаголовок",
    buttonText: "Текст кнопки", buttonTextPlaceholder: "Напр.: Смотреть коллекцию",
    linkType: "Тип ссылки", category: "Категория", externalLink: "Внешняя ссылка", internalPage: "Внутренняя страница",
    selectCategory: "Выберите категорию", enterUrl: "URL", selectPage: "Выберите страницу",
    noSlides: "Нет слайдов. Добавьте первый слайд.",
    slidesSaved: "Слайды сохранены", slideSaveError: "Ошибка сохранения", newSlideAdded: "Добавлен новый слайд",
    slideDeleted: "Слайд удалён", imageUploaded: "Изображение загружено", imageUploadError: "Ошибка загрузки",
    sliderLoadError: "Ошибка загрузки данных",
    smsBalance: "Баланс SMS", messages: "сообщений",
    searchPlaceholder: "Поиск по номеру, имени, телефону...",
    addOrder: "Добавить заказ/ремонт", newOrder: "Новый заказ/ремонт", editOrder: "Редактировать заказ/ремонт",
    orderNumber: "Номер заказа", rescan: "Сканировать заново", orderExists: "Этот номер заказа уже существует.",
    guestCustomer: "Гость", registeredCustomer: "Зарегистрированный",
    guestPhone: "Телефон гостя *", guestPhoneNote: "Клиент сможет отслеживать заказ по номеру телефона с OTP",
    searchCustomer: "Поиск клиента", searchCustomerPlaceholder: "Поиск по имени, телефону или email...",
    noCustomersFound: "Клиенты не найдены", titleField: "Название *", description: "Описание",
    status: "Статус", cancel: "Отмена", save: "Сохранить",
    noOrdersFound: "Заказы не найдены", noOrders: "Нет заказов/ремонтов",
    guest: "Гость", phone: "Телефон", email: "Email", customer: "Клиент",
    created: "Создан", updated: "Обновлён", markDelivered: "Отметить как доставлен",
    alreadyDelivered: "Заказ уже отмечен как доставлен", statusDelivered: "Статус изменён на доставлен",
    fillRequired: "Заполните все обязательные поля", orderAdded: "Заказ добавлен",
    orderUpdated: "Заказ обновлён", orderDeleted: "Заказ удалён",
    deleteOrder: "Удалить этот заказ?", orderAddError: "Ошибка добавления", orderUpdateError: "Ошибка обновления",
    orderDeleteError: "Ошибка удаления", dataLoadError: "Ошибка загрузки данных",
    smsError: "Заказ обновлён, но SMS не отправлено", smsSent: "SMS отправлено клиенту", smsErrorMsg: "Ошибка SMS",
    orderExistsError: "Этот номер заказа уже существует",
    faqPage: "FAQ - страница FAQ", faqProduct: "FAQ - страница товара",
    main: "Главная", orders: "Заказы", favorites: "Избранное", tracking: "Отслеживание",
    more: "Ещё", myMessages: "Мои сообщения", moreOptions: "Дополнительно",
    contactMessages: "Сообщения контакта", sendMessages: "Отправка сообщений", smsMessages: "SMS сообщения",
    blogManagement: "Управление блогом", sliderManagementNav: "Управление слайдером", promoBanner: "Промо баннер",
    faq: "FAQ", repairManagement: "Управление ремонтами", userManagement: "Управление пользователями",
    menuSettings: "Настройки меню", searchSettings: "Настройки поиска", botSettings: "Настройки бота",
    colorHelper: "Помощь - цвета", accessibilityReport: "Отчёт о доступности",
    communication: "Коммуникация", contentManagement: "Управление контентом", operations: "Операции",
    settingsAndTools: "Настройки и инструменты", backToSite: "Вернуться на сайт", disconnect: "Выйти",
    hello: "Здравствуйте",
    statusPending: "Ожидает", statusInRepair: "В ремонте", statusCompleted: "Завершён",
    statusWaitingInStore: "Ожидает в магазине", statusShipped: "Отправлен",
    statusDeliveredLabel: "Доставлен", statusCancelled: "Отменён",
    addRepair: "Добавить заказ",
  },
  ar: {
    manageTags: "إدارة الوسوم", newPost: "منشور جديد", backToList: "العودة للقائمة",
    newTagPlaceholder: "اسم وسم جديد...", addTag: "إضافة", noPostsYet: "لا توجد منشورات بعد. أنشئ أول منشور!",
    published: "منشور", draft: "مسودة", deletePost: "حذف المنشور؟", hide: "إخفاء", publish: "نشر",
    tagCreated: "تم إنشاء الوسم", tagDeleted: "تم حذف الوسم", postDeleted: "تم حذف المنشور", statusUpdated: "تم تحديث الحالة",
    deleteTag: "حذف الوسم", confirmDeleteTag: "حذف الوسم",
    sliderManagement: "إدارة الشرائح", addSlide: "إضافة شريحة", saveChanges: "حفظ التغييرات",
    slideNumber: "شريحة", active: "نشط", image: "صورة", uploadImage: "رفع صورة",
    title: "العنوان", titlePlaceholder: "عنوان الشريحة", subtitle: "العنوان الفرعي", subtitlePlaceholder: "العنوان الفرعي",
    buttonText: "نص الزر", buttonTextPlaceholder: "مثال: عرض المجموعة",
    linkType: "نوع الرابط", category: "فئة", externalLink: "رابط خارجي", internalPage: "صفحة داخلية",
    selectCategory: "اختر فئة", enterUrl: "عنوان URL", selectPage: "اختر صفحة",
    noSlides: "لا توجد شرائح. أضف أول شريحة للبدء.",
    slidesSaved: "تم حفظ الشرائح", slideSaveError: "خطأ في الحفظ", newSlideAdded: "تمت إضافة شريحة جديدة",
    slideDeleted: "تم حذف الشريحة", imageUploaded: "تم رفع الصورة", imageUploadError: "خطأ في رفع الصورة",
    sliderLoadError: "خطأ في تحميل البيانات",
    smsBalance: "رصيد SMS", messages: "رسائل",
    searchPlaceholder: "بحث برقم الطلب، الاسم، الهاتف...",
    addOrder: "إضافة طلب/إصلاح", newOrder: "طلب/إصلاح جديد", editOrder: "تعديل الطلب/الإصلاح",
    orderNumber: "رقم الطلب", rescan: "إعادة المسح", orderExists: "رقم الطلب هذا موجود بالفعل.",
    guestCustomer: "عميل زائر", registeredCustomer: "عميل مسجل",
    guestPhone: "رقم هاتف الزائر *", guestPhoneNote: "يمكن للعميل تتبع الطلب عبر رقم الهاتف باستخدام رمز OTP",
    searchCustomer: "بحث عن عميل", searchCustomerPlaceholder: "بحث بالاسم أو الهاتف أو البريد...",
    noCustomersFound: "لم يتم العثور على عملاء", titleField: "العنوان *", description: "الوصف",
    status: "الحالة", cancel: "إلغاء", save: "حفظ",
    noOrdersFound: "لم يتم العثور على طلبات", noOrders: "لا توجد طلبات/إصلاحات حالياً",
    guest: "زائر", phone: "هاتف", email: "بريد إلكتروني", customer: "عميل",
    created: "أُنشئ", updated: "حُدّث", markDelivered: "تحديد كمُسلَّم",
    alreadyDelivered: "الطلب مُحدد كمُسلَّم بالفعل", statusDelivered: "تم تغيير الحالة إلى مُسلَّم",
    fillRequired: "يرجى ملء جميع الحقول المطلوبة", orderAdded: "تمت إضافة الطلب",
    orderUpdated: "تم تحديث الطلب", orderDeleted: "تم حذف الطلب",
    deleteOrder: "حذف هذا الطلب؟", orderAddError: "خطأ في الإضافة", orderUpdateError: "خطأ في التحديث",
    orderDeleteError: "خطأ في الحذف", dataLoadError: "خطأ في تحميل البيانات",
    smsError: "تم تحديث الطلب لكن فشل إرسال SMS", smsSent: "تم إرسال SMS للعميل", smsErrorMsg: "خطأ SMS",
    orderExistsError: "رقم الطلب هذا موجود بالفعل",
    faqPage: "الأسئلة الشائعة - صفحة FAQ", faqProduct: "الأسئلة الشائعة - صفحة المنتج",
    main: "الرئيسية", orders: "الطلبات", favorites: "المفضلة", tracking: "التتبع",
    more: "المزيد", myMessages: "رسائلي", moreOptions: "خيارات إضافية",
    contactMessages: "رسائل التواصل", sendMessages: "إرسال رسائل", smsMessages: "رسائل SMS",
    blogManagement: "إدارة المدونة", sliderManagementNav: "إدارة الشرائح", promoBanner: "شريط العروض",
    faq: "الأسئلة الشائعة", repairManagement: "إدارة الإصلاحات", userManagement: "إدارة المستخدمين",
    menuSettings: "إعدادات القائمة", searchSettings: "إعدادات البحث", botSettings: "إعدادات البوت",
    colorHelper: "مساعدة - الألوان", accessibilityReport: "تقرير إمكانية الوصول",
    communication: "التواصل", contentManagement: "إدارة المحتوى", operations: "العمليات",
    settingsAndTools: "الإعدادات والأدوات", backToSite: "العودة للموقع", disconnect: "قطع الاتصال",
    hello: "مرحباً",
    statusPending: "قيد الانتظار", statusInRepair: "قيد الإصلاح", statusCompleted: "مكتمل",
    statusWaitingInStore: "في انتظار بالمتجر", statusShipped: "تم الشحن",
    statusDeliveredLabel: "تم التسليم", statusCancelled: "ملغي",
    addRepair: "إضافة طلب",
  },
};

// ─── Variant Quick Picker ─────────────────────────────────────────
const variantPicker = {
  en: {
    variantUnavailable: "Variant unavailable", noEngraving: "Without engraving",
    productDescription: "Product Description", outOfStock: "Out of Stock", addToCart: "Add to Cart",
    engravingError: "Please fill in engraving details", engravingErrorDesc: "Select a font and enter engraving text",
  },
  ru: {
    variantUnavailable: "Вариант недоступен", noEngraving: "Без гравировки",
    productDescription: "Описание товара", outOfStock: "Нет в наличии", addToCart: "В корзину",
    engravingError: "Заполните данные гравировки", engravingErrorDesc: "Выберите шрифт и введите текст",
  },
  ar: {
    variantUnavailable: "الخيار غير متاح", noEngraving: "بدون نقش",
    productDescription: "وصف المنتج", outOfStock: "نفذ من المخزون", addToCart: "أضف إلى السلة",
    engravingError: "يرجى ملء تفاصيل النقش", engravingErrorDesc: "اختر خطاً وأدخل نص النقش",
  },
};

// ─── Alert Dialog / Confirm ─────────────────────────────────────────
const confirmDialog = {
  en: {
    deleteMessage: "Delete this message?", deleteMessageDesc: "This action cannot be undone. The message will be permanently deleted.",
    cancel: "Cancel", delete: "Delete",
    deleteCategory: "Delete Category", deleteCategoryDesc: "Are you sure you want to delete this category? This will also delete all questions in it.",
    deleteQuestion: "Delete Question", deleteQuestionDesc: "Are you sure you want to delete this question?",
  },
  ru: {
    deleteMessage: "Удалить это сообщение?", deleteMessageDesc: "Это действие нельзя отменить. Сообщение будет удалено навсегда.",
    cancel: "Отмена", delete: "Удалить",
    deleteCategory: "Удалить категорию", deleteCategoryDesc: "Вы уверены, что хотите удалить эту категорию? Все вопросы в ней также будут удалены.",
    deleteQuestion: "Удалить вопрос", deleteQuestionDesc: "Вы уверены, что хотите удалить этот вопрос?",
  },
  ar: {
    deleteMessage: "حذف هذه الرسالة؟", deleteMessageDesc: "لا يمكن التراجع عن هذا الإجراء. سيتم حذف الرسالة نهائياً.",
    cancel: "إلغاء", delete: "حذف",
    deleteCategory: "حذف الفئة", deleteCategoryDesc: "هل أنت متأكد من حذف هذه الفئة؟ سيتم حذف جميع الأسئلة فيها أيضاً.",
    deleteQuestion: "حذف السؤال", deleteQuestionDesc: "هل أنت متأكد من حذف هذا السؤال؟",
  },
};

// ─── Admin Menu Settings ─────────────────────────────────────────
const adminMenu = {
  en: {
    menuSettings: "Menu Settings", saveSettings: "Save Settings", searchShopify: "Search category in Shopify...",
    search: "Search", searchResults: "Search results", mainMenu: "Main Menu", featuredCategories: "Featured Categories",
    categoryFilter: "Category Filter", sections: "Sections", emptyCart: "Empty Cart",
    megaMenu: "Main Menu - Mega Menu", megaMenuDesc: "Create headings with sub-headings and categories. Drag to reorder.",
    addHeading: "Add Heading", noHeadingsYet: "No headings defined yet", clickAddHeading: "Click \"Add Heading\" to start",
    headingPlaceholder: "Heading name (e.g.: Necklaces)", megaMenuImage: "Mega menu image:",
    uploadImage: "Upload Image", createWithAI: "Create with AI", describeImage: "Describe the image you want to create...",
    createImage: "Create Image", megaMenuImageDesc: "The image will appear on the left side of the mega menu when hovering over the category",
    directLink: "Direct link to category (no dropdown):", noDirectLink: "No direct link (use sub-headings)",
    directLinkDesc: "If a direct link is selected, clicking the heading will navigate directly to the category without a dropdown",
    subHeadingPlaceholder: "Sub-heading name (e.g.: Men's Necklaces)", addSubHeading: "Add Sub-heading",
    selectCategories: "Select categories:", searchCategory: "Search category...", foundCategories: "categories found",
    noMatchingCategories: "No matching categories found", selectedCount: "selected", categories: "categories",
    featuredTitle: "Featured Categories on Homepage", featuredDesc: "Choose which categories to display in the \"Featured Categories\" section on the homepage",
    clearAll: "Clear All", selectedFeatured: "featured categories selected out of",
    filterTitle: "Filtering on Category Page", filterDesc: "Choose which categories to display in the filter menu on the products page",
    selectAll: "Select All", selectedFilter: "selected out of",
    homepageSectionsTitle: "Homepage Sections", homepageSectionsDesc: "Create product sections by category. Each section will display 4 products from the selected category.",
    addSection: "Add Section", noSectionsYet: "No sections defined yet", clickAddSection: "Click \"Add Section\" to start",
    sectionPlaceholder: "Section name (e.g.: Engagement Rings)", selectCategory: "Select category",
    sectionsCount: "sections defined", categoriesAvailable: "categories available",
    emptyCartTitle: "Empty Cart Categories", emptyCartDesc: "Choose which categories to display when the shopping cart is empty. These buttons will help customers start shopping.",
    selectedEmptyCart: "empty cart categories selected out of",
    imageUploaded: "Image uploaded successfully", imageUploadError: "Error uploading image",
    enterImageDesc: "Please enter an image description", imageCreated: "Image created successfully", imageCreateError: "Error creating image",
    titleLink: "Title link to categories (hover still opens mega menu):", noTitleLink: "No title link",
    titleLinkDesc: "Clicking the title will navigate to all products with auto-filter on selected categories, hovering will still open the mega menu",
    settingsSaved: "Settings saved successfully", settingsSaveError: "Error saving settings",
    loadError: "Error loading settings", loadMoreError: "Error loading more categories",
    searchError: "Error searching categories", loadingPublic: "Loading public categories (Admin API unavailable)",
  },
  ru: {
    menuSettings: "Настройки меню", saveSettings: "Сохранить настройки", searchShopify: "Поиск категории в Shopify...",
    search: "Поиск", searchResults: "Результаты поиска", mainMenu: "Главное меню", featuredCategories: "Избранные категории",
    categoryFilter: "Фильтр категорий", sections: "Секции", emptyCart: "Пустая корзина",
    megaMenu: "Главное меню - Mega Menu", megaMenuDesc: "Создавайте заголовки с подзаголовками и категориями. Перетащите для изменения порядка.",
    addHeading: "Добавить заголовок", noHeadingsYet: "Заголовки ещё не определены", clickAddHeading: "Нажмите \"Добавить заголовок\" чтобы начать",
    headingPlaceholder: "Название заголовка (пример: Колье)", megaMenuImage: "Изображение для мега-меню:",
    uploadImage: "Загрузить изображение", createWithAI: "Создать с AI", describeImage: "Опишите изображение, которое хотите создать...",
    createImage: "Создать изображение", megaMenuImageDesc: "Изображение будет отображаться слева в мега-меню при наведении на категорию",
    directLink: "Прямая ссылка на категорию (без выпадающего меню):", noDirectLink: "Без прямой ссылки (использовать подзаголовки)",
    directLinkDesc: "Если выбрана прямая ссылка, нажатие на заголовок перенаправит прямо на категорию без выпадающего меню",
    subHeadingPlaceholder: "Название подзаголовка (пример: Мужские колье)", addSubHeading: "Добавить подзаголовок",
    selectCategories: "Выберите категории:", searchCategory: "Поиск категории...", foundCategories: "категорий найдено",
    noMatchingCategories: "Подходящие категории не найдены", selectedCount: "выбрано", categories: "категорий",
    featuredTitle: "Избранные категории на главной", featuredDesc: "Выберите категории для секции \"Избранные категории\" на главной странице",
    clearAll: "Очистить всё", selectedFeatured: "избранных категорий выбрано из",
    filterTitle: "Фильтрация на странице категории", filterDesc: "Выберите категории для меню фильтрации на странице товаров",
    selectAll: "Выбрать всё", selectedFilter: "выбрано из",
    homepageSectionsTitle: "Секции главной страницы", homepageSectionsDesc: "Создайте секции товаров по категориям. Каждая секция покажет 4 товара из выбранной категории.",
    addSection: "Добавить секцию", noSectionsYet: "Секции ещё не определены", clickAddSection: "Нажмите \"Добавить секцию\" чтобы начать",
    sectionPlaceholder: "Название секции (пример: Обручальные кольца)", selectCategory: "Выберите категорию",
    sectionsCount: "секций определено", categoriesAvailable: "категорий доступно",
    emptyCartTitle: "Категории в пустой корзине", emptyCartDesc: "Выберите категории для отображения когда корзина пуста. Эти кнопки помогут покупателям начать покупки.",
    selectedEmptyCart: "категорий пустой корзины выбрано из",
    imageUploaded: "Изображение загружено", imageUploadError: "Ошибка загрузки изображения",
    enterImageDesc: "Введите описание изображения", imageCreated: "Изображение создано", imageCreateError: "Ошибка создания изображения",
    settingsSaved: "Настройки сохранены", settingsSaveError: "Ошибка сохранения настроек",
    loadError: "Ошибка загрузки настроек", loadMoreError: "Ошибка загрузки категорий",
    titleLink: "Ссылка заголовка на категории (hover всё ещё открывает мега-меню):", noTitleLink: "Без ссылки заголовка",
    titleLinkDesc: "Нажатие на заголовок перенаправит на все товары с авто-фильтром по выбранным категориям, наведение всё ещё откроет мега-меню",
    searchError: "Ошибка поиска категорий", loadingPublic: "Загрузка публичных категорий (Admin API недоступен)",
  },
  ar: {
    menuSettings: "إعدادات القائمة", saveSettings: "حفظ الإعدادات", searchShopify: "ابحث عن فئة في Shopify...",
    search: "بحث", searchResults: "نتائج البحث", mainMenu: "القائمة الرئيسية", featuredCategories: "الفئات المميزة",
    categoryFilter: "تصفية الفئات", sections: "الأقسام", emptyCart: "السلة فارغة",
    megaMenu: "القائمة الرئيسية - Mega Menu", megaMenuDesc: "أنشئ عناوين مع عناوين فرعية وفئات. اسحب لإعادة الترتيب.",
    addHeading: "إضافة عنوان", noHeadingsYet: "لم يتم تعريف عناوين بعد", clickAddHeading: "انقر على \"إضافة عنوان\" للبدء",
    headingPlaceholder: "اسم العنوان (مثال: قلادات)", megaMenuImage: "صورة القائمة الضخمة:",
    uploadImage: "تحميل صورة", createWithAI: "إنشاء بالذكاء الاصطناعي", describeImage: "صِف الصورة التي تريد إنشاءها...",
    createImage: "إنشاء صورة", megaMenuImageDesc: "ستظهر الصورة على الجانب الأيسر من القائمة الضخمة عند التمرير فوق الفئة",
    directLink: "رابط مباشر للفئة (بدون قائمة منسدلة):", noDirectLink: "بدون رابط مباشر (استخدم عناوين فرعية)",
    directLinkDesc: "إذا تم اختيار رابط مباشر، سيؤدي النقر على العنوان إلى الانتقال مباشرة للفئة بدون قائمة منسدلة",
    subHeadingPlaceholder: "اسم العنوان الفرعي (مثال: قلادات رجالية)", addSubHeading: "إضافة عنوان فرعي",
    selectCategories: "اختر الفئات:", searchCategory: "ابحث عن فئة...", foundCategories: "فئات تم العثور عليها",
    noMatchingCategories: "لم يتم العثور على فئات مطابقة", selectedCount: "محدد", categories: "فئات",
    featuredTitle: "الفئات المميزة في الصفحة الرئيسية", featuredDesc: "اختر الفئات التي ستظهر في قسم \"الفئات المميزة\" في الصفحة الرئيسية",
    clearAll: "مسح الكل", selectedFeatured: "فئات مميزة محددة من",
    filterTitle: "التصفية في صفحة الفئة", filterDesc: "اختر الفئات التي ستظهر في قائمة التصفية في صفحة المنتجات",
    selectAll: "تحديد الكل", selectedFilter: "محدد من",
    homepageSectionsTitle: "أقسام الصفحة الرئيسية", homepageSectionsDesc: "أنشئ أقسام منتجات حسب الفئة. كل قسم سيعرض 4 منتجات من الفئة المختارة.",
    addSection: "إضافة قسم", noSectionsYet: "لم يتم تعريف أقسام بعد", clickAddSection: "انقر على \"إضافة قسم\" للبدء",
    sectionPlaceholder: "اسم القسم (مثال: خواتم خطوبة)", selectCategory: "اختر فئة",
    sectionsCount: "أقسام محددة", categoriesAvailable: "فئات متاحة",
    emptyCartTitle: "فئات السلة الفارغة", emptyCartDesc: "اختر الفئات التي ستظهر عندما تكون سلة التسوق فارغة. ستساعد هذه الأزرار العملاء على بدء التسوق.",
    selectedEmptyCart: "فئات سلة فارغة محددة من",
    imageUploaded: "تم تحميل الصورة بنجاح", imageUploadError: "خطأ في تحميل الصورة",
    enterImageDesc: "يرجى إدخال وصف الصورة", imageCreated: "تم إنشاء الصورة بنجاح", imageCreateError: "خطأ في إنشاء الصورة",
    settingsSaved: "تم حفظ الإعدادات بنجاح", settingsSaveError: "خطأ في حفظ الإعدادات",
    loadError: "خطأ في تحميل الإعدادات", loadMoreError: "خطأ في تحميل المزيد من الفئات",
    titleLink: "رابط العنوان للفئات (التمرير يفتح القائمة الضخمة):", noTitleLink: "بدون رابط عنوان",
    titleLinkDesc: "النقر على العنوان سينتقل إلى جميع المنتجات مع تصفية تلقائية على الفئات المختارة، التمرير سيظل يفتح القائمة الضخمة",
    searchError: "خطأ في البحث عن الفئات", loadingPublic: "تحميل الفئات العامة (Admin API غير متاح)",
  },
};

// ─── Admin User Management ─────────────────────────────────────────
const adminUsers = {
  en: {
    userManagement: "User Management", users: "users", searchPlaceholder: "Search by name or phone...",
    noUsersFound: "No users found", noName: "No name", admin: "Admin", disabled: "Disabled",
    joined: "Joined:", editDetails: "Edit details", removeAdmin: "Remove admin permissions", makeAdmin: "Make admin",
    active: "Active", editUser: "Edit User Details", editUserDesc: "Edit user details",
    firstName: "First Name", lastName: "Last Name", phone: "Phone", cancel: "Cancel", save: "Save",
    userActivated: "User activated", userDeactivated: "User deactivated", statusError: "Error updating user status",
    madeAdmin: "User is now an admin", adminRemoved: "Admin permissions removed", roleError: "Error updating user permissions",
    userUpdated: "User details updated", saveError: "Error saving changes", loadError: "Error loading users",
  },
  ru: {
    userManagement: "Управление пользователями", users: "пользователей", searchPlaceholder: "Поиск по имени или телефону...",
    noUsersFound: "Пользователи не найдены", noName: "Без имени", admin: "Админ", disabled: "Отключён",
    joined: "Присоединился:", editDetails: "Редактировать", removeAdmin: "Убрать права админа", makeAdmin: "Сделать админом",
    active: "Активен", editUser: "Редактирование пользователя", editUserDesc: "Редактируйте данные пользователя",
    firstName: "Имя", lastName: "Фамилия", phone: "Телефон", cancel: "Отмена", save: "Сохранить",
    userActivated: "Пользователь активирован", userDeactivated: "Пользователь отключён", statusError: "Ошибка обновления статуса",
    madeAdmin: "Пользователь стал админом", adminRemoved: "Права админа убраны", roleError: "Ошибка обновления прав",
    userUpdated: "Данные обновлены", saveError: "Ошибка сохранения", loadError: "Ошибка загрузки пользователей",
  },
  ar: {
    userManagement: "إدارة المستخدمين", users: "مستخدمين", searchPlaceholder: "ابحث بالاسم أو الهاتف...",
    noUsersFound: "لم يتم العثور على مستخدمين", noName: "بدون اسم", admin: "مشرف", disabled: "معطّل",
    joined: "انضم:", editDetails: "تعديل التفاصيل", removeAdmin: "إزالة صلاحيات المشرف", makeAdmin: "جعله مشرفاً",
    active: "نشط", editUser: "تعديل بيانات المستخدم", editUserDesc: "تعديل بيانات المستخدم",
    firstName: "الاسم الأول", lastName: "اسم العائلة", phone: "هاتف", cancel: "إلغاء", save: "حفظ",
    userActivated: "تم تفعيل المستخدم", userDeactivated: "تم تعطيل المستخدم", statusError: "خطأ في تحديث الحالة",
    madeAdmin: "أصبح المستخدم مشرفاً", adminRemoved: "تمت إزالة صلاحيات المشرف", roleError: "خطأ في تحديث الصلاحيات",
    userUpdated: "تم تحديث البيانات", saveError: "خطأ في الحفظ", loadError: "خطأ في تحميل المستخدمين",
  },
};

// ─── Admin Color Helper ─────────────────────────────────────────
const adminColor = {
  en: {
    variantInfo: "Variant Information", variantInfoDesc: "Mapping of colors, fonts, and tags for use in orders and products",
    colors: "Colors", fonts: "Fonts", icons: "Icons", tags: "Tags", variants: "Variants",
    copied: "Copied", copy: "Copy", copyError: "Error copying",
    fontsDesc: "Below is the list of engraving fonts. When a customer selects a font, the font ID is sent to the Shopify order.",
    howItWorks: "How does it work?",
    fontHowTo1: "When a customer selects a font for engraving, the ID (e.g.: font-a) is sent as part of the order",
    fontHowTo2: "The Shopify order will contain the \"Font Type\" field with the font ID",
    fontHowTo3: "Use the table above to identify which font the customer selected",
    iconsDesc: "Below is the list of available icons for engraving. Products with the \"אייקון\" tag will show an icon selection option.",
    iconHowTo1: "Add the tag \"אייקון\" to the product in Shopify",
    iconHowTo2: "On the product page, an icon selection option will appear",
    iconHowTo3: "The icon the customer selected will appear in the order details with its ID",
    tagsDesc: "Below is the list of tags that can be added to products in Shopify to enable various features on the site.",
    engravingTags: "Engraving Tags",
    howToAddTag: "How to add a tag?",
    tagHowTo1: "Go to the product editor in Shopify", tagHowTo2: "Scroll to the \"Tags\" area",
    tagHowTo3: "Add the desired tag exactly as shown above", tagHowTo4: "For example: adding \"חריטה 2\" will show 2 engraving fields on the product page",
    wordsVariant: "\"Words\" Variant - Pricing by Word Count",
    wordsVariantDesc: "This variant allows setting different prices based on the number of words the customer enters for engraving. The system automatically selects the appropriate variant by word count.",
    noEngraving: "No engraving - the base price of the product", oneWord: "One word - price for single word engraving",
    twoWords: "Two words - price for two-word engraving", threeWords: "Three words - price for three-word engraving",
    howToSetup: "How to set up in Shopify?",
    setupStep1: "Go to the product editor in Shopify", setupStep2: "Scroll to the \"Variants\" area",
    setupStep3: "Add a new option named", setupStep4: "Add the values:", 
    setupStep5: "Set a different price for each variant (e.g.: 0 = ₪100, 1 = ₪120, 2 = ₪140, 3 = ₪160)",
    setupStep6: "Save the product",
    quickLinks: "Quick Links to Shopify Management", productManagement: "Product Management",
    addNewProduct: "Add New Product", collectionManagement: "Collection Management", orderManagement: "Order Management",
    attention: "Attention!",
    attentionDesc: "The system automatically selects the variant based on the number of words the customer types in the engraving field. If there's no \"Words\" variant, the price stays fixed regardless of word count.",
    usageInstructions: "Usage Instructions",
    usage1Title: "1. Adding a color to a product in Shopify",
    usage1Desc: "When adding a variant to a product, use the color names from the list above. The system will automatically recognize the color and display the correct circle.",
    usage2Title: "2. Supported color names",
    usage2Desc: "Make sure you use the exact color names from the list. For example: \"Gold 14K\" not \"14 Karat Gold\".",
    usage3Title: "3. Engraving in orders",
    usage3Desc: "When a customer orders a product with engraving, the order will contain the engraving text and the font ID. Use the \"Engraving Fonts\" tab to identify the correct font.",
    usage4Title: "4. Variant images",
    usage4Desc: "It's recommended to upload a specific image for each color variant in Shopify. The image will also be displayed in the shopping cart when the customer selects the specific color.",
  },
  ru: {
    variantInfo: "Информация о вариантах", variantInfoDesc: "Маппинг цветов, шрифтов и тегов для использования в заказах и товарах",
    colors: "Цвета", fonts: "Шрифты", icons: "Иконки", tags: "Теги", variants: "Варианты",
    copied: "Скопировано", copy: "Копировать", copyError: "Ошибка копирования",
    fontsDesc: "Ниже список шрифтов для гравировки. Когда клиент выбирает шрифт, ID шрифта отправляется в заказ Shopify.",
    howItWorks: "Как это работает?",
    fontHowTo1: "Когда клиент выбирает шрифт для гравировки, ID (например: font-a) отправляется как часть заказа",
    fontHowTo2: "Заказ в Shopify будет содержать поле \"Тип шрифта\" с ID шрифта",
    fontHowTo3: "Используйте таблицу выше чтобы определить какой шрифт выбрал клиент",
    iconsDesc: "Ниже список доступных иконок для гравировки. Для товаров с тегом \"אייקון\" отображается выбор иконки.",
    iconHowTo1: "Добавьте тег \"אייקון\" к товару в Shopify",
    iconHowTo2: "На странице товара появится выбор иконки для гравировки",
    iconHowTo3: "Выбранная клиентом иконка появится в деталях заказа с её ID",
    tagsDesc: "Ниже список тегов для добавления к товарам в Shopify для активации различных функций на сайте.",
    engravingTags: "Теги гравировки",
    howToAddTag: "Как добавить тег?",
    tagHowTo1: "Перейдите в редактор товара в Shopify", tagHowTo2: "Прокрутите до области \"Tags\" (теги)",
    tagHowTo3: "Добавьте нужный тег точно как показано выше", tagHowTo4: "Например: добавление \"חריטה 2\" покажет 2 поля гравировки на странице товара",
    wordsVariant: "Вариант \"Слова\" - Ценообразование по количеству слов",
    wordsVariantDesc: "Этот вариант позволяет устанавливать разные цены в зависимости от количества слов для гравировки. Система автоматически выбирает подходящий вариант.",
    noEngraving: "Без гравировки - базовая цена товара", oneWord: "Одно слово - цена за гравировку одного слова",
    twoWords: "Два слова - цена за гравировку двух слов", threeWords: "Три слова - цена за гравировку трёх слов",
    howToSetup: "Как настроить в Shopify?",
    setupStep1: "Перейдите в редактор товара в Shopify", setupStep2: "Прокрутите до области \"Variants\" (варианты)",
    setupStep3: "Добавьте новую опцию с именем", setupStep4: "Добавьте значения:",
    setupStep5: "Установите разную цену для каждого варианта (пример: 0 = ₪100, 1 = ₪120, 2 = ₪140, 3 = ₪160)",
    setupStep6: "Сохраните товар",
    quickLinks: "Быстрые ссылки на управление в Shopify", productManagement: "Управление товарами",
    addNewProduct: "Добавить новый товар", collectionManagement: "Управление коллекциями", orderManagement: "Управление заказами",
    attention: "Внимание!",
    attentionDesc: "Система автоматически выбирает вариант по количеству слов, которые клиент вводит в поле гравировки. Если варианта \"Слова\" нет, цена остаётся фиксированной.",
    usageInstructions: "Инструкция по использованию",
    usage1Title: "1. Добавление цвета к товару в Shopify",
    usage1Desc: "При добавлении варианта используйте названия цветов из списка выше. Система автоматически распознает цвет.",
    usage2Title: "2. Поддерживаемые названия цветов",
    usage2Desc: "Убедитесь что используете точные названия из списка. Например: \"Gold 14K\" а не \"Золото 14 карат\".",
    usage3Title: "3. Гравировка в заказах",
    usage3Desc: "Когда клиент заказывает товар с гравировкой, заказ содержит текст и ID шрифта. Используйте вкладку \"Шрифты\" для идентификации.",
    usage4Title: "4. Изображения вариантов",
    usage4Desc: "Рекомендуется загрузить отдельное изображение для каждого цветового варианта. Оно также отобразится в корзине.",
  },
  ar: {
    variantInfo: "معلومات المتغيرات", variantInfoDesc: "خريطة الألوان والخطوط والعلامات للاستخدام في الطلبات والمنتجات",
    colors: "ألوان", fonts: "خطوط", icons: "أيقونات", tags: "علامات", variants: "متغيرات",
    copied: "تم النسخ", copy: "نسخ", copyError: "خطأ في النسخ",
    fontsDesc: "فيما يلي قائمة خطوط النقش. عندما يختار العميل خطاً، يتم إرسال معرّف الخط إلى طلب Shopify.",
    howItWorks: "كيف يعمل؟",
    fontHowTo1: "عندما يختار العميل خطاً للنقش، يتم إرسال المعرّف (مثل: font-a) كجزء من الطلب",
    fontHowTo2: "سيحتوي طلب Shopify على حقل \"نوع الخط\" مع معرّف الخط",
    fontHowTo3: "استخدم الجدول أعلاه لتحديد الخط الذي اختاره العميل",
    iconsDesc: "فيما يلي قائمة الأيقونات المتاحة للنقش. المنتجات التي تحمل علامة \"אייקון\" ستعرض خيار اختيار أيقونة.",
    iconHowTo1: "أضف علامة \"אייקון\" للمنتج في Shopify",
    iconHowTo2: "في صفحة المنتج سيظهر خيار اختيار أيقونة للنقش",
    iconHowTo3: "ستظهر الأيقونة التي اختارها العميل في تفاصيل الطلب مع معرّفها",
    tagsDesc: "فيما يلي قائمة العلامات التي يمكن إضافتها للمنتجات في Shopify لتفعيل ميزات مختلفة في الموقع.",
    engravingTags: "علامات النقش",
    howToAddTag: "كيف تضيف علامة؟",
    tagHowTo1: "ادخل إلى محرر المنتج في Shopify", tagHowTo2: "انتقل إلى منطقة \"Tags\" (العلامات)",
    tagHowTo3: "أضف العلامة المطلوبة تماماً كما تظهر أعلاه", tagHowTo4: "مثال: إضافة \"חריטה 2\" ستعرض حقلين للنقش في صفحة المنتج",
    wordsVariant: "متغير \"كلمات\" - التسعير حسب عدد الكلمات",
    wordsVariantDesc: "يتيح هذا المتغير تحديد أسعار مختلفة بناءً على عدد الكلمات التي يدخلها العميل للنقش. يختار النظام المتغير المناسب تلقائياً.",
    noEngraving: "بدون نقش - السعر الأساسي للمنتج", oneWord: "كلمة واحدة - سعر نقش كلمة واحدة",
    twoWords: "كلمتان - سعر نقش كلمتين", threeWords: "ثلاث كلمات - سعر نقش ثلاث كلمات",
    howToSetup: "كيف تُعد في Shopify؟",
    setupStep1: "ادخل إلى محرر المنتج في Shopify", setupStep2: "انتقل إلى منطقة \"Variants\" (المتغيرات)",
    setupStep3: "أضف خياراً جديداً باسم", setupStep4: "أضف القيم:",
    setupStep5: "حدد سعراً مختلفاً لكل متغير (مثال: 0 = ₪100، 1 = ₪120، 2 = ₪140، 3 = ₪160)",
    setupStep6: "احفظ المنتج",
    quickLinks: "روابط سريعة لإدارة Shopify", productManagement: "إدارة المنتجات",
    addNewProduct: "إضافة منتج جديد", collectionManagement: "إدارة المجموعات", orderManagement: "إدارة الطلبات",
    attention: "انتبه!",
    attentionDesc: "يختار النظام المتغير تلقائياً بناءً على عدد الكلمات في حقل النقش. إذا لم يكن هناك متغير \"كلمات\"، يبقى السعر ثابتاً.",
    usageInstructions: "تعليمات الاستخدام",
    usage1Title: "1. إضافة لون لمنتج في Shopify",
    usage1Desc: "عند إضافة متغير، استخدم أسماء الألوان من القائمة أعلاه. سيتعرف النظام على اللون تلقائياً.",
    usage2Title: "2. أسماء الألوان المدعومة",
    usage2Desc: "تأكد من استخدام أسماء الألوان الدقيقة من القائمة. مثال: \"Gold 14K\" وليس \"ذهب 14 قيراط\".",
    usage3Title: "3. النقش في الطلبات",
    usage3Desc: "عند طلب منتج مع نقش، سيحتوي الطلب على النص ومعرّف الخط. استخدم تبويب \"الخطوط\" للتعرف على الخط.",
    usage4Title: "4. صور المتغيرات",
    usage4Desc: "يُنصح بتحميل صورة مخصصة لكل متغير لون. ستظهر الصورة أيضاً في سلة التسوق عند اختيار اللون.",
  },
};

// ─── Admin Search Settings ──────────────────────────────────
const adminSearch = {
  en: {
    title: "Search Settings",
    description: "Customize the search experience on the site",
    defaultCollections: "Default Collections",
    defaultCollectionsDesc: "Products from these collections will be shown before the user starts typing",
    searchCollection: "Search collection...",
    selectedCount: (selected: number, total: number) => `${selected} collections selected out of ${total}`,
    showCategories: "Show categories in results",
    showCategoriesDesc: "Show matching categories in addition to products",
    saving: "Saving...",
    saveSettings: "Save Settings",
    savedSuccess: "Settings saved successfully",
    loadError: "Error loading settings",
    saveError: "Error saving settings",
  },
  ru: {
    title: "Настройки поиска",
    description: "Настройте поисковый опыт на сайте",
    defaultCollections: "Коллекции по умолчанию",
    defaultCollectionsDesc: "Товары из этих коллекций будут показаны до начала ввода пользователем",
    searchCollection: "Поиск коллекции...",
    selectedCount: (selected: number, total: number) => `Выбрано ${selected} коллекций из ${total}`,
    showCategories: "Показывать категории в результатах",
    showCategoriesDesc: "Показывать подходящие категории помимо товаров",
    saving: "Сохранение...",
    saveSettings: "Сохранить настройки",
    savedSuccess: "Настройки сохранены",
    loadError: "Ошибка загрузки настроек",
    saveError: "Ошибка сохранения настроек",
  },
  ar: {
    title: "إعدادات البحث",
    description: "تخصيص تجربة البحث في الموقع",
    defaultCollections: "مجموعات افتراضية",
    defaultCollectionsDesc: "ستظهر المنتجات من هذه المجموعات قبل أن يبدأ المستخدم بالكتابة",
    searchCollection: "ابحث عن مجموعة...",
    selectedCount: (selected: number, total: number) => `تم اختيار ${selected} مجموعات من ${total}`,
    showCategories: "عرض الفئات في النتائج",
    showCategoriesDesc: "عرض الفئات المطابقة بالإضافة إلى المنتجات",
    saving: "جارٍ الحفظ...",
    saveSettings: "حفظ الإعدادات",
    savedSuccess: "تم حفظ الإعدادات بنجاح",
    loadError: "خطأ في تحميل الإعدادات",
    saveError: "خطأ في حفظ الإعدادات",
  },
};

// ─── Admin Bot Settings ─────────────────────────────────────
const adminBot = {
  en: {
    title: "Jewelry Advisor Settings",
    description: "Configure which collections belong to each target audience and product type. The bot will use this information to recommend suitable products.",
    howItWorks: "How does it work?",
    howItWorksDesc: "When a customer asks for \"bracelet for a woman\", the bot will search in the collections you defined as \"Women\" + \"Bracelets\". This prevents the bot from suggesting children's bracelets instead of adult ones.",
    noCollections: "Could not load collections from Shopify. The API token may have expired or there's a permissions issue.",
    targetAudience: "Target Audience",
    productTypes: "Product Types",
    audienceDesc: "Select which collections are suitable for each target audience. The bot will use this to suggest tailored products.",
    productTypeDesc: "Select which collections contain each product type. The bot will use this for focused search.",
    collectionsSelected: "collections selected",
    searchCollection: "Search collection...",
    noCollectionsFound: "No collections found",
    loadError: "Error loading data",
    updateError: "Error updating",
  },
  ru: {
    title: "Настройки ювелирного консультанта",
    description: "Настройте, какие коллекции принадлежат каждой целевой аудитории и типу продукта. Бот будет использовать эту информацию для рекомендации подходящих товаров.",
    howItWorks: "Как это работает?",
    howItWorksDesc: "Когда клиент просит «браслет для женщины», бот будет искать в коллекциях, которые вы определили как «Женщины» + «Браслеты». Это предотвращает предложение детских браслетов вместо взрослых.",
    noCollections: "Не удалось загрузить коллекции из Shopify. Возможно, срок действия токена API истёк или есть проблема с разрешениями.",
    targetAudience: "Целевая аудитория",
    productTypes: "Типы товаров",
    audienceDesc: "Выберите, какие коллекции подходят для каждой целевой аудитории. Бот будет использовать это для персонализированных рекомендаций.",
    productTypeDesc: "Выберите, какие коллекции содержат каждый тип товара. Бот будет использовать это для точного поиска.",
    collectionsSelected: "коллекций выбрано",
    searchCollection: "Поиск коллекции...",
    noCollectionsFound: "Коллекции не найдены",
    loadError: "Ошибка загрузки данных",
    updateError: "Ошибка обновления",
  },
  ar: {
    title: "إعدادات مستشار المجوهرات",
    description: "حدد المجموعات التي تنتمي لكل جمهور مستهدف ونوع منتج. سيستخدم البوت هذه المعلومات لتوصية المنتجات المناسبة.",
    howItWorks: "كيف يعمل؟",
    howItWorksDesc: "عندما يطلب العميل \"سوار لامرأة\"، سيبحث البوت في المجموعات التي حددتها كـ\"نساء\" + \"أساور\". هذا يمنع البوت من اقتراح أساور أطفال بدلاً من أساور بالغين.",
    noCollections: "لم نتمكن من تحميل المجموعات من Shopify. ربما انتهت صلاحية رمز API أو هناك مشكلة في الأذونات.",
    targetAudience: "الجمهور المستهدف",
    productTypes: "أنواع المنتجات",
    audienceDesc: "اختر المجموعات المناسبة لكل جمهور مستهدف. سيستخدم البوت هذا لاقتراح منتجات مخصصة.",
    productTypeDesc: "اختر المجموعات التي تحتوي على كل نوع منتج. سيستخدم البوت هذا للبحث المركّز.",
    collectionsSelected: "مجموعات مختارة",
    searchCollection: "ابحث عن مجموعة...",
    noCollectionsFound: "لم يتم العثور على مجموعات",
    loadError: "خطأ في تحميل البيانات",
    updateError: "خطأ في التحديث",
  },
};

// ─── Admin Promo Banner Settings ────────────────────────────
const adminPromo = {
  en: {
    title: "Promo Banner Management",
    saveSettings: "Save Settings",
    enableTitle: "Enable Promo Banner",
    enableDesc: "Enable or disable the promo banner that appears above the header",
    enabled: "Promo banner is enabled",
    disabled: "Promo banner is disabled",
    bgColor: "Background Color",
    preview: "Preview",
    messagesTitle: "Promo Messages",
    messagesDesc: "Add messages to display in the promo banner. Drag to reorder.",
    addMessage: "Add Message",
    noMessages: "No messages defined yet",
    noMessagesHint: "Click \"Add Message\" to start",
    messageText: "Message Text",
    messagePlaceholder: "Free shipping on orders over ₪500!",
    linkLabel: "Link (optional)",
    linkPlaceholder: "/category/sale or https://...",
    active: "Active",
    inactive: "Inactive",
    savedSuccess: "Settings saved successfully",
    loadError: "Error loading data",
    saveError: "Error saving settings",
    addedSuccess: "New message added",
    addError: "Error adding message",
    deletedSuccess: "Message deleted",
    deleteError: "Error deleting message",
  },
  ru: {
    title: "Управление промо-баннером",
    saveSettings: "Сохранить настройки",
    enableTitle: "Включить промо-баннер",
    enableDesc: "Включите или выключите промо-баннер, отображаемый над шапкой",
    enabled: "Промо-баннер включён",
    disabled: "Промо-баннер выключен",
    bgColor: "Цвет фона",
    preview: "Предпросмотр",
    messagesTitle: "Промо-сообщения",
    messagesDesc: "Добавьте сообщения для промо-баннера. Перетаскивайте для изменения порядка.",
    addMessage: "Добавить сообщение",
    noMessages: "Сообщения ещё не настроены",
    noMessagesHint: "Нажмите \"Добавить сообщение\" чтобы начать",
    messageText: "Текст сообщения",
    messagePlaceholder: "Бесплатная доставка при заказе от ₪500!",
    linkLabel: "Ссылка (необязательно)",
    linkPlaceholder: "/category/sale или https://...",
    active: "Активно",
    inactive: "Неактивно",
    savedSuccess: "Настройки сохранены",
    loadError: "Ошибка загрузки данных",
    saveError: "Ошибка сохранения настроек",
    addedSuccess: "Новое сообщение добавлено",
    addError: "Ошибка добавления сообщения",
    deletedSuccess: "Сообщение удалено",
    deleteError: "Ошибка удаления сообщения",
  },
  ar: {
    title: "إدارة شريط العروض",
    saveSettings: "حفظ الإعدادات",
    enableTitle: "تفعيل شريط العروض",
    enableDesc: "تفعيل أو تعطيل شريط العروض الذي يظهر فوق الترويسة",
    enabled: "شريط العروض مفعّل",
    disabled: "شريط العروض معطّل",
    bgColor: "لون الخلفية",
    preview: "معاينة",
    messagesTitle: "رسائل العروض",
    messagesDesc: "أضف رسائل لعرضها في شريط العروض. اسحب لإعادة الترتيب.",
    addMessage: "إضافة رسالة",
    noMessages: "لم يتم تحديد رسائل بعد",
    noMessagesHint: "اضغط على \"إضافة رسالة\" للبدء",
    messageText: "نص الرسالة",
    messagePlaceholder: "شحن مجاني للطلبات فوق ₪500!",
    linkLabel: "رابط (اختياري)",
    linkPlaceholder: "/category/sale أو https://...",
    active: "نشط",
    inactive: "غير نشط",
    savedSuccess: "تم حفظ الإعدادات بنجاح",
    loadError: "خطأ في تحميل البيانات",
    saveError: "خطأ في حفظ الإعدادات",
    addedSuccess: "تمت إضافة رسالة جديدة",
    addError: "خطأ في إضافة الرسالة",
    deletedSuccess: "تم حذف الرسالة",
    deleteError: "خطأ في حذف الرسالة",
  },
};

// ─── Admin FAQ Settings ─────────────────────────────────────
const adminFaq = {
  en: {
    manageDesc: "Manage the FAQ displayed on the site",
    newCategory: "New Category",
    newQuestion: "New Question",
    editCategory: "Edit Category",
    addCategory: "Add Category",
    categoryName: "Category Name",
    categoryPlaceholder: "e.g.: General Questions",
    update: "Update",
    add: "Add",
    questions: "questions",
    inactive: "Inactive",
    category: "Category",
    selectCategory: "Select category",
    theQuestion: "Question",
    questionPlaceholder: "Enter the question",
    theAnswer: "Answer",
    answerPlaceholder: "Enter the answer",
    noCategories: "No categories yet. Click \"New Category\" to add the first one.",
    noQuestionsInCategory: "No questions in this category",
    addQuestionToCategory: "Add question to this category",
    enterCategoryName: "Please enter a category name",
    fillAllFields: "Please fill in all fields",
    categoryAdded: "Category added successfully",
    categoryUpdated: "Category updated successfully",
    categorySaveError: "Error saving category",
    categoryDeleted: "Category deleted successfully",
    categoryDeleteError: "Error deleting category",
    categoryToggleError: "Error updating category",
    questionAdded: "Question added successfully",
    questionUpdated: "Question updated successfully",
    questionSaveError: "Error saving question",
    questionDeleted: "Question deleted successfully",
    questionDeleteError: "Error deleting question",
    questionToggleError: "Error updating question",
    loadError: "Error loading data",
  },
  ru: {
    manageDesc: "Управление часто задаваемыми вопросами на сайте",
    newCategory: "Новая категория",
    newQuestion: "Новый вопрос",
    editCategory: "Редактировать категорию",
    addCategory: "Добавить категорию",
    categoryName: "Название категории",
    categoryPlaceholder: "например: Общие вопросы",
    update: "Обновить",
    add: "Добавить",
    questions: "вопросов",
    inactive: "Неактивно",
    category: "Категория",
    selectCategory: "Выберите категорию",
    theQuestion: "Вопрос",
    questionPlaceholder: "Введите вопрос",
    theAnswer: "Ответ",
    answerPlaceholder: "Введите ответ",
    noCategories: "Категорий пока нет. Нажмите \"Новая категория\" чтобы добавить первую.",
    noQuestionsInCategory: "В этой категории нет вопросов",
    addQuestionToCategory: "Добавить вопрос в эту категорию",
    enterCategoryName: "Введите название категории",
    fillAllFields: "Заполните все поля",
    categoryAdded: "Категория добавлена",
    categoryUpdated: "Категория обновлена",
    categorySaveError: "Ошибка сохранения категории",
    categoryDeleted: "Категория удалена",
    categoryDeleteError: "Ошибка удаления категории",
    categoryToggleError: "Ошибка обновления категории",
    questionAdded: "Вопрос добавлен",
    questionUpdated: "Вопрос обновлён",
    questionSaveError: "Ошибка сохранения вопроса",
    questionDeleted: "Вопрос удалён",
    questionDeleteError: "Ошибка удаления вопроса",
    questionToggleError: "Ошибка обновления вопроса",
    loadError: "Ошибка загрузки данных",
  },
  ar: {
    manageDesc: "إدارة الأسئلة الشائعة المعروضة على الموقع",
    newCategory: "فئة جديدة",
    newQuestion: "سؤال جديد",
    editCategory: "تعديل الفئة",
    addCategory: "إضافة فئة",
    categoryName: "اسم الفئة",
    categoryPlaceholder: "مثال: أسئلة عامة",
    update: "تحديث",
    add: "إضافة",
    questions: "أسئلة",
    inactive: "غير نشط",
    category: "الفئة",
    selectCategory: "اختر فئة",
    theQuestion: "السؤال",
    questionPlaceholder: "أدخل السؤال",
    theAnswer: "الإجابة",
    answerPlaceholder: "أدخل الإجابة",
    noCategories: "لا توجد فئات بعد. اضغط على \"فئة جديدة\" لإضافة الأولى.",
    noQuestionsInCategory: "لا توجد أسئلة في هذه الفئة",
    addQuestionToCategory: "إضافة سؤال لهذه الفئة",
    enterCategoryName: "يرجى إدخال اسم الفئة",
    fillAllFields: "يرجى ملء جميع الحقول",
    categoryAdded: "تمت إضافة الفئة بنجاح",
    categoryUpdated: "تم تحديث الفئة بنجاح",
    categorySaveError: "خطأ في حفظ الفئة",
    categoryDeleted: "تم حذف الفئة بنجاح",
    categoryDeleteError: "خطأ في حذف الفئة",
    categoryToggleError: "خطأ في تحديث الفئة",
    questionAdded: "تمت إضافة السؤال بنجاح",
    questionUpdated: "تم تحديث السؤال بنجاح",
    questionSaveError: "خطأ في حفظ السؤال",
    questionDeleted: "تم حذف السؤال بنجاح",
    questionDeleteError: "خطأ في حذف السؤال",
    questionToggleError: "خطأ في تحديث السؤال",
    loadError: "خطأ في تحميل البيانات",
  },
};

// ─── Admin Contact Messages ─────────────────────────────────
const adminContact = {
  en: {
    loading: "Loading messages...",
    noMessages: "No messages to display",
    status: "Status",
    name: "Name",
    email: "Email",
    phone: "Phone",
    message: "Message",
    date: "Date",
    actions: "Actions",
    new: "New",
    handled: "Handled",
    notAvailable: "N/A",
    markedHandled: "Message marked as handled",
    markedNew: "Message marked as new",
    statusError: "Error updating status",
    deleted: "Message deleted successfully",
    deleteError: "Error deleting message",
    loadError: "Error loading messages",
  },
  ru: {
    loading: "Загрузка сообщений...",
    noMessages: "Нет сообщений для отображения",
    status: "Статус",
    name: "Имя",
    email: "Email",
    phone: "Телефон",
    message: "Сообщение",
    date: "Дата",
    actions: "Действия",
    new: "Новое",
    handled: "Обработано",
    notAvailable: "Н/Д",
    markedHandled: "Сообщение отмечено как обработанное",
    markedNew: "Сообщение отмечено как новое",
    statusError: "Ошибка обновления статуса",
    deleted: "Сообщение удалено",
    deleteError: "Ошибка удаления сообщения",
    loadError: "Ошибка загрузки сообщений",
  },
  ar: {
    loading: "جارٍ تحميل الرسائل...",
    noMessages: "لا توجد رسائل للعرض",
    status: "الحالة",
    name: "الاسم",
    email: "البريد الإلكتروني",
    phone: "الهاتف",
    message: "الرسالة",
    date: "التاريخ",
    actions: "إجراءات",
    new: "جديد",
    handled: "تمت المعالجة",
    notAvailable: "غير متوفر",
    markedHandled: "تم تحديد الرسالة كمعالجة",
    markedNew: "تم تحديد الرسالة كجديدة",
    statusError: "خطأ في تحديث الحالة",
    deleted: "تم حذف الرسالة بنجاح",
    deleteError: "خطأ في حذف الرسالة",
    loadError: "خطأ في تحميل الرسائل",
  },
};

// ─── Admin Customer Messages ────────────────────────────────
const adminCustomerMsg = {
  en: {
    sendTitle: "Send New Message",
    selectCustomer: "Select Customer",
    selectCustomerPlaceholder: "Select customer...",
    searchCustomer: "Search customer...",
    unknownCustomer: "Unknown Customer",
    subject: "Subject",
    subjectPlaceholder: "Message subject...",
    messageContent: "Message Content",
    messagePlaceholder: "Write the message...",
    attachment: "Attachment (optional)",
    addFile: "Add File",
    sending: "Sending...",
    sendMessage: "Send Message",
    historyTitle: "Message History",
    noMessages: "No messages yet",
    to: "To:",
    unread: "Unread",
    sentAt: "Sent at:",
    attachedFile: "Attached file",
    fileTooLarge: "File too large. Maximum 10MB",
    fillAllFields: "Please fill in all fields",
    sentSuccess: "Message sent successfully!",
    sendError: "Error sending message",
    deleteTitle: "Delete Message",
    deleteDesc: "Are you sure you want to delete this message? This action cannot be undone.",
    cancel: "Cancel",
    delete: "Delete",
    deleted: "Message deleted",
    deleteError: "Error deleting message",
    loadError: "Error loading data",
  },
  ru: {
    sendTitle: "Отправить новое сообщение",
    selectCustomer: "Выберите клиента",
    selectCustomerPlaceholder: "Выберите клиента...",
    searchCustomer: "Поиск клиента...",
    unknownCustomer: "Неизвестный клиент",
    subject: "Тема",
    subjectPlaceholder: "Тема сообщения...",
    messageContent: "Содержание сообщения",
    messagePlaceholder: "Напишите сообщение...",
    attachment: "Вложение (необязательно)",
    addFile: "Добавить файл",
    sending: "Отправка...",
    sendMessage: "Отправить сообщение",
    historyTitle: "История сообщений",
    noMessages: "Сообщений пока нет",
    to: "Кому:",
    unread: "Не прочитано",
    sentAt: "Отправлено:",
    attachedFile: "Прикреплённый файл",
    fileTooLarge: "Файл слишком большой. Максимум 10МБ",
    fillAllFields: "Заполните все поля",
    sentSuccess: "Сообщение отправлено!",
    sendError: "Ошибка отправки сообщения",
    deleteTitle: "Удалить сообщение",
    deleteDesc: "Вы уверены, что хотите удалить это сообщение? Это действие необратимо.",
    cancel: "Отмена",
    delete: "Удалить",
    deleted: "Сообщение удалено",
    deleteError: "Ошибка удаления сообщения",
    loadError: "Ошибка загрузки данных",
  },
  ar: {
    sendTitle: "إرسال رسالة جديدة",
    selectCustomer: "اختر عميل",
    selectCustomerPlaceholder: "اختر عميل...",
    searchCustomer: "بحث عن عميل...",
    unknownCustomer: "عميل غير معروف",
    subject: "الموضوع",
    subjectPlaceholder: "موضوع الرسالة...",
    messageContent: "محتوى الرسالة",
    messagePlaceholder: "اكتب الرسالة...",
    attachment: "مرفق (اختياري)",
    addFile: "إضافة ملف",
    sending: "جارٍ الإرسال...",
    sendMessage: "إرسال رسالة",
    historyTitle: "سجل الرسائل",
    noMessages: "لا توجد رسائل بعد",
    to: "إلى:",
    unread: "غير مقروءة",
    sentAt: "أُرسلت في:",
    attachedFile: "ملف مرفق",
    fileTooLarge: "الملف كبير جداً. الحد الأقصى 10 ميجابايت",
    fillAllFields: "يرجى ملء جميع الحقول",
    sentSuccess: "تم إرسال الرسالة بنجاح!",
    sendError: "خطأ في إرسال الرسالة",
    deleteTitle: "حذف الرسالة",
    deleteDesc: "هل أنت متأكد من حذف هذه الرسالة؟ لا يمكن التراجع عن هذا الإجراء.",
    cancel: "إلغاء",
    delete: "حذف",
    deleted: "تم حذف الرسالة",
    deleteError: "خطأ في حذف الرسالة",
    loadError: "خطأ في تحميل البيانات",
  },
};

// ─── Admin SMS Templates ────────────────────────────────────
const adminSms = {
  en: {
    title: "Edit SMS Messages",
    description: "Edit the SMS message templates sent to customers on each status change",
    updated: "Updated:",
    chars: "characters",
    save: "Save",
    noTemplates: "No SMS templates found",
    availableVars: "Available variables:",
    var1: "The ticket number will appear automatically at the beginning",
    var2: "The ticket title will appear automatically",
    var3: "A link to the personal area will appear at the end",
    noChanges: "No changes made",
    savedSuccess: "Template updated successfully",
    saveError: "Error saving template",
    loadError: "Error loading SMS templates",
    placeholder: "Enter message content...",
    pending: "Pending",
    in_repair: "In Repair",
    completed: "Completed",
    waiting_in_store: "Waiting in Store",
    shipped: "Shipped",
    delivered: "Delivered",
    cancelled: "Cancelled",
  },
  ru: {
    title: "Редактирование SMS-сообщений",
    description: "Редактируйте шаблоны SMS-сообщений, отправляемых клиентам при изменении статуса",
    updated: "Обновлено:",
    chars: "символов",
    save: "Сохранить",
    noTemplates: "Шаблоны SMS не найдены",
    availableVars: "Доступные переменные:",
    var1: "Номер заявки появится автоматически в начале",
    var2: "Заголовок заявки появится автоматически",
    var3: "Ссылка на личный кабинет появится в конце",
    noChanges: "Изменений нет",
    savedSuccess: "Шаблон обновлён",
    saveError: "Ошибка сохранения шаблона",
    loadError: "Ошибка загрузки шаблонов SMS",
    placeholder: "Введите текст сообщения...",
    pending: "Ожидает",
    in_repair: "В ремонте",
    completed: "Завершено",
    waiting_in_store: "Ожидает в магазине",
    shipped: "Отправлено",
    delivered: "Доставлено",
    cancelled: "Отменено",
  },
  ar: {
    title: "تعديل رسائل SMS",
    description: "تعديل قوالب رسائل SMS المرسلة للعملاء عند كل تغيير حالة",
    updated: "تم التحديث:",
    chars: "أحرف",
    save: "حفظ",
    noTemplates: "لم يتم العثور على قوالب SMS",
    availableVars: "المتغيرات المتاحة:",
    var1: "سيظهر رقم الطلب تلقائياً في البداية",
    var2: "سيظهر عنوان الطلب تلقائياً",
    var3: "سيظهر رابط المنطقة الشخصية في النهاية",
    noChanges: "لم يتم إجراء تغييرات",
    savedSuccess: "تم تحديث القالب بنجاح",
    saveError: "خطأ في حفظ القالب",
    loadError: "خطأ في تحميل قوالب SMS",
    placeholder: "أدخل محتوى الرسالة...",
    pending: "قيد الانتظار",
    in_repair: "قيد الإصلاح",
    completed: "مكتمل",
    waiting_in_store: "في انتظار بالمتجر",
    shipped: "تم الشحن",
    delivered: "تم التسليم",
    cancelled: "ملغى",
  },
};

// ─── Admin TV Display Settings ──────────────────────────────
const adminTV = {
  en: {
    title: "Store Display Settings (TV)",
    openDisplay: "Open Display",
    tvLink: "TV display link:",
    generalSettings: "General Settings",
    activeDisplay: "Active Display",
    showPrices: "Show Prices",
    showVariants: "Show Variants",
    slideDuration: "Display time per product (seconds)",
    categoriesToShow: "Categories to Display",
    categoriesDesc: "Select specific categories. If none selected, all products will be shown.",
    selectCategory: "Select category...",
    removeSale: "Remove Sale",
    markAsSale: "Mark as Sale",
    saleText: "Sale Text",
    salePlaceholder: "e.g.: End of Season Sale!",
    saleEndTime: "Sale End (date and time)",
    promoMessages: "Promo Messages",
    promoDesc: "Messages that will run in the ticker at the bottom of the screen (like a news ticker).",
    promoPlaceholder: "e.g.: 20% off all rings this week!",
    saving: "Saving...",
    saveSettings: "Save Settings",
    savedSuccess: "TV display settings saved successfully",
    saveError: "Error saving settings",
  },
  ru: {
    title: "Настройки витрины (ТВ)",
    openDisplay: "Открыть витрину",
    tvLink: "Ссылка на ТВ-витрину:",
    generalSettings: "Общие настройки",
    activeDisplay: "Активная витрина",
    showPrices: "Показывать цены",
    showVariants: "Показывать варианты",
    slideDuration: "Время показа товара (секунды)",
    categoriesToShow: "Категории для отображения",
    categoriesDesc: "Выберите конкретные категории. Если не выбрано ни одной, будут показаны все товары.",
    selectCategory: "Выберите категорию...",
    removeSale: "Убрать акцию",
    markAsSale: "Отметить как акцию",
    saleText: "Текст акции",
    salePlaceholder: "например: Распродажа конца сезона!",
    saleEndTime: "Окончание акции (дата и время)",
    promoMessages: "Промо-сообщения",
    promoDesc: "Сообщения, которые будут бегущей строкой внизу экрана (как новостная лента).",
    promoPlaceholder: "например: Скидка 20% на все кольца на этой неделе!",
    saving: "Сохранение...",
    saveSettings: "Сохранить настройки",
    savedSuccess: "Настройки ТВ-витрины сохранены",
    saveError: "Ошибка сохранения настроек",
  },
  ar: {
    title: "إعدادات شاشة العرض (TV)",
    openDisplay: "فتح العرض",
    tvLink: "رابط شاشة العرض:",
    generalSettings: "إعدادات عامة",
    activeDisplay: "العرض نشط",
    showPrices: "عرض الأسعار",
    showVariants: "عرض المتغيرات",
    slideDuration: "مدة العرض لكل منتج (ثوانٍ)",
    categoriesToShow: "فئات للعرض",
    categoriesDesc: "اختر فئات محددة. إذا لم يتم اختيار أي فئة، ستُعرض جميع المنتجات.",
    selectCategory: "اختر فئة...",
    removeSale: "إزالة العرض",
    markAsSale: "تحديد كعرض",
    saleText: "نص العرض",
    salePlaceholder: "مثال: تخفيضات نهاية الموسم!",
    saleEndTime: "انتهاء العرض (تاريخ ووقت)",
    promoMessages: "رسائل ترويجية",
    promoDesc: "رسائل ستظهر في شريط أسفل الشاشة (مثل شريط الأخبار).",
    promoPlaceholder: "مثال: خصم 20% على جميع الخواتم هذا الأسبوع!",
    saving: "جارٍ الحفظ...",
    saveSettings: "حفظ الإعدادات",
    savedSuccess: "تم حفظ إعدادات شاشة العرض بنجاح",
    saveError: "خطأ في حفظ الإعدادات",
  },
};

const allSections = { contact, auth, profile, about, common, shipping, returns, pages, newsletter, footer, homepage, header, whyUs, whyChoose, privacy, terms, giftCard, trackOrder, ringSizeGuide, accessibility, cookiePolicy, wishlistPrompt, ringSizeDialog, cookieBanner, cookiePreferences, productCard, search, cartDrawer, seo, tryOn, imageSearch, engraving, orderHistory, favorites, repairOrders, userMessages, profileTour, profileGreeting, admin, variantPicker, confirmDialog, adminMenu, adminUsers, adminColor, adminSearch, adminBot, adminPromo, adminFaq, adminContact, adminCustomerMsg, adminSms, adminTV } as const;

type SectionName = keyof typeof allSections;

/**
 * Get translations for a specific section based on the current Google Translate language.
 * Returns undefined if the language is Hebrew (default).
 */
export function getStaticTranslation<S extends SectionName>(
  section: S
): (typeof allSections)[S][LangCode] | null {
  const lang = getTranslateLang();
  if (!lang || lang === "he") return null;
  
  const sectionData = allSections[section];
  return (sectionData as any)[lang] || null;
}

/**
 * React hook that provides translations for a section, reactive to language changes.
 * Returns a `t(key)` function that returns the translated text or the Hebrew fallback.
 */
export function useStaticTranslation<S extends SectionName>(section: S) {
  const [lang, setLang] = useState(getTranslateLang);
  
  useEffect(() => {
    const interval = setInterval(() => {
      const current = getTranslateLang();
      setLang(prev => prev !== current ? current : prev);
    }, 300);
    return () => clearInterval(interval);
  }, []);
  
  const translations = lang && lang !== "he" 
    ? (allSections[section] as any)[lang] as Record<string, string> | undefined
    : null;

  const t = useCallback((key: string, hebrewFallback: string): string => {
    if (!translations) return hebrewFallback;
    return translations[key] || hebrewFallback;
  }, [translations]);

  const isHebrew = !lang || lang === "he";

  return { t, isHebrew, lang };
}
